import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { StateService } from '@app/shared/services/state/state.service';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { toDesignHuddleProjectTitle } from '@app/shared/components/master-product/project-titles-dialog/project-titles-dialog.component';
import { ProductImageMatteService } from '@app/shared/services/design-huddle/product-image-matte.service';
import { ProductPreviewNormalizeService } from '@app/shared/services/design-huddle/product-preview-normalize.service';
import { DesignHuddleUploadOverrideService } from '@app/shared/services/design-huddle/design-huddle-upload-override.service';
import { isFrameDistorted } from '@app/shared/services/design-huddle/frame-fit';
import {
  PrintExport,
  artDiffersFromLocked,
  needsOwnPrintExport,
  resolveSideDownloadUrl,
  shouldForceAlternateRouting,
  sideArtKey
} from '@app/shared/services/design-huddle/alternate-print-file';
import { PrintExportGenerations, clearTrackedSideExport } from '@app/shared/services/design-huddle/print-export-generation';
import { getRoutePrefix } from '@app/shared/utils/route-prefix.util';
import { canonicalSide as canonicalSideUtil } from '@app/shared/utils/canonical-side.util';
import { getDefaultDecorationLocationName, resolveSideAlias } from '@app/shared/constants/decoration-location.constants';
import { forkJoin, of, Observable, firstValueFrom } from 'rxjs';
import { catchError, map, timeout, switchMap } from 'rxjs/operators';
import { DecorationTypeService } from '@app/shared/services/decoration-type.service';
import { ProductDecoration } from '@app/shared/models/decoration-type.model';

declare var DSHDLib: any;

interface ProductColorVariant {
  productId: string;
  productName: string;
  productCategory: string;
  productImage: string;
  selectedColors: ColorVariant[];
  availableColors: any[];
  lockedStyleId?: number; // The color selected from previous page - cannot be unchecked
}

interface ColorVariant {
  colorName: string;
  colorCode: string;
  styleId: number;
  useDefaultDesign: boolean;
  variantDesignUrl?: string;
  variantDesignFile?: File;
  variantPreviewUrl?: string;
  customizations?: any;
  projectId?: string;
  templateId?: number;
  jobId?: string;
  sides?: any[];
  projectName?: string;
  // Per-side logo URLs
  sideLogos?: { [sideName: string]: string };
  // Source of variant: 'customizedProduct' | 'alternateDesign' | 'customProductImage'
  variantSource?: string;
  // Whether an InkSoft variant has been migrated (design updated in editor)
  isMigrated?: boolean;
}

@Component({
  selector: 'app-product-color-selection',
  templateUrl: './product-color-selection.component.html',
  styleUrls: ['./product-color-selection.component.css']
})
export class ProductColorSelectionComponent implements OnInit {
  storeId: number;
  storeName: string;
  designData: any = null;
  selectedProducts: ProductColorVariant[] = [];
  totalVariants: number = 0;
  productsDataFromPreviousPage: any[] = [];

  // Edit mode
  isEditMode: boolean = false;
  editProductData: any = null;
  masterProductImgBaseURL: string = environment.masterProductImgBaseURL || '';
  imgBaseURL: string = environment.imgBaseURL || '';
  // Product-level side logos (applies to all variants)
  productSideLogos: Map<string, { [sideName: string]: string }> = new Map();
  // Store project details (pages) for each projectId
  projectDetailsMap: Map<string, any> = new Map();
  // Track pending export jobs: Map<variantKey, { pending, completed, sideJobIds, sideDownloadUrls }>
  pendingExports: Map<string, { pending: number; completed: number; sideJobIds: Map<string, string>; sideDownloadUrls?: Map<string, string> }> = new Map();
  // Flag to track if we're in the process of saving (to trigger save after all exports complete)
  waitingForExportsToSave: boolean = false;
  // Store products data for saving after exports complete
  productsToSave: any[] = [];
  // Flag to navigate after save (set when Continue is clicked)
  shouldNavigateAfterSave: boolean = false;
  // Cache for Azure background URLs: Map<productId_styleId_sideName, azureUrl>
  backgroundUrls: Map<string, string> = new Map();
  // Track which backgrounds are uploading: Set<productId_styleId_sideName>
  uploadingBackgrounds: Set<string> = new Set();
  // Track which previews are being generated: Set<productId_styleId_sideName>
  generatingPreviews: Set<string> = new Set();
  // WI-1524 fix #1: sides whose print export "completed" without ever getting a download URL
  // (poll timeout, poll error-exhaustion, export-job with no job_id, etc.) — no print file exists
  // for these, so they need a visible error + retry instead of silently saving with nothing.
  // Keyed identically to pendingExports' sideJobIds/sideDownloadUrls: `${productId}_${styleId}_${sideName}`
  // using the SAME raw sideName format (not the display name used by backgroundUrls/preview keys).
  printExportFailures: Set<string> = new Set();
  // Sides whose Design Huddle logo element frame no longer matches the artwork's aspect ratio
  // (frame-fit.ts). Exporting such a project yields a stretched print file (oval → circle), so the
  // export is skipped and the side is flagged until the designer re-adds the logo in the editor.
  // Same `${variantKey}_${canonicalSide}` key as printExportFailures.
  printExportDistorted: Set<string> = new Set();
  // Monotonic render generation per cacheKey. Bumped by refreshPreviewAfterEditorClose so a
  // slow in-flight fresh preview (started before the edit) can't land afterwards and clobber
  // the correct post-edit thumbnail. See isStalePreview().
  private previewGeneration: Map<string, number> = new Map();
  // Edit generation per `${variantKey}_${canonicalSide}` for the PRINT export. A design change bumps
  // it (invalidatePrintExport); an export only lands in sideDownloadUrls if it began at the current
  // generation, so a pre-edit export can't be saved next to the post-edit thumbnail/placement.
  private printGens = new PrintExportGenerations();
  // De-dupe the "print placement unavailable" toast: Set<productId_canonicalSide>
  private placementWarningsShown: Set<string> = new Set();
  // Track pending uploads count for coordinating save
  pendingUploads: number = 0;
  // Flag indicating save is waiting for uploads
  saveWaitingForUploads: boolean = false;
  // WI-1521 fix #4: uploadBackgroundsForAllVariants() used to fire generatePreviewsForVariant()
  // for every product × color at once, with no cap — each one fans out into a DH
  // createProject/export-job/upload chain per side. This queue+slot pair caps how many
  // (product, variant) preview jobs run concurrently; the rest wait in variantPreviewQueue.
  // Reported symptom this addresses: "adding multiple products in one go can get problematic...
  // workaround is to build/add up to 5 items only per batch" (manual batching of this same fan-out).
  private readonly MAX_CONCURRENT_VARIANT_PREVIEWS = 3;
  private variantPreviewQueue: { product: ProductColorVariant; variant: ColorVariant }[] = [];
  private activeVariantPreviewSlots: { product: ProductColorVariant; variant: ColorVariant }[] = [];
  // Flag to ensure initial save only happens once (for locked color from master product list)
  initialSaveCompleted: boolean = false;
  // Single-side flow properties
  currentSideNameMap: Map<string, string> = new Map(); // per-product initial side
  availableSidesMap: Map<string, { name: string, displayName: string, templateId: number, printPlacement: any }[]> = new Map();
  isSwitchingSide: boolean = false;
  // Per-variant side tracking: Map<productId_styleId, sideName[]>
  variantActiveSides: Map<string, string[]> = new Map();
  // Per-variant-per-side project IDs: Map<productId_styleId_sideName, projectId>
  variantSideProjects: Map<string, string> = new Map();
  // Side-level project OWNERSHIP: "does ANY already-selected color already have a project for this
  // (product, side)" is answered by SCANNING `variantSideProjects` itself (see
  // findSideProjectOwner()/getSharedSideProjectId()/findSideProjectOwnerKey() below) rather than a
  // separate cache — a separate cache needs every project-creation/restore site to remember to
  // also write into it, and one real site (initializeSingleSideFlow's create-flow branch, the
  // locked/default color's own initial-side project at page load) was missed in an earlier version
  // of this fix, silently defeating it for the single most common side every added color needs.
  // The export job is color-independent (see exportCurrentSide — no product_image/garment-color
  // param) and the preview is often color-independent too (SDK getVariableTemplatePreviewURL just
  // needs template_id + a customizationObject; only a per-color product_image_url differs), so the
  // exact same unedited DH project can safely serve every color sharing that design until one of
  // them is actually edited. Only whichever color first creates a project for a side gets a
  // `variantSideProjects` entry for ITSELF (toggleColorSelection's isCurrentSide branch,
  // ensurePendingSideProjectsBeforeSave's create branch, onSideChange's create branch) — every
  // other color that merely REUSES it for preview/export must NOT also get one, so that
  // openEditorForSide's clone-on-edit safety net (a borrowing color always clones into its own
  // independent project the first time its editor is opened) stays intact.
  // In-flight side-project creation: Map<productId_sideName, Promise<string>>. Closes the race
  // where onSideChange processes every selected color for a product CONCURRENTLY (a plain
  // Array.map(), not sequential) and more than one of them needs a brand-new project for the same
  // (product, side) in the very same call — before either's createProjectForSide has resolved,
  // `variantSideProjects` still has no entry for either (the project id isn't known yet), so
  // without this a second (or third, ...) color would independently race to create its own
  // duplicate project too. A later variant processed in the same .map() call sees this entry
  // synchronously (set before any await, right when the first variant starts creating) and awaits
  // it instead of starting a second creation. Only ever set for a "generic" (no per-side custom
  // logo) creation — see createAndOwnSideProject's `registerAsShared` param — since a project
  // seeded from one color's OWN custom logo must never be handed to another color expecting the
  // shared/default design.
  private pendingSideProjectCreations: Map<string, Promise<string>> = new Map();
  // Project IDs created via DSHDLib.createProject in THIS guest session — i.e. owned by the
  // current guest and therefore safe to open in the editor directly. Project IDs NOT in this set
  // (e.g. loaded from the DB in edit mode) were created by a guest session that no longer exists,
  // so opening them throws "Access to this project is not authorized" and they must be reproduced
  // under the current guest session first (see reproduceProjectUnderGuestAndInsert).
  private guestOwnedProjectIds: Set<string> = new Set<string>();
  // Per-product DesignId: true = no change, false = logo/template changed: Map<productId, boolean>
  designIdMap: Map<string, boolean> = new Map();
  // Per-variant DesignId offset: increments each time logo/template changes: Map<productId_styleId, offset>
  variantDesignIdOffset: Map<string, number> = new Map();

  // Transient add-form state for the per-product "Imprint Locations" module, keyed by productId:
  // the chosen side (normalized available-side name) + the picked graphic. The graphic is uploaded
  // IMMEDIATELY on file selection — same flow as upload-design.component.ts's auto-upload-on-pick —
  // rather than staged as a raw File and only uploaded later when "Add to All Colors" is clicked;
  // `logoUrl` holds the already-uploaded Azure URL once that finishes, `uploading` drives the
  // in-progress UI state. The imprint entries shown in the module are DERIVED from each variant's
  // sides/sideLogos (the saved source of truth) via getProductImprints(), so no extra persistence
  // is needed.
  imprintDraft: Map<string, { side: string; fileName: string; logoUrl: string | null; uploading: boolean }> = new Map();

  // InkSoft migration: block continue until all InkSoft variants are updated
  isInksoftProduct: boolean = false;

  // Text-element export flag: true once the editor has closed and the current projectId's
  // data confirmed text elements exist.  Variant previews generated after this point (e.g.
  // when the user selects a different color) will use the export-API path instead of the
  // SDK/getVariableTemplatePreviewURL path, because the SDK cannot render text elements.
  // Reset to false whenever the editor opens (so a fresh open/close cycle re-evaluates).
  currentProjectHasTextElements: boolean = false;

  // Editor modal state
  showEditorModal: boolean = false;
  isEditorLoading: boolean = false;
  // Static container id for the DH editor iframe.
  readonly editorContainerId: string = 'dh-editor-container-color';
  // Holds the editor instance returned by DSHDLib.Editors.insert.
  // The SDK tracks mounted editors by an internal random id (not container id / project id).
  // Calling editorInstance.remove() is the only reliable way to deregister an editor from
  // the SDK's internal registry before the next Editors.insert — clearing DOM children alone
  // does NOT remove the SDK's registry entry, causing Editors.insert to silently no-op on
  // the second open (the SDK sees an already-registered session and skips building).
  private editorInstance: any = null;
  isFetchingEditorMetadata: boolean = false;
  editorProductName: string = '';
  editorProjectId: string | null = null;
  editorProduct: ProductColorVariant | null = null;
  editorVariant: ColorVariant | null = null;
  editorSideName: string | null = null;
  // WI-1578: true when the editor was opened via an imprint row's "Edit Design" button — on
  // Save & Close the edited project is shared with every other selected color for this side,
  // instead of only updating the one variant being edited (the normal per-color pencil-icon flow).
  editorApplyToAllColors: boolean = false;
  // WI-1578: styleIds the user chose to leave alone (via the "already has its own design" confirm)
  // when this editor session closes — every OTHER selected color still gets the edited design.
  editorApplyToAllColorsExcludeStyleIds: Set<number> = new Set();

  // WI-1578: lightweight in-page confirm modal — replaces window.confirm() for the imprint-location
  // Remove/Edit Design prompts (a native browser confirm looks out of place next to the rest of the
  // page's own styled modals, e.g. the DH editor modal just below). Supports a message with embedded
  // newlines (split into separate lines when rendering) and an optional non-destructive styling.
  showConfirmModal: boolean = false;
  confirmModalTitle: string = '';
  confirmModalMessage: string = '';
  confirmModalConfirmLabel: string = 'Continue';
  confirmModalCancelLabel: string = 'Cancel';
  confirmModalDestructive: boolean = false;
  // When false, clicking the dark overlay does nothing — the X button or one of the two bottom
  // buttons is the only way to close. Used for choices with real consequences (WI-1578's
  // Edit-Design-for-all-colors prompts) where an accidental outside click shouldn't silently decide.
  confirmModalAllowOverlayDismiss: boolean = true;
  // Smaller message text for lighter, informational confirms (e.g. the first-time "this applies
  // to all colors" notice) vs. the default size for prompts naming specific colors/consequences.
  confirmModalCompactText: boolean = false;
  // Shows a warning icon next to the title — used for prompts flagging a real pre-existing state
  // the user should notice (e.g. "Design already customized"), for more visibility than plain text.
  confirmModalWarn: boolean = false;
  private confirmModalResolve: ((result: boolean | null) => void) | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private stateService: StateService,
    private loader: LoaderService,
    private notification: NotificationService,
    private location: Location,
    private http: HttpClient,
    private designHuddleService: DesignHuddleService,
    private productImageMatteService: ProductImageMatteService,
    private productPreviewNormalizeService: ProductPreviewNormalizeService,
    private uploadOverride: DesignHuddleUploadOverrideService,
    private decorationTypeService: DecorationTypeService
  ) {
    this.route.queryParams.subscribe(params => {
      this.storeId = params['storeId'] || localStorage.getItem('storeId');
      this.storeName = params['storeName'] || localStorage.getItem('storeName') || '';
    });
  }

  async ngOnInit(): Promise<void> {

    // Check if we have saved color variants data (coming back from pricing page)
    const savedColorVariantsData = this.stateService.getColorVariantsData();

    // Clear stale payload but preserve colorVariantsData for restoration
    this.stateService.clearColorVariantsPayload();

    // Check edit mode
    this.isEditMode = this.stateService.isEditMode();
    this.editProductData = this.stateService.getEditProductData();
    if (this.isEditMode) {
      // Check if this is an InkSoft product requiring migration
      const editCPs = this.editProductData?.productDetails?.CustomizedProducts || [];
      this.isInksoftProduct = editCPs.length > 0 && editCPs[0].IsInksoft;
    }

    // Load design data
    this.designData = this.stateService.getDesignData();

    if (!this.designData) {
      console.error('❌ No design data found');
      this.notification.error('No design data found. Please start over.', 'Error');
      this.goBack();
      return;
    }

    // If we have saved data from a previous visit (coming back from pricing page),
    // restore from it to preserve updated thumbnails/logos
    if (savedColorVariantsData?.products) {
      if (savedColorVariantsData.designData) {
        this.designData = savedColorVariantsData.designData;
      }
      // Await the design dimensions BEFORE generating any previews so the top-anchor
      // default placement is ready on the first pass (otherwise variant thumbnails render
      // centered while the design image is still loading).
      await this.getDesignDims(this.designData?.designUrl || this.designData?.previewUrl || '');
      this.stateService.clearColorVariantsData();
      this.restoreFromSavedData(savedColorVariantsData.products);
      return;
    }

    // Await the design dimensions before any preview/export generation (see note above).
    await this.getDesignDims(this.designData?.designUrl || this.designData?.previewUrl || '');

    this.stateService.clearColorVariantsData();

    // Load selected products from state
    this.loadSelectedProducts();
  }

  /**
   * Restore color selection state from saved data (when navigating back from pricing page).
   * Preserves updated thumbnails, logos, and variant data.
   */
  restoreFromSavedData(savedProducts: any[]): void {
    this.productsDataFromPreviousPage = savedProducts;

    this.selectedProducts = savedProducts.map((product: any) => {
      const selectedColors: ColorVariant[] = (product.selectedColors || []).map((variant: any) => ({
        colorName: variant.colorName,
        colorCode: variant.colorCode,
        styleId: variant.styleId,
        useDefaultDesign: variant.useDefaultDesign !== false,
        variantDesignUrl: variant.variantDesignUrl,
        projectId: variant.projectId,
        customizations: variant.customizations,
        templateId: variant.templateId,
        jobId: variant.jobId,
        sides: variant.sides,
        projectName: variant.projectName,
        sideLogos: variant.sideLogos,
        variantSource: variant.variantSource,
        isMigrated: variant.isMigrated
      }));

      return {
        productId: product.ProductId || product.productId,
        productName: product.productName || product.Description,
        productCategory: product.productCategory || product.CustomizationType || 'Product',
        productImage: product.productImage || product.PreviewImageUrl || product.ImageUrl,
        selectedColors: selectedColors,
        availableColors: product.availableColors || product.Styles || [],
        lockedStyleId: Number(product.lockedStyleId || product.selectedStyleId)
      };
    });

    // Restore backgroundUrls from variant sides' ThumbnailUrl
    this.selectedProducts.forEach(product => {
      product.selectedColors.forEach(variant => {
        if (!variant.sides) return;
        variant.sides.forEach((side: any) => {
          const sName = (side.SideName || side.Name || '').toLowerCase().replace(/\s+/g, '_');
          const thumbnailUrl = side.ThumbnailUrl || side.ThumbnailRelativeUrl || '';
          if (thumbnailUrl) {
            const sideConfig = this.getSideConfigForCurrentSide(sName);
            if (sideConfig) {
              const cacheKey = `${product.productId}_${variant.styleId}_${sideConfig.displayName}`;
              const fullUrl = thumbnailUrl.startsWith('http') ? thumbnailUrl : this.joinUrl(environment.imgBaseURL, thumbnailUrl);
              this.backgroundUrls.set(cacheKey, fullUrl);
            }
          }
          // Restore side project IDs
          if (side.ProjectId) {
            this.variantSideProjects.set(`${product.productId}_${variant.styleId}_${sName}`, side.ProjectId);
          }
        });
      });
    });


    this.calculateTotalVariants();

    // Build available sides per product
    this.productsDataFromPreviousPage.forEach((pd: any) => {
      const rawSideName = pd.currentSideName || 'front';
      const normalizedSide = rawSideName.toLowerCase().replace(/\s+/g, '_');
      this.currentSideNameMap.set(String(pd.ProductId || pd.productId), normalizedSide);
      this.buildAvailableSides(pd);
    });

    // Restore active sides per variant.
    //
    // variant.sides being non-empty does NOT mean every entry in it is a side this variant has
    // actually been customized for — in the create flow, loadSelectedProducts() seeds variant.sides
    // directly from product.Sides, the MASTER PRODUCT'S full list of available print locations
    // (Front, Back, both sleeves), regardless of which one the user actually started on. Treating
    // every one of those as "already active" made a side the user never touched (e.g. Front, when
    // the product was started on Right Sleeve) look pre-customized everywhere this map is read:
    // the Imprint Locations dropdown routed it through Replace Artwork instead of a new side (a
    // real existing project to regenerate was assumed but never actually existed), producing false
    // "Logo is stretched" / "No print file" flags, and addSideToSingleVariant's own early-return
    // guard would have silently no-op'd adding it at all.
    //
    // Only a side with a genuine ProjectId (actually exported/customized at some point — by this
    // variant, or carried over from the locked color's own saved design) counts as active here; the
    // product's own current/initial side is always included even if it has no project yet (it's the
    // side actively being worked on, matching the pre-existing single-side-flow fallback below).
    this.selectedProducts.forEach(product => {
      const currentSide = this.getProductCurrentSide(product.productId);
      product.selectedColors.forEach(variant => {
        const variantKey = `${product.productId}_${variant.styleId}`;
        const activeSides: string[] = [];
        if (variant.sides) {
          variant.sides.forEach((side: any) => {
            const sName = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
            if (sName && side.ProjectId) activeSides.push(sName);
          });
        }
        if (!activeSides.includes(currentSide)) {
          activeSides.push(currentSide);
        }
        this.variantActiveSides.set(variantKey, activeSides);
      });
    });

    this.initializeSingleSideFlow();
  }

  loadSelectedProducts(): void {
    const productsData = this.stateService.getSelectedProductsForColors();

    if (!productsData || productsData.length === 0) {
      console.error('❌ No products data found in state service');
      this.notification.error('No products selected. Please select products first.', 'Error');
      this.goBack();
      return;
    }

    // Store original products data for accessing projectId, templateId, etc.
    this.productsDataFromPreviousPage = productsData;

    // If the master-products page detected text elements in the editor project, initialize
    // the flag here so the very first variant preview (locked color) also routes through
    // the export-API path — no need to re-open the editor to re-derive this.
    const anyHasTextElements = productsData.some((pd: any) => pd.hasTextElements === true);
    if (anyHasTextElements) {
      this.currentProjectHasTextElements = true;
    }

    // Transform products data into ProductColorVariant format
    this.selectedProducts = productsData.map((product: any) => {
      // Auto-select the color that was selected in master products page
      const selectedColors: ColorVariant[] = [];


      if (product.selectedStyleId && product.Styles) {
        // Find the style that was selected
        const selectedStyle = product.Styles.find((style: any) => Number(style.ID) === Number(product.selectedStyleId));

        if (selectedStyle) {
          selectedColors.push({
            colorName: selectedStyle.Name,
            colorCode: selectedStyle.HtmlColor1,
            styleId: selectedStyle.ID,
            useDefaultDesign: true,
            projectId: product.ProjectId,
            customizations: product.Customizations,
            templateId: product.TemplateId,
            jobId: product.JobId,
            sides: product.Sides,
            projectName: product.ProjectName || product.ProjectTitle || product.Description
          });
        }
      }

      // Every other colorway staged on the master-products card (StagedStyleIds — see
      // master-product-listing.component.ts's snapshotForBuild/proceedToColorSelection) gets
      // auto-added here too, in the same shape toggleColorSelection() builds when a color is
      // added manually below: same shared project/customizations/template (the DesignHuddle
      // project is color-agnostic — only the garment photo differs per color), sides deep-copied
      // with ProjectId cleared so initializeSingleSideFlow() generates this color's own preview
      // instead of reusing the locked color's already-rendered one. The locked color above is
      // skipped since it's already pushed.
      const stagedStyleIds: any[] = Array.isArray(product.StagedStyleIds) ? product.StagedStyleIds : [];
      for (const rawStyleId of stagedStyleIds) {
        const styleId = Number(rawStyleId);
        if (!styleId || styleId === Number(product.selectedStyleId)) continue;
        if (selectedColors.some(c => Number(c.styleId) === styleId)) continue;
        const stagedStyle = product.Styles?.find((style: any) => Number(style.ID) === styleId);
        if (!stagedStyle) continue;

        let variantSides = product.Sides;
        if (variantSides) {
          variantSides = JSON.parse(JSON.stringify(variantSides));
          variantSides.forEach((s: any) => { s.ProjectId = null; });
        }

        selectedColors.push({
          colorName: stagedStyle.Name,
          colorCode: stagedStyle.HtmlColor1,
          styleId: stagedStyle.ID,
          useDefaultDesign: true,
          projectId: product.ProjectId,
          customizations: product.Customizations,
          templateId: product.TemplateId,
          jobId: product.JobId,
          sides: variantSides,
          projectName: product.ProjectName || product.ProjectTitle || product.Description
        });
      }

      return {
        productId: product.ProductId,
        productName: product.Description,
        productCategory: product.CustomizationType || 'Product',
        productImage: product.PreviewImageUrl || product.ImageUrl, // Use generated preview with design
        selectedColors: selectedColors,
        availableColors: product.Styles || [],
        lockedStyleId: Number(product.selectedStyleId), // This color cannot be unchecked
        // WI 1378 — carry the decoration chosen on the master-products card (if any) so the
        // decoration dropdown pre-selects it; falls back to the product default when absent.
        selectedDecorationTypeId: product.selectedDecorationTypeId ?? null
      };
    });


    // In edit mode, pre-select additional colors from the published product's SavedStyles
    if (this.isEditMode && this.editProductData?.productDetails) {
      this.preSelectEditColors();
    }

    // In product request mode, pre-select every color the customer requested
    this.preSelectProductRequestColors();

    this.calculateTotalVariants();

    // Extract current side info per product (each product may have a different initial side)
    productsData.forEach((pd: any) => {
      const rawSideName = pd.currentSideName || 'front';
      const normalizedSide = rawSideName.toLowerCase().replace(/\s+/g, '_');
      this.currentSideNameMap.set(String(pd.ProductId), normalizedSide);
      this.buildAvailableSides(pd);
    });

    // Load decoration options for each product
    this.loadProductDecorations();

    // Initialize single-side flow instead of fetching all project details
    this.initializeSingleSideFlow();
  }

  /**
   * Fetch available decoration types for each selected product and attach them directly
   * to the product object.  Falls back to an empty array on error so the selector simply
   * stays hidden rather than breaking the page.
   * In edit mode, prefills selectedDecorationTypeId from the previously-saved value on the
   * customized-product detail (editProductData.productDetails.DecorationTypeId).
   */
  loadProductDecorations(): void {
    const storeId = Number(this.storeId) || undefined;

    this.selectedProducts.forEach(product => {
      const productIdNum = Number(product.productId);
      if (!productIdNum) return;

      this.decorationTypeService.getProductDecorations(productIdNum, storeId).subscribe({
        next: (decorations: ProductDecoration[]) => {
          (product as any).decorations = decorations || [];

          // Prefill from edit-mode saved value if present
          const editDecorationTypeId = this.isEditMode
            ? (this.editProductData?.productDetails?.DecorationTypeId ?? null)
            : null;

          if (editDecorationTypeId) {
            // Adopt the matching option's OWN value so [(ngModel)] is identity-equal to its
            // <option [ngValue]> and renders selected.
            const editMatch = (decorations || []).find(d => Number(d.DecorationTypeId) === Number(editDecorationTypeId));
            (product as any).selectedDecorationTypeId = editMatch ? editMatch.DecorationTypeId : editDecorationTypeId;
          } else {
            // WI 1378 — prefer the decoration carried from the master-products card when it maps to
            // one of this product's options; otherwise fall back to the product default. Match
            // numerically (the carried id is coerced to a number on the card via Number(), while the
            // API value is a long) and then adopt the matched option's OWN DecorationTypeId value, so
            // [(ngModel)]="selectedDecorationTypeId" is identity-equal to its <option [ngValue]> and the
            // dropdown actually shows the carried choice selected instead of silently dropping to default.
            const carried = (product as any).selectedDecorationTypeId;
            const carriedMatch = carried != null
              ? (decorations || []).find(d => Number(d.DecorationTypeId) === Number(carried))
              : null;
            if (carriedMatch) {
              (product as any).selectedDecorationTypeId = carriedMatch.DecorationTypeId;
            } else {
              const defaultDecoration = (decorations || []).find(d => d.IsDefault) || (decorations || [])[0];
              (product as any).selectedDecorationTypeId = defaultDecoration?.DecorationTypeId ?? null;
            }
          }

          // Keep the displayed decoration category (used for the subtitle here and the
          // non-removable default tag + Shopify product_type on the pricing page) in sync with
          // the resolved decoration, instead of leaving it on the product's default CustomizationType.
          this.applyDecorationCategory(product);
        },
        error: () => {
          (product as any).decorations = [];
          (product as any).selectedDecorationTypeId = null;
        }
      });
    });
  }

  /**
   * WI 1378 — set product.productCategory to the Name of the currently-selected decoration
   * (resolved from product.decorations by selectedDecorationTypeId). productCategory is what the
   * UI displays as the decoration and what the pricing page turns into the non-removable default
   * tag and the Shopify product_type, so it must track the user's choice rather than the product's
   * default CustomizationType. Leaves productCategory untouched when no match is found (single-
   * decoration products, or an unresolved id), preserving the existing CustomizationType fallback.
   */
  private applyDecorationCategory(product: any): void {
    const decorations: ProductDecoration[] = product?.decorations || [];
    const selectedId = product?.selectedDecorationTypeId;
    if (selectedId == null || decorations.length === 0) return;
    const match = decorations.find(d => Number(d.DecorationTypeId) === Number(selectedId));
    if (match && match.Name) {
      product.productCategory = match.Name;
    }
  }

  /**
   * WI 1378 — re-sync the displayed decoration category when the decoration dropdown changes on
   * this page. [(ngModel)] has already updated selectedDecorationTypeId; this only mirrors it into
   * productCategory so the subtitle + the data carried to pricing reflect the new choice.
   */
  onDecorationChange(product: any): void {
    this.applyDecorationCategory(product);
  }

  /**
   * Pre-select colors that were saved in the published product (edit mode).
   * For each style, builds per-side data by looking up that style's SavedStyle
   * within each CustomizedProduct (side). This correctly handles per-style alternate
   * designs from ProductStyleAlternateDesign table.
   */
  preSelectEditColors(): void {
    if (!this.editProductData?.productDetails) return;

    const details = this.editProductData.productDetails;
    const customizedProducts = details.CustomizedProducts || [];
    const productData = this.productsDataFromPreviousPage?.[0];
    const editCPs = productData?._editCustomizedProducts || customizedProducts;

    if (editCPs.length === 0) return;

    const parseCustomizations = (val: any) => {
      if (!val) return null;
      return typeof val === 'string' ? JSON.parse(val) : val;
    };

    // Collect ALL SavedStyles from all CPs into one flat list
    // (ProductStyleMappings all point to the first CP, so only the first CP has SavedStyles)
    const allSavedStyles: any[] = [];
    editCPs.forEach((cp: any) => {
      (cp.SavedStyles || []).forEach((ss: any) => {
        allSavedStyles.push(ss);
      });
    });

    // DIAGNOSTIC: Verify incoming data from state
    editCPs.forEach((cp: any, i: number) => {
      (cp.SavedStyles || []).forEach((ss: any) => {
        (ss.AlternateDesigns || []).forEach((ad: any) => {
        });
      });
    });

    // Collect unique styleIds
    const uniqueStyleIds = new Set<number>();
    allSavedStyles.forEach((ss: any) => {
      const id = Number(ss.UniqueStyleId || ss.StyleId);
      if (id) uniqueStyleIds.add(id);
    });

    if (uniqueStyleIds.size === 0) return;

    this.selectedProducts.forEach(product => {
      uniqueStyleIds.forEach(styleId => {
        // Get all SavedStyles entries for this specific style
        const styleEntries = allSavedStyles.filter((ss: any) => {
          const ssId = Number(ss.UniqueStyleId || ss.StyleId);
          return ssId === styleId;
        });

        // Collect all AlternateDesigns from styleEntries (one per side from ProductStyleAlternateDesign)
        const firstEntry = styleEntries[0];
        const alternateDesigns: any[] = firstEntry?.AlternateDesigns || [];
        const hasAlternateDesign = alternateDesigns.length > 0;


        // Build per-side data for this style.
        // When the style has AlternateDesigns, iterate over those directly — they are the
        // authoritative list of saved sides for this style. The locked style's CPs may have
        // a different number of sides (fewer or more), so iterating over CPs would truncate
        // or over-produce sides for this style.
        // When no AlternateDesigns exist, iterate over CPs as before (locked/same-design path).
        let styleSides: any[];

        if (hasAlternateDesign) {
          styleSides = alternateDesigns.map((perStyleData: any) => {
            // SideName may hold a decoration-location name ("Full Front", or a custom one like
            // "Left Chest") rather than a technical identifier — reverse-resolve so downstream
            // matching (productData.Sides, etc.) keeps working regardless of which form is stored.
            // Passing productData.Sides lets custom (non-default) labels resolve correctly too.
            const sideName = resolveSideAlias(perStyleData.SideName || 'front', productData?.Sides);
            // Look up the corresponding CP for TemplateId
            const matchingCp = editCPs.find((cp: any) =>
              resolveSideAlias(cp.SideName || 'front', productData?.Sides) === sideName
            );
            // Get PrintPlacement from product.Sides
            const productSide = productData?.Sides?.find((s: any) =>
              (s.SideName || '').toLowerCase() === sideName
            );
            const perStyleThumbnail = perStyleData?.PngRelativeUrlWithProduct || null;

            return {
              SideName: sideName,
              ProjectId: perStyleData?.DesignHuddleProjectId || matchingCp?.DesignHuddleProjectId || null,
              DownloadUrl: perStyleData?.AzureBlobPrintFileUrl || matchingCp?.AzureBlobPrintFileUrl || null,
              ThumbnailUrl: perStyleThumbnail || matchingCp?.AzureBlobThumbnailUrl || null,
              ThumbnailRelativeUrl: perStyleData?.ThumbnailRelativeUrl || perStyleThumbnail || matchingCp?.ThumbnailRelativeUrl || '',
              PageId: '',
              JobId: perStyleData?.DesignHuddleJobId || matchingCp?.DesignHuddleJobId || '',
              TemplateId: matchingCp?.DesignHuddleTemplateId || perStyleData?.DesignHuddleTemplateId || productSide?.TemplateId || null,
              PrintPlacement: productSide?.PrintPlacement || null,
              ElementPlacement: perStyleData?.ElementPlacement || matchingCp?.ElementPlacement || null,
              ZonePlacement: perStyleData?.ZonePlacement || matchingCp?.ZonePlacement || null,
              Customizations: parseCustomizations(perStyleData?.Customizations) || parseCustomizations(matchingCp?.Customizations) || null
            };
          }).filter((s: any) => s.SideName);
        } else {
          // No alternate design → iterate over CPs (locked/same-design replica path)
          const perSideImages: any[] = firstEntry?.PerSideImages || [];
          // Get correct image from CustomProductImage data (same source as product-detail page)
          const allImages: any[] = productData?._allProductImages || [];
          const styleCustomImages = allImages.filter((img: any) => img.UniqueStyleId == styleId && img.Id >= 0 && img.Image);
          const customImageUrl = styleCustomImages.length > 0 ? styleCustomImages[0].Image : null;

          styleSides = editCPs.map((cp: any, cpIndex: number) => {
            const sideName = resolveSideAlias(cp.SideName || 'front', productData?.Sides);
            // Get PrintPlacement from product.Sides
            const productSide = productData?.Sides?.find((s: any) =>
              (s.SideName || '').toLowerCase() === sideName
            );
            const sideImage = perSideImages[cpIndex] || null;
            const perSideThumbnail = sideImage?.ImageName || null;
            // Only fall back to CP-level thumbnail if this IS the locked/original style
            // For other styles, don't show the default color's thumbnail (wrong color product image)
            const isLockedStyle = Number(cp.StyleId) === Number(styleId);
            const fallbackThumbnail = isLockedStyle
              ? (cp.AzureBlobThumbnailUrl || cp.ThumbnailRelativeUrl || null)
              : null;
            // Priority: CustomProductImage (full quality) > PerSideImages > CP fallback
            const bestThumbnail = customImageUrl || perSideThumbnail || fallbackThumbnail || null;

            return {
              SideName: sideName,
              ProjectId: cp.DesignHuddleProjectId || null,
              DownloadUrl: cp.AzureBlobPrintFileUrl || null,
              ThumbnailUrl: bestThumbnail,
              ThumbnailRelativeUrl: bestThumbnail || '',
              PageId: '',
              JobId: cp.DesignHuddleJobId || '',
              TemplateId: cp.DesignHuddleTemplateId || productSide?.TemplateId || null,
              PrintPlacement: productSide?.PrintPlacement || null,
              ElementPlacement: cp.ElementPlacement || null,
              ZonePlacement: cp.ZonePlacement || null,
              Customizations: parseCustomizations(cp.Customizations) || null
            };
          }).filter((s: any) => s.SideName);
        }

        // For non-locked styles:
        // - When sourced from AlternateDesigns, all sides are authoritative (the entry proves the
        //   side was published for this variant). Include them all even if the thumbnail is null —
        //   it will be regenerated. Filtering by ThumbnailUrl here would drop sides whose thumbnail
        //   was never saved (e.g. a second side added later), causing the Charcoal/2-side truncation.
        // - When sourced from PerSideImages only (no AlternateDesigns), keep the thumbnail filter
        //   because those entries are inferred from the locked style's CPs and a missing thumbnail
        //   means the side was never actually published for this variant.
        // Locked style always keeps all sides.
        const isLockedStyle = Number(styleId) === Number(product.lockedStyleId);
        const filteredSides = isLockedStyle || hasAlternateDesign
          ? styleSides
          : styleSides.filter((s: any) => s.ThumbnailUrl != null);


        // Determine variant source:
        // 'customizedProduct' = locked style from CustomizeProducts table
        // 'alternateDesign' = from ProductStyleAlternateDesign
        // 'customProductImage' = replica from CustomProductImage only
        let variantSource = 'customProductImage';
        if (isLockedStyle) {
          variantSource = 'customizedProduct';
        } else if (hasAlternateDesign) {
          variantSource = 'alternateDesign';
        }

        // Check if already selected (the locked one from previous page)
        const alreadySelected = product.selectedColors.find(c => Number(c.styleId) === Number(styleId));
        if (alreadySelected) {
          // Enrich existing variant's sides with per-style DB data
          alreadySelected.sides = filteredSides;
          alreadySelected.projectId = filteredSides[0]?.ProjectId || alreadySelected.projectId;
          alreadySelected.jobId = filteredSides[0]?.JobId || alreadySelected.jobId;
          alreadySelected.customizations = filteredSides[0]?.Customizations || alreadySelected.customizations;
          alreadySelected.variantSource = variantSource;
          return;
        }

        // Find matching available color to add as new selection
        const availableColor = product.availableColors.find((c: any) => Number(c.ID) === Number(styleId));
        if (availableColor) {
          product.selectedColors.push({
            colorName: availableColor.Name,
            colorCode: availableColor.HtmlColor1,
            styleId: availableColor.ID,
            useDefaultDesign: true,
            projectId: filteredSides[0]?.ProjectId || null,
            customizations: filteredSides[0]?.Customizations || null,
            templateId: filteredSides[0]?.TemplateId || null,
            jobId: filteredSides[0]?.JobId || '',
            sides: filteredSides,
            projectName: product.selectedColors[0]?.projectName || product.productName,
            variantSource: variantSource
          });
        }
      });
    });

    // DIAGNOSTIC: Final state after preSelectEditColors
    this.selectedProducts.forEach(p => {
      p.selectedColors.forEach((v: any) => {
      });
    });

  }

  /**
   * Product request mode: pre-select every color the customer requested (beyond
   * the single locked color from the master product page). Reuses the DH project
   * data from the first variant so all colors share the same template/project.
   * Also sets per-side logos from StyleSideArtworks so each side uses the
   * customer's requested artwork as the logo and preview thumbnail uses the
   * product_image_url for that side/color.
   */
  preSelectProductRequestColors(): void {
    const prMode = this.stateService.getProductRequestMode();
    if (!prMode || !prMode.isProductRequestMode) return;

    const requestedStyles: any[] = prMode.requestedStyles || [];
    const styleSideArtworks: any[] = prMode.styleSideArtworks || [];
    if (requestedStyles.length === 0) return;

    // Product request mode always operates on a single product
    const product = this.selectedProducts[0];
    if (!product) return;

    // Build mapping: DB StyleId -> UniqueStyleId (variant.styleId uses UniqueStyleId)
    const dbStyleToUnique = new Map<number, string>();
    for (const rs of requestedStyles) {
      if (rs.StyleId && rs.ID) {
        dbStyleToUnique.set(Number(rs.StyleId), String(rs.ID));
      }
    }

    // Build per-variant, per-side artwork URL map from StyleSideArtworks
    // Map: UniqueStyleId -> { normalizedSideName -> artworkImageUrl }
    const variantArtworkMap = new Map<string, Map<string, string>>();
    for (const ssa of styleSideArtworks) {
      const dbStyleId = Number(ssa.StyleId || 0);
      const uniqueStyleId = dbStyleToUnique.get(dbStyleId) || String(dbStyleId);
      const sideName = (ssa.SideName || 'front').toLowerCase().replace(/\s+/g, '_');
      const artworkUrl = ssa.ArtworkImageUrl || '';
      if (!artworkUrl) continue;

      if (!variantArtworkMap.has(uniqueStyleId)) {
        variantArtworkMap.set(uniqueStyleId, new Map());
      }
      // Keep first artwork per side if multiple exist
      if (!variantArtworkMap.get(uniqueStyleId)!.has(sideName)) {
        variantArtworkMap.get(uniqueStyleId)!.set(sideName, artworkUrl);
      }
    }

    // Reuse DH project data from the first already-selected variant if present,
    // falling back to productsDataFromPreviousPage for the project fields.
    const productData = this.productsDataFromPreviousPage?.[0] || {};
    const firstVariant = product.selectedColors[0];
    const templateSides = firstVariant?.sides || productData.Sides;
    const projectId = firstVariant?.projectId || productData.ProjectId;
    const customizations = firstVariant?.customizations || productData.Customizations;
    const templateId = firstVariant?.templateId || productData.TemplateId;
    const projectName = firstVariant?.projectName || productData.ProjectName || productData.ProjectTitle || product.productName;

    // Apply per-side artwork logos to the locked (first) variant
    if (firstVariant) {
      const lockedArtworks = variantArtworkMap.get(String(firstVariant.styleId));
      if (lockedArtworks && lockedArtworks.size > 0) {
        if (!firstVariant.sideLogos) firstVariant.sideLogos = {};
        lockedArtworks.forEach((artworkUrl, sideName) => {
          firstVariant.sideLogos![sideName] = artworkUrl;
          const displayName = this.getDisplaySideName(sideName);
          if (displayName !== sideName) {
            firstVariant.sideLogos![displayName] = artworkUrl;
          }
        });
      }
    }

    const alreadySelectedIds = new Set(product.selectedColors.map(c => Number(c.styleId)));

    for (const reqStyle of requestedStyles) {
      const styleId = Number(reqStyle.ID || reqStyle.StyleId);
      if (!styleId || alreadySelectedIds.has(styleId)) continue;

      const availableStyle = (product.availableColors || []).find((s: any) => Number(s.ID) === styleId);
      if (!availableStyle) continue;

      // Build sideLogos from artwork map for this variant
      const sideLogos: { [sideName: string]: string } = {};
      const artworks = variantArtworkMap.get(String(styleId));
      if (artworks) {
        artworks.forEach((artworkUrl, sideName) => {
          sideLogos[sideName] = artworkUrl;
          const displayName = this.getDisplaySideName(sideName);
          if (displayName !== sideName) {
            sideLogos[displayName] = artworkUrl;
          }
        });
      }

      product.selectedColors.push({
        colorName: availableStyle.Name,
        colorCode: availableStyle.HtmlColor1,
        styleId: availableStyle.ID,
        useDefaultDesign: true,
        projectId: projectId,
        customizations: customizations,
        templateId: templateId,
        jobId: '',
        sides: templateSides,
        projectName: projectName,
        sideLogos: Object.keys(sideLogos).length > 0 ? sideLogos : undefined
      });
      alreadySelectedIds.add(styleId);
    }

  }

  /**
   * Generate preview images using DesignHuddle SDK for all variants and upload to Azure
   */
  uploadBackgroundsForAllVariants(): void {

    // WI-1521 fix #4: queue every (product, color) pair and only dispatch
    // MAX_CONCURRENT_VARIANT_PREVIEWS of them at a time instead of firing all of them (and every
    // DH createProject/export/upload chain underneath) simultaneously.
    this.variantPreviewQueue = [];
    this.activeVariantPreviewSlots = [];
    this.selectedProducts.forEach(product => {
      product.selectedColors.forEach(variant => {
        this.variantPreviewQueue.push({ product, variant });
      });
    });

    this.fillVariantPreviewSlots();
  }

  /**
   * Pulls queued (product, variant) preview jobs into empty concurrency slots (up to
   * MAX_CONCURRENT_VARIANT_PREVIEWS active at once) and starts their preview generation. Called
   * on initial dispatch and again whenever a slot frees up (see reapCompletedVariantPreviewSlots,
   * invoked from checkAndTriggerSave — the shared completion path every preview/export chain
   * funnels through).
   */
  private fillVariantPreviewSlots(): void {
    while (this.activeVariantPreviewSlots.length < this.MAX_CONCURRENT_VARIANT_PREVIEWS
        && this.variantPreviewQueue.length > 0) {
      const next = this.variantPreviewQueue.shift();
      if (!next) break;
      this.activeVariantPreviewSlots.push(next);
      this.generatePreviewsForVariant(next.product, next.variant);
    }
    // A variant with no qualifying sides (no projectId/pages/images) never adds anything to
    // generatingPreviews/uploadingBackgrounds, so nothing would ever call back to free its slot —
    // reap right away in case any slot just filled was already trivially complete.
    this.reapCompletedVariantPreviewSlots();
  }

  /**
   * Removes any active slot whose variant no longer has in-flight preview/upload work, then
   * refills freed slots from the queue. Cheap no-op when the queue mechanism isn't in use
   * (activeVariantPreviewSlots empty), so it's safe to call unconditionally from the shared
   * checkAndTriggerSave() completion path.
   */
  private reapCompletedVariantPreviewSlots(): void {
    if (this.activeVariantPreviewSlots.length === 0) return;

    const stillActive = this.activeVariantPreviewSlots.filter(slot => {
      const prefix = `${slot.product.productId}_${slot.variant.styleId}_`;
      for (const key of this.generatingPreviews) {
        if (key.startsWith(prefix)) return true;
      }
      for (const key of this.uploadingBackgrounds) {
        if (key.startsWith(prefix)) return true;
      }
      return false;
    });

    if (stillActive.length === this.activeVariantPreviewSlots.length) return; // nothing finished yet

    this.activeVariantPreviewSlots = stillActive;
    if (this.variantPreviewQueue.length > 0) {
      this.fillVariantPreviewSlots();
    }
  }

  /**
   * Generate preview images for all sides of a specific variant using DesignHuddle SDK
   * Uses project details to get correct page_id for each side (same as export-job)
   */
  generatePreviewsForVariant(product: ProductColorVariant, variant: ColorVariant): void {
    // Get the style for this variant
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) {
      return;
    }

    // Get project details to find page_id for each side (same as export-job)
    if (!variant.projectId) {
      return;
    }

    const projectDetails = this.projectDetailsMap.get(variant.projectId);
    if (!projectDetails) {
      // Project details will be fetched, previews will be generated after
      return;
    }

    // Get pages from project details (same structure as export-job)
    const pages = projectDetails?.data?.pages || projectDetails?.pages || [];
    if (pages.length === 0) {
      return;
    }

    const originalData = this.productsDataFromPreviousPage.find(
      p => String(p.ProductId) === String(product.productId)
    );

    // Find a template ID to use
    const sideWithTemplate = originalData?.Sides?.find((s: any) => s.TemplateId);
    const defaultTemplateId = sideWithTemplate?.TemplateId;


    // Side config mapping for background keys and image fields
    const sideConfigs: { [key: string]: { displayName: string, imageField: string, backgroundKey: string } } = {
      'front': { displayName: 'Front', imageField: 'ImageFilePath_Front', backgroundKey: 'background_front' },
      'back': { displayName: 'Back', imageField: 'ImageFilePath_Back', backgroundKey: 'background_back' },
      'sleeve_left': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'left_sleeve': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'sleeve left': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'sleeve_right': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' },
      'right_sleeve': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' },
      'sleeve right': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' }
    };

    // Generate preview for each page from project details (same as export-job)
    pages.forEach((page: any, index: number) => {
      const pageId = page.page_id || page.id || '';
      const pageName = (page.name || page.page_name || 'front').toLowerCase().replace(/\s+/g, '_');
      // Use page_number from API if available, otherwise use index + 1
      const pageNumber = page.page_number || page.pageNumber || (index + 1);

      if (!pageId) {
        return;
      }

      // Find matching side config - enhanced matching for sleeves
      let sideConfig = sideConfigs[pageName];
      if (!sideConfig) {
        // Try partial matching with improved logic for sleeves
        for (const [key, config] of Object.entries(sideConfigs)) {
          // Check for sleeve left variations
          if ((pageName.includes('sleeve') && pageName.includes('left')) ||
              (pageName.includes('left') && pageName.includes('sleeve'))) {
            if (key === 'sleeve_left' || key === 'left_sleeve') {
              sideConfig = config;
              break;
            }
          }
          // Check for sleeve right variations
          else if ((pageName.includes('sleeve') && pageName.includes('right')) ||
                   (pageName.includes('right') && pageName.includes('sleeve'))) {
            if (key === 'sleeve_right' || key === 'right_sleeve') {
              sideConfig = config;
              break;
            }
          }
          // General partial matching
          else if (pageName.includes(key) || key.includes(pageName)) {
            sideConfig = config;
            break;
          }
        }
      }

      if (!sideConfig) {
        return;
      }

      // Check if style has image for this side
      if (!variantStyle[sideConfig.imageField]) {
        return;
      }

      // Build side object with page_id and page_number from project details
      const side = {
        SideName: pageName,
        DisplayName: sideConfig.displayName,
        BackgroundKey: sideConfig.backgroundKey,
        ImageField: sideConfig.imageField,
        TemplateId: defaultTemplateId,
        PageId: pageId,  // Use page_id from project details (for save payload)
        PageNumber: pageNumber  // Use page_number for preview URL call
      };

      this.generateSidePreview(product, variant, side, variantStyle);
    });
  }

  /**
   * Generate preview for a single side using DesignHuddle SDK getVariableTemplatePreviewURL
   */
  generateSidePreview(product: ProductColorVariant, variant: ColorVariant, side: any, variantStyle: any): void {
    const cacheKey = `${product.productId}_${variant.styleId}_${side.DisplayName}`;

    // Skip if already cached or generating
    if (this.backgroundUrls.has(cacheKey) || this.generatingPreviews.has(cacheKey)) {
      return;
    }

    // Check if DesignHuddle SDK is available
    if (typeof (window as any).DSHDLib === 'undefined') {
      return;
    }

    const numericTemplateId = parseInt(side.TemplateId);

    // Get PLAIN product side image URL for DesignHuddle template (product_image_url).
    // Must be the blank product image — NOT the customized/preview image.
    // DH overlays the design on top, so using a customized image would double the design.
    let productImageUrl = '';
    let rawImagePath: string = variantStyle[side.ImageField] || '';
    if (!rawImagePath) {
      rawImagePath = variantStyle.ImageFilePath_Front
        || variantStyle.ImageFilePath_Back
        || variantStyle.ImageFilePath_SleeveLeft
        || variantStyle.ImageFilePath_SleeveRight
        || '';
    }
    productImageUrl = rawImagePath;
    if (productImageUrl && !productImageUrl.startsWith('http')) {
      productImageUrl = this.joinUrl(this.imgBaseURL, productImageUrl);
    }
    if (!productImageUrl) {
      productImageUrl = (product as any).productImage || '';
    }

    // Get logo URL - check for side-specific custom logo first
    // In edit mode, prefer designUrl (actual logo) over previewUrl (product mockup)
    let logoUrl = this.isEditMode
      ? (this.designData?.designUrl || this.designData?.previewUrl || '')
      : (this.designData?.previewUrl || this.designData?.designUrl || '');
    const sideNameNormForLogo = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
    const sideNameVariations = [side.DisplayName, side.SideName, side.DisplayName.toLowerCase(), sideNameNormForLogo];
    for (const nameVariation of sideNameVariations) {
      if (variant.sideLogos && variant.sideLogos[nameVariation]) {
        logoUrl = variant.sideLogos[nameVariation];
        break;
      }
    }
    // In edit mode, extract logo from saved Customizations per side
    const defaultLogoUrl = this.isEditMode
      ? (this.designData?.designUrl || this.designData?.previewUrl || '')
      : (this.designData?.previewUrl || this.designData?.designUrl || '');
    if (this.isEditMode && logoUrl === defaultLogoUrl) {
      const sideLogoFromCust = this.getLogoUrlFromSideCustomizations(variant, sideNameNormForLogo);
      if (sideLogoFromCust) {
        logoUrl = sideLogoFromCust;
      }
    }
    // For NEW (non-locked) colors, fallback to locked variant's side logo if still default.
    // WI-1522 fix #2: this used to be gated on isEditMode, so a color added in CREATE mode after a
    // secondary imprint (e.g. Full Back) was set up on the locked/default color kept the primary
    // logo (or none) on that side instead of inheriting it — the reported "second imprint doesn't
    // apply to new colors" bug. getLockedVariantLogoForSide reads the locked variant's LIVE
    // sideLogos/Customizations (not a snapshot taken when this color was added), so this also
    // picks up the locked color's latest edit to that side whenever this preview is (re)generated.
    if (Number(variant.styleId) !== Number(product.lockedStyleId)
        && logoUrl === defaultLogoUrl) {
      const lockedVariant = product.selectedColors.find(
        (c: ColorVariant) => Number(c.styleId) === Number(product.lockedStyleId)
      );
      const lockedLogo = this.getLockedVariantLogoForSide(lockedVariant, sideNameNormForLogo);
      if (lockedLogo) {
        logoUrl = lockedLogo;
      }
    }
    // Fall back to variant-level custom logo
    if (variant.variantDesignUrl && !variant.useDefaultDesign) {
      logoUrl = variant.variantDesignUrl;
    }

    // Resolve the saved editor placement the same way every other render path in this component
    // does. This used to be hardcoded `null` on the assumption that generateSidePreview only ever
    // runs for fresh, unedited previews — it does not: a left-chest design placed in the editor
    // still reached here and rendered as a full front, because with no media_position DH auto-fits
    // the logo to the ENTIRE zone (see bug-left-chest-thumbnail-mismatch).
    //
    // Feeding the stored placement is safe because calculateMediaPosition already rejects the two
    // non-edit cases: a pristine auto-fit (element === zone) and a near-centred result
    // (|x-0.5| < 0.02 && |y-0.5| < 0.02). So a full-front auto-fit still returns null and falls
    // through to the top-anchor default below, while a genuine off-centre placement like a left
    // chest is honoured as-is.
    const sideNameNorm = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
    const mediaPosition = this.getMediaPositionForSide(variant, sideNameNorm);
    const customizationObject = this.buildLogoCustomization(logoUrl, mediaPosition);
    // NOTE: no background_* is injected. The per-color shirt already comes from product_image_url
    // (set per-variant below), so a template background_front just duplicated it and diverged this
    // customization from the working product-card preview (logo only). Keeping it logo-only.

    // Parse PrintPlacement for zone dimensions and offset from THIS specific side's data.
    // Match canonically (front/back/sleeve_left/sleeve_right) and DO NOT fall back to the
    // current side — that made back/sleeve previews inherit front's print placement.
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    const currentSideInfo = this.getSideInfoForSide(product.productId, sideNameNorm);
    if (!currentSideInfo || !currentSideInfo.printPlacement) {
      // Falling back to the 300x300/100x100 default zone renders a large, roughly centred
      // "full front" composite that will NOT match the print file. This used to be a bare
      // console.warn, so a designer shipped a wrong mockup with no signal at all.
      const safeSide = this.canonicalSide(sideNameNorm);
      console.error('❌ Print placement unavailable — preview falling back to default zone', {
        productId: product.productId,
        styleId: variant.styleId,
        side: sideNameNorm,
        canonicalSide: safeSide,
        sideInfoFound: !!currentSideInfo,
        printPlacementFound: !!currentSideInfo?.printPlacement,
        fallbackZoneDimensions: zoneDimensions,
        fallbackZoneOffset: zoneOffset
      });
      // One toast per product+side, not per colour variant — this runs for every selected colour
      // and would otherwise stack a dozen identical warnings for the same missing placement.
      const warnKey = `${product.productId}_${safeSide}`;
      if (!this.placementWarningsShown.has(warnKey)) {
        this.placementWarningsShown.add(warnKey);
        this.notification.warning(
          `Print placement unavailable for ${safeSide} — thumbnail may not match the print file.`,
          'Placement missing'
        );
      }
    }
    if (currentSideInfo && currentSideInfo.printPlacement) {
      try {
        const pp = typeof currentSideInfo.printPlacement === 'string'
          ? JSON.parse(currentSideInfo.printPlacement)
          : currentSideInfo.printPlacement;
        if (pp.Width && pp.Height) {
          zoneDimensions = `${pp.Width}x${pp.Height}`;
        }
        if (pp.X !== undefined && pp.Y !== undefined) {
          zoneOffset = `${pp.X}x${pp.Y}`;
        }
      } catch (e) {
      }
    }

    // NOTE: no top-anchor media_position is injected here. In the composited preview,
    // top-anchoring is done purely by product_image_canvas_zone_vertical_align:"top" (added to
    // params below) — exactly like the product-card catalog preview. Stacking a top-anchor
    // media_position on TOP of vertical_align double-anchors and makes this thumbnail disagree
    // with the product card. A real editor placement (mediaPosition) is still applied via
    // buildLogoCustomization above. The EXPORT top-anchors separately via media_position
    // (createSideCustomizations), since the export job never sees vertical_align.

    // Guard: DSHDLib.getVariableTemplatePreviewURL silently never fires the callback when
    // product_image_url isn't absolute. After we removed the InkSoft host prefix, any legacy
    // relative path bleeds through as a non-absolute URL and would strand the spinner forever.
    // Fail fast here so the gate advances instead.
    if (!productImageUrl || !/^https?:\/\//i.test(productImageUrl)) {
      console.error(`❌ Skipping preview for ${cacheKey}: product_image_url is not absolute (${productImageUrl})`);
      this.backgroundUrls.set(cacheKey, '');
      this.checkAndTriggerSave();
      return;
    }

    // Mark as generating
    this.generatingPreviews.add(cacheKey);
    this.pendingUploads++;

    // WI-1521-followup (progressive preview): fire a FAST, stateless, projectless preview right
    // away — the same DSHDLib.getVariableTemplatePreviewURL SDK call and params master-product-
    // listing already uses for its own card thumbnails, which is exactly why THAT page can show a
    // preview so much faster than color-selection historically did. This is deliberately
    // independent of, and non-blocking for, the slower project+export-job pipeline below: it only
    // ever writes backgroundUrls (what the <img> actually renders), never touches
    // generatingPreviews/pendingUploads, so the save-flow gate (checkAndTriggerSave) and WI-1524's
    // failure tracking still depend ENTIRELY on the slow path's own completion, unchanged. If the
    // fast call errors or never resolves, the user just keeps seeing the full spinner until the
    // slow path finishes, like before — no regression, purely additive.
    this.fireFastPlaceholderPreview(numericTemplateId, customizationObject, productImageUrl, zoneDimensions, zoneOffset, mediaPosition, cacheKey);

    // This persisted thumbnail is generated via a DEDICATED per-side DH project + the export-job
    // API (NOT the stateless getVariableTemplatePreviewURL SDK call) — see refreshPreviewViaExport.
    // WHY a project at all: the SDK preview's low fixed canvas (300/500) under-resourced small
    // print zones (blur/"zoomed in"), but simply bumping those numbers distorts every saved
    // print-zone placement — product_image_canvas_zone_dimensions/offset are pixel coordinates
    // calibrated against product_image_width:500. The export-job path exposes a genuinely
    // independent resolution knob (its top-level `width`) that doesn't share that coordinate space.
    //
    // WHY REUSE (added after this was found to be the actual cause of "still creates a project for
    // every color" — this function used to create a FRESH project on EVERY call, completely
    // independent of and bypassing the side-sharing reuse logic in toggleColorSelection/
    // onSideChange/ensurePendingSideProjectsBeforeSave, since generatePreviewForCurrentSide is
    // called from every one of those branches, including their "reuse" branches): export-job has no
    // customizations-override parameter — it only renders whatever is already SAVED in the DH
    // project. That's true whether this project was created HERE or by the print-export path
    // (createProjectForSide) — both seed with the identical logo/media_position for an unedited
    // design. So as long as `sameLogo` holds (this color has no custom logo override for this side),
    // any already-existing project for this (product, side) — created by ANY color, for preview OR
    // print — already has the exact customizations this preview needs, and can be reused directly.
    // Only when no such project exists yet, or this color needs its own custom logo, do we actually
    // create one — same findSideProjectOwner/pendingSideProjectCreations machinery as everywhere
    // else in this file.
    const variantKey = `${product.productId}_${variant.styleId}`;

    const createFreshProjectAndPreview = (registerAsShared: boolean): Promise<void> => {
      const projectTitle = this.generateRandomProjectTitle();
      const creation: Promise<string> = new Promise((resolve) => {
        (window as any).DSHDLib.createProject(
          {
            template_id: numericTemplateId,
            project_title: toDesignHuddleProjectTitle(projectTitle),
            customizations: customizationObject
          },
          (error: any, projectData: any) => {
            if (error || !projectData?.project_id) {
              console.error(`❌ createProject failed for ${cacheKey}:`, error);
              resolve('');
              return;
            }
            resolve(projectData.project_id);
          }
        );
      });
      if (registerAsShared) {
        this.pendingSideProjectCreations.set(sideKey, creation);
      }
      return creation.then(projectId => {
        if (registerAsShared) this.pendingSideProjectCreations.delete(sideKey);
        if (!projectId) {
          this.generatingPreviews.delete(cacheKey);
          this.pendingUploads--;
          this.backgroundUrls.set(cacheKey, '');
          this.checkAndTriggerSave();
          return;
        }
        this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, projectId);
        this.refreshPreviewViaExport(projectId, productImageUrl, zoneDimensions, zoneOffset, cacheKey, product, variant, side.DisplayName, sideNameNorm);
      });
    };

    // WI-1578: compare against the locked color's CURRENT logo VALUE (same approach
    // addSideToSingleVariant already uses for its own reuse decision), not mere presence of a
    // sideLogos entry. A bulk action (Edit Design propagation, Add to All Colors, Replace Artwork
    // across all colors) deliberately writes the SAME sideLogos value onto every variant so each
    // one resolves the right graphic — treating "has an entry at all" as "needs its own project"
    // made every one of those bulk flows create a redundant DH project per color instead of sharing
    // the one actually edited/uploaded project, which in turn could race with (and sometimes starve)
    // that color's print export, surfacing as a false "no print file for this side" even though no
    // API call actually failed.
    const lockedVariantForSide = Number(variant.styleId) === Number(product.lockedStyleId)
      ? variant
      : product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId));
    const lockedLogoUrl = this.getLockedVariantLogoForSide(lockedVariantForSide, sideNameNorm);
    const newLogoUrl = variant.sideLogos?.[side.DisplayName] || variant.sideLogos?.[sideNameNorm] || '';
    const sameLogo = !newLogoUrl || newLogoUrl === lockedLogoUrl;
    const sideKey = `${product.productId}_${sideNameNorm}`;
    const inFlight = sameLogo ? this.pendingSideProjectCreations.get(sideKey) : undefined;
    const already = sameLogo ? this.getSharedSideProjectId(product, sideNameNorm) : null;

    if (inFlight || already) {
      const waitFor = inFlight || Promise.resolve(already as string);
      waitFor.then(sharedProjectId => {
        if (!sharedProjectId) {
          // The owner's creation failed — nothing to borrow; become the new owner instead.
          createFreshProjectAndPreview(true);
          return;
        }
        this.refreshPreviewViaExport(sharedProjectId, productImageUrl, zoneDimensions, zoneOffset, cacheKey, product, variant, side.DisplayName, sideNameNorm);
      });
      return;
    }

    createFreshProjectAndPreview(sameLogo);
  }

  /**
   * Fast, stateless, projectless preview — DSHDLib.getVariableTemplatePreviewURL, the same SDK
   * call and params master-product-listing already uses for its own card thumbnails (see its
   * refreshPreviewAfterEditorClose). No DH project is created or needed; DH renders the
   * template+customization+garment-photo composite server-side and hands back a URL directly.
   * Deliberately the SAME lower resolution master-product-listing uses (width 300 /
   * product_image_width 500 — see that component's own "REVERTED to 300/500 (original)" comment
   * for why those two numbers can't be bumped without distorting every saved print-zone
   * placement) — that's an accepted tradeoff for speed here, since the real, full-resolution
   * thumbnail from the slow export-job pipeline in generateSidePreview still overwrites this one
   * once it's ready (same cacheKey in backgroundUrls). Never touches generatingPreviews/
   * pendingUploads — those stay owned entirely by the slow pipeline, so the save-flow gate and
   * WI-1524's failure tracking are completely unaffected by whatever this does.
   */
  private fireFastPlaceholderPreview(
    numericTemplateId: number,
    customizationObject: any,
    productImageUrl: string,
    zoneDimensions: string,
    zoneOffset: string,
    mediaPosition: any,
    cacheKey: string
  ): void {
    const DSHDLib = (window as any).DSHDLib;
    if (!DSHDLib || typeof DSHDLib.getVariableTemplatePreviewURL !== 'function') return;

    // Flatten onto white first, same as the slow path — DH's product_image compositing has no
    // background/matte param of its own (see designhuddle-no-background-param gotcha), so skipping
    // this would show a checkerboard behind any transparent product photo in the fast placeholder.
    this.productImageMatteService.getFlattenedProductImageUrl(productImageUrl).subscribe(flattenedUrl => {
      DSHDLib.getVariableTemplatePreviewURL(
        {
          template_id: numericTemplateId,
          customizations: customizationObject,
          width: 300,
          product_image_url: flattenedUrl,
          product_image_width: 500,
          product_image_canvas_zone_dimensions: zoneDimensions,
          product_image_canvas_zone_offset: zoneOffset,
          ...(mediaPosition ? {} : { product_image_canvas_zone_vertical_align: 'top' }),
          fit: 'center'
        },
        (error: any, url: string) => {
          if (error || !url) return;
          // The slow path may have already finished (success or failure) by the time this
          // resolves — if so, don't clobber its result with a lower-res late arrival.
          if (!this.generatingPreviews.has(cacheKey)) return;
          this.backgroundUrls.set(cacheKey, url);
        }
      );
    });
  }

  /**
   * True when a newer render for this cacheKey has started since `generation` was captured —
   * i.e. this callback's result is stale and must not be written to backgroundUrls.
   * `undefined` means the caller opted out of the check (refreshPreviewAfterEditorClose is
   * itself the newest render, so its writes always win).
   */
  private isStalePreview(cacheKey: string, generation?: number): boolean {
    if (generation === undefined) return false;
    return (this.previewGeneration.get(cacheKey) || 0) !== generation;
  }

  /**
   * Build a descriptive file name prefix from project context for Azure uploads.
   */
  buildAzureFileName(productId: string, styleId: number, sideName: string): string {
    const variantKey = `${productId}_${styleId}`;
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const projectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`) || '';

    // Find the variant to get projectName and colorName
    let projectTitle = '';
    let colorName = '';
    for (const product of this.selectedProducts) {
      const variant = product.selectedColors.find(v => Number(v.styleId) === Number(styleId));
      if (variant) {
        projectTitle = variant.projectName || '';
        colorName = variant.colorName || '';
        break;
      }
    }

    // Sanitize parts for filename
    const sanitize = (s: string) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
    const parts = [sanitize(projectTitle), sanitize(projectId), sanitize(colorName), sanitize(sideNorm)].filter(p => p);
    return parts.length > 0 ? parts.join('_') : '';
  }

  /**
   * Upload the generated preview image to Azure via upload-huddle-background
   */
  uploadPreviewToAzure(previewUrl: string, cacheKey: string, productId: string, styleId: number, sideName: string, generation?: number): void {
    this.uploadingBackgrounds.add(cacheKey);

    // 20s hard cap on fetch(previewUrl); without it a hung DH preview URL strands isPreviewLoading.
    const fetchController = new AbortController();
    const fetchTimer = setTimeout(() => fetchController.abort(), 20000);

    // Fetch the preview image from DesignHuddle
    fetch(previewUrl, { signal: fetchController.signal })
      .then(response => {
        clearTimeout(fetchTimer);
        if (!response.ok) {
          throw new Error(`Failed to fetch preview: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Pad the composited preview onto a fixed white square canvas before it's persisted —
        // DH's output dimensions otherwise vary per garment-photo aspect ratio (see
        // ProductPreviewNormalizeService), which is what made Shopify's product grid look
        // asymmetric across different templates. Falls back to the original blob on failure.
        return firstValueFrom(this.productPreviewNormalizeService.normalizeToSquare(blob));
      })
      .then(normalizedBlob => {
        // Convert blob to base64
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(normalizedBlob);
        });
      })
      .then(base64 => {
        // Upload to Azure via upload-huddle-background endpoint
        const azureFileName = this.buildAzureFileName(productId, styleId, sideName);
        this.http.post(`${environment.baseUrl}/Common/upload-huddle-background`, {
          File: base64,
          Extension: 'png',
          FileName: azureFileName || undefined
        }).pipe(timeout(30000)).subscribe({
          next: (response: any) => {
            this.uploadingBackgrounds.delete(cacheKey);
            this.pendingUploads--;

            // Always seed backgroundUrls so isPreviewLoading flips false even if the upload
            // came back without a filename or with the new full-URL response shape.
            let azureUrl = '';
            if (response.StatusCode === 200) {
              const responseValue = response.Data?.ResponseData || '';
              if (responseValue) {
                // Backend now returns the full Azure URL; older builds returned bare filename.
                // Detect and handle both: if it already starts with http, use as-is.
                azureUrl = responseValue.startsWith('http')
                  ? responseValue
                  : `${environment.huddleBackgroundFolder}${responseValue}`;
              }
            } else {
              console.error(`❌ Failed to upload preview for ${cacheKey}:`, response.Message);
            }
            // Drop the write if a newer render (post-edit refresh) superseded this one.
            if (this.isStalePreview(cacheKey, generation)) {
            } else {
              this.backgroundUrls.set(cacheKey, azureUrl);
            }

            this.checkAndTriggerSave();
          },
          error: (error) => {
            this.uploadingBackgrounds.delete(cacheKey);
            this.pendingUploads--;
            if (!this.isStalePreview(cacheKey, generation)) {
              this.backgroundUrls.set(cacheKey, '');
            }
            console.error(`❌ Error uploading preview for ${cacheKey}:`, error);
            this.checkAndTriggerSave();
          }
        });
      })
      .catch(error => {
        clearTimeout(fetchTimer);
        this.uploadingBackgrounds.delete(cacheKey);
        this.pendingUploads--;
        if (!this.isStalePreview(cacheKey, generation)) {
          this.backgroundUrls.set(cacheKey, '');
        }
        console.error(`❌ Error fetching preview for ${cacheKey}:`, error);
        this.checkAndTriggerSave();
      });
  }

  /**
   * Check if all uploads are complete and trigger save if waiting
   */
  checkAndTriggerSave(): void {

    // WI-1521 fix #4: every preview/export completion path funnels through here — piggyback the
    // concurrency-queue reap+refill on it instead of adding a separate callback chain.
    this.reapCompletedVariantPreviewSlots();

    if (this.pendingUploads <= 0 && this.saveWaitingForUploads) {
      this.saveWaitingForUploads = false;

      // Rebuild products from fresh selectedProducts data
      const freshProductsData = this.rebuildProductsFromSelectedProducts();
      this.prepareFinalPayloadAndSave(freshProductsData);
    } else if (this.pendingUploads <= 0) {
      // Uploads finished but no save pending (e.g. color-click preview generation complete).
      // Re-evaluate the export gate so branch 3 of checkAllExportsComplete can hide the loader.
      this.checkAllExportsComplete();
    }
  }

  // ==================== Single-Side Flow Methods ====================

  /**
   * Build available sides dropdown data from product Sides array
   */
  buildAvailableSides(productData: any): void {
    const productId = String(productData.ProductId);
    const sides: { name: string, displayName: string, templateId: number, printPlacement: any }[] = [];

    // In the edit flow, productData.Sides is overridden with DB sides (from CustomizedProducts),
    // which only contains sides that were previously published and lacks the full set from the
    // sides table. masterProductSides preserves the original API sides (Name/DisplayName/TemplateId)
    // so we can build the complete dropdown. Fall back to productData.Sides for the create flow.
    const sidesSource: any[] = productData.masterProductSides?.length > 0
      ? productData.masterProductSides
      : (productData.Sides || []);

    const fallbackTemplate = productData.currentTemplateId ||
      sidesSource.find((s: any) => s.TemplateId)?.TemplateId ||
      (productData.Sides || []).find((s: any) => s.TemplateId)?.TemplateId || 0;

    const defaultStyle = productData.Styles?.find((s: any) => Number(s.ID) === Number(productData.selectedStyleId))
      || productData.Styles?.[0];

    // Map side names to their ImageFilePath field for checking if the style has images.
    // Keyed by canonicalSide()'s own output (front/back/sleeve_left/sleeve_right) — NOT by literal
    // name variants — because the raw DB Side.Name for a sleeve has NO separator at all
    // ("sleeveleft"/"sleeveright", confirmed by getSideConfigForCurrentSide's own doc comment on
    // this exact issue). A literal-key map like this used to miss that form entirely, silently
    // dropping the sleeve from this primary loop (recovered only by the "Backfill" step below,
    // which already used canonicalSide — this lookup was the one spot never updated to match).
    const sideToImageField: { [key: string]: string } = {
      'front': 'ImageFilePath_Front',
      'back': 'ImageFilePath_Back',
      'sleeve_left': 'ImageFilePath_SleeveLeft',
      'sleeve_right': 'ImageFilePath_SleeveRight'
    };

    // A side is available if ANY style for this product has its image populated — not just
    // the user-selected style. Otherwise, picking a color variant without a sleeve image
    // would hide the sleeve side from every variant's dropdown.
    const styles: any[] = productData.Styles || [];
    const anyStyleHasImage = (imageField: string): boolean => {
      if (defaultStyle && defaultStyle[imageField]) return true;
      return styles.some((s: any) => s && s[imageField]);
    };

    const addedSideNames = new Set<string>();

    // Build from sidesSource: master product sides when available (edit flow), or Sides (create flow)
    if (sidesSource.length > 0) {
      sidesSource.forEach((side: any) => {
        const rawName = (side.SideName || side.Name || side.DisplayName || '').toLowerCase();
        // Canonical lookup (not a literal-string one) — the raw DB Side.Name for a sleeve has NO
        // separator at all ("sleeveleft"/"sleeveright"), which matched none of the old map's keys
        // and silently dropped the side from this loop entirely (recovered only by the Backfill
        // step below, which already used canonicalSide).
        const canon = this.canonicalSide(rawName);
        const imageField = sideToImageField[canon];
        // Normalized to the SAME token convention the Backfill step's standardSides list below
        // uses (front/back/left_sleeve/right_sleeve — left/right-first, NOT canonicalSide's own
        // sleeve-first 'sleeve_left'/'sleeve_right' form) for exactly those 4 standard sides only.
        // Using canonicalSide's raw output here instead would desync this loop's addedSideNames
        // entries from the Backfill guard's literal 'left_sleeve'/'right_sleeve' checks (so a
        // sleeve resolved by THIS loop would no longer be recognized as already-added and the
        // Backfill step would push a duplicate), and canonicalSide also strips ALL separators
        // (including underscores) from any OTHER, non-standard side name — a plain
        // whitespace-to-underscore normalize is kept for those instead, unchanged from before.
        const normalized = canon === 'sleeve_left' ? 'left_sleeve'
          : canon === 'sleeve_right' ? 'right_sleeve'
          : (canon === 'front' || canon === 'back') ? canon
          : rawName.replace(/\s+/g, '_');
        if (imageField && anyStyleHasImage(imageField)) {
          // Prefer TemplateId/PrintPlacement from the matching DB side (productData.Sides).
          // Match canonically so legacy DB names ("Left Sleeve", "SleeveLeft", "sleeve_left")
          // still line up — otherwise that side's PrintPlacement falls back to the default zone.
          const dbSide = (productData.Sides || []).find((s: any) =>
            this.canonicalSide(s.SideName || s.Name || s.DisplayName || '') === this.canonicalSide(rawName)
          );
          const resolvedPrintPlacement = side.PrintPlacement || dbSide?.PrintPlacement || null;
          if (!resolvedPrintPlacement) {
          }
          sides.push({
            name: normalized,
            // Prefer the side's own stored DisplayName (what an admin sees/sets in Product
            // Management, e.g. "Left Side") over the technical-name-derived default ("Left
            // Sleeve") — the technical Name (sleeveleft/sleeveright) never changes even after a
            // custom rename, so deriving the label from it silently overrode the admin's label
            // in the customize-product flow while Product Management itself showed the real one
            // (product 58: Name=sleeveleft, DisplayName="Left Side" — fixed to show "Left Side"
            // here too). Falls back to the derived label only when no DisplayName is stored.
            displayName: side.DisplayName || dbSide?.DisplayName || this.getDisplaySideName(rawName),
            templateId: side.TemplateId
              ? parseInt(side.TemplateId)
              : (dbSide?.TemplateId ? parseInt(dbSide.TemplateId) : parseInt(fallbackTemplate)),
            printPlacement: resolvedPrintPlacement
          });
          addedSideNames.add(normalized);
        }
      });
    }

    // Backfill: if any standard side has image data on the styles but is missing from
    // sidesSource (e.g., the API Sides array is incomplete), still add it to the dropdown.
    const standardSides: { field: string, normalized: string, raw: string }[] = [
      { field: 'ImageFilePath_Front', normalized: 'front', raw: 'front' },
      { field: 'ImageFilePath_Back', normalized: 'back', raw: 'back' },
      { field: 'ImageFilePath_SleeveLeft', normalized: 'left_sleeve', raw: 'left sleeve' },
      { field: 'ImageFilePath_SleeveRight', normalized: 'right_sleeve', raw: 'right sleeve' }
    ];

    standardSides.forEach(({ field, normalized, raw }) => {
      if (addedSideNames.has(normalized)) return;
      if (!anyStyleHasImage(field)) return;

      // Canonical match so legacy DB/side names ("Left Sleeve", "SleeveLeft", "sleeve_left")
      // resolve to this standard side and carry its own PrintPlacement.
      const canonTarget = this.canonicalSide(normalized);
      const matchingSide = sidesSource.find((s: any) =>
        this.canonicalSide(s.SideName || s.Name || s.DisplayName || '') === canonTarget
      );
      const dbSide = (productData.Sides || []).find((s: any) =>
        this.canonicalSide(s.SideName || s.Name || s.DisplayName || '') === canonTarget
      );
      const resolvedPrintPlacement = matchingSide?.PrintPlacement || dbSide?.PrintPlacement || null;
      if (!resolvedPrintPlacement) {
      }

      sides.push({
        name: normalized,
        displayName: this.getDisplaySideName(raw),
        templateId: matchingSide?.TemplateId
          ? parseInt(matchingSide.TemplateId)
          : (dbSide?.TemplateId ? parseInt(dbSide.TemplateId) : parseInt(fallbackTemplate)),
        printPlacement: resolvedPrintPlacement
      });
      addedSideNames.add(normalized);
    });

    this.availableSidesMap.set(productId, sides);
    // Per-side PrintPlacement summary (verify each side has its OWN distinct zone from DB)
    console.table(sides.map(s => {
      let zone = 'DEFAULT (none in DB)';
      if (s.printPlacement) {
        try {
          const pp = typeof s.printPlacement === 'string' ? JSON.parse(s.printPlacement) : s.printPlacement;
          zone = `${pp.Width}x${pp.Height} @ (${pp.X},${pp.Y})`;
        } catch { zone = 'INVALID JSON'; }
      }
      return { side: s.name, templateId: s.templateId, zone };
    }));
  }

  /**
   * Get available sides for a specific product by its productId
   */
  getProductAvailableSides(productId: string): { name: string, displayName: string, templateId: number, printPlacement: any }[] {
    return this.availableSidesMap.get(String(productId)) || [];
  }

  /**
   * Normalize any side/page name variant to a canonical token so DesignHuddle page names
   * (e.g. "sleeve_left") line up with the built side names (e.g. "left_sleeve").
   * Returns: front | back | sleeve_left | sleeve_right | (collapsed lowercase for anything else).
   *
   * Delegates to the pure, unit-tested `canonicalSide` util (WI 1819 fix — see its doc comment
   * for why a bare "contains left/right" match was wrong and what it broke). Kept as a thin
   * instance method rather than inlined so the ~30 existing `this.canonicalSide(...)` call sites
   * throughout this component don't need to change.
   */
  canonicalSide(name: string): string {
    return canonicalSideUtil(name);
  }

  /**
   * Resolve the built side info (incl. that side's own PrintPlacement) for a product,
   * matching side names canonically. Returns undefined when the product genuinely has no
   * matching side — callers must NOT fall back to another side's zone (that was the bug that
   * made back/sleeve previews use front's print placement).
   */
  getSideInfoForSide(productId: string, sideName: string): { name: string, displayName: string, templateId: number, printPlacement: any } | undefined {
    const sides = this.getProductAvailableSides(productId);
    const norm = (sideName || '').toLowerCase().replace(/\s+/g, '_');
    const target = this.canonicalSide(sideName);
    return sides.find(s => s.name === norm)
      || sides.find(s => this.canonicalSide(s.name) === target);
  }

  /**
   * Get the initial side name for a product (each product may have a different initial side)
   */
  getProductCurrentSide(productId: string): string {
    return this.currentSideNameMap.get(String(productId)) || 'front';
  }

  /**
   * Get or set initial DesignId for a product (starts at true)
   */
  getDesignId(productId: string): boolean {
    const pid = String(productId);
    if (!this.designIdMap.has(pid)) {
      this.designIdMap.set(pid, true);
    }
    return this.designIdMap.get(pid)!;
  }

  /**
   * Generate a random project title for new project creation
   */
  generateRandomProjectTitle(): string {
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `Project_${randomNum}`;
  }

  /**
   * Mark a variant as migrated after design update in editor.
   * If the variant is a customizedProduct, propagate its design to all customProductImage variants.
   */
  markVariantMigrated(product: ProductColorVariant, variant: ColorVariant, sideName: string, logoUrl: string | null, parsedData: any): void {
    variant.isMigrated = true;

    // If the customizedProduct variant was updated, propagate design to all customProductImage replicas
    // Same flow as adding a new color: generate preview, update logo, export for each side
    if (variant.variantSource === 'customizedProduct') {
      const replicaVariants = product.selectedColors.filter(
        v => v.variantSource === 'customProductImage' && !v.isMigrated
      );

      if (replicaVariants.length === 0) return;

      // Get the updated variant's project and side info
      const variantKey = `${product.productId}_${variant.styleId}`;
      const currentSide = this.getProductCurrentSide(product.productId);
      const activeSides = this.variantActiveSides.get(variantKey) || [currentSide];

      replicaVariants.forEach(replica => {
        const replicaKey = `${product.productId}_${replica.styleId}`;

        // Copy sideLogos from the updated variant under all name variations
        if (logoUrl) {
          if (!replica.sideLogos) replica.sideLogos = {};
          const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
          const displayName = this.getDisplaySideName(sideNorm);
          replica.sideLogos[sideName] = logoUrl;
          replica.sideLogos[sideNorm] = logoUrl;
          replica.sideLogos[displayName] = logoUrl;
        }

        // Copy placements from updated variant's sides
        if (parsedData && variant.sides) {
          const srcSide = this.matchSideByName(variant.sides, sideName);
          if (srcSide && replica.sides) {
            const destSide = this.matchSideByName(replica.sides, sideName);
            if (destSide) {
              destSide.elementPlacement = srcSide.elementPlacement;
              destSide.zonePlacement = srcSide.zonePlacement;
            }
          }
        }

        // Copy active sides from the updated variant
        this.variantActiveSides.set(replicaKey, [...activeSides]);

        // Set up export tracking for replica
        this.pendingExports.set(replicaKey, { pending: activeSides.length, completed: 0, sideJobIds: new Map() });

        // For each active side: reuse updated variant's project, generate preview + export
        activeSides.forEach(activeSideName => {
          const sideInfo = this.getSideInfoForSide(product.productId, activeSideName);
          if (!sideInfo) {
            this.markExportCompleted(replicaKey, activeSideName, '');
            return;
          }

          // Reuse the updated variant's projectId for this side
          const srcProjectId = this.variantSideProjects.get(`${variantKey}_${activeSideName}`);
          if (srcProjectId) {
            this.variantSideProjects.set(`${replicaKey}_${activeSideName}`, srcProjectId);
            // Generate preview with this replica's product color
            this.generatePreviewForCurrentSide(product, replica, activeSideName);
            // Export the side
            this.exportCurrentSide(product, replica, activeSideName);
          } else {
            // No project from updated variant — create new one (seeded top-anchored via helper)
            const replicaLogoUrl = this.getSideLogoUrl(replica, activeSideName) || logoUrl || '';
            const mediaPosition = parsedData ? this.calculateMediaPosition(parsedData.elementPlacement, parsedData.zonePlacement) : null;
            const projectTitle = this.generateRandomProjectTitle();
            this.buildSeedCustomization(replicaLogoUrl, product.productId, activeSideName, mediaPosition).then(customizationObject =>
              this.createProjectForSide(sideInfo.templateId, projectTitle, customizationObject)
                .then(newProjectId => {
                  if (newProjectId) {
                    this.variantSideProjects.set(`${replicaKey}_${activeSideName}`, newProjectId);
                    this.fetchAndStoreMetadata(newProjectId, replica, activeSideName);
                    this.generatePreviewForCurrentSide(product, replica, activeSideName);
                    this.exportCurrentSide(product, replica, activeSideName);
                  } else {
                    this.markExportCompleted(replicaKey, activeSideName, '');
                  }
                })
                .catch(err => {
                  console.error(`❌ Error creating project for replica ${replica.colorName} - ${activeSideName}:`, err);
                  this.markExportCompleted(replicaKey, activeSideName, '');
                })
            );
          }
        });

        replica.isMigrated = true;
      });
    }
  }

  /**
   * Propagate a logo change from customizedProduct variant to all customProductImage replicas.
   * Generates preview, updates logo, and hits export-job for each replica — same as new color flow.
   */
  propagateLogoToReplicas(product: ProductColorVariant, sourceVariant: ColorVariant, sideNorm: string, logoUrl: string, sourceProjectId: string, sideInfo: any): void {
    const replicas = product.selectedColors.filter(v => v.variantSource === 'customProductImage');
    if (replicas.length === 0) return;


    replicas.forEach(replica => {
      const replicaKey = `${product.productId}_${replica.styleId}`;

      // Update replica's sideLogos under all name variations
      if (!replica.sideLogos) replica.sideLogos = {};
      const displayName = this.getDisplaySideName(sideNorm);
      replica.sideLogos[sideNorm] = logoUrl;
      replica.sideLogos[displayName] = logoUrl;

      // Increment DesignId offset for replica
      this.incrementDesignIdOffset(product.productId, replica.styleId);

      // Set up export tracking
      const existingExport = this.pendingExports.get(replicaKey);
      if (existingExport) {
        existingExport.pending++;
      } else {
        this.pendingExports.set(replicaKey, { pending: 1, completed: 0, sideJobIds: new Map() });
      }

      // Reuse source variant's project for this side
      this.variantSideProjects.set(`${replicaKey}_${sideNorm}`, sourceProjectId);
      this.invalidatePrintExport(product, replica, sideNorm);

      // Clear old cached preview. NOTE: this call site goes through generatePreviewForCurrentSide
      // -> generateSidePreview, whose own guard (`if backgroundUrls.has(cacheKey) return`) would
      // silently skip the regeneration entirely if the old value were left in place — unlike the
      // renderSharedProjectPreview-based call sites (no such guard), this one genuinely needs the
      // delete to make the regeneration actually run.
      const cacheKey = `${product.productId}_${replica.styleId}_${displayName}`;
      this.backgroundUrls.delete(cacheKey);
      this.generatingPreviews.delete(cacheKey);

      // Generate preview with replica's product color + export
      this.generatePreviewForCurrentSide(product, replica, sideNorm);
      this.exportCurrentSide(product, replica, sideNorm);

    });
  }

  /**
   * Check if all InkSoft variants that need migration have been updated.
   * customProductImage variants are auto-migrated when their parent customizedProduct is updated.
   */
  isInksoftMigrationComplete(): boolean {
    if (!this.isInksoftProduct) return true;
    for (const product of this.selectedProducts) {
      for (const variant of product.selectedColors) {
        // Only customizedProduct and alternateDesign need manual update
        if (variant.variantSource === 'customizedProduct' || variant.variantSource === 'alternateDesign') {
          if (!variant.isMigrated) return false;
        }
      }
    }
    return true;
  }

  /**
   * Increment DesignId offset for a variant (called when logo/template changes)
   */
  incrementDesignIdOffset(productId: string, styleId: number): void {
    const key = `${productId}_${styleId}`;
    const current = this.variantDesignIdOffset.get(key) || 0;
    this.variantDesignIdOffset.set(key, current + 1);
  }

  /**
   * Get the DesignId for a variant: true if no change from locked, false if logo/template changed
   */
  getVariantDesignId(productId: string, styleId: number): boolean {
    const key = `${productId}_${styleId}`;
    const offset = this.variantDesignIdOffset.get(key) || 0;
    // If offset > 0, logo/template was changed → false; otherwise → true
    return offset === 0;
  }

  /**
   * Initialize single-side flow: fetch page_id, generate preview, export for current side only.
   * In product request mode, activates all sides that have artwork from StyleSideArtworks
   * and creates projects/previews/exports for each.
   */
  initializeSingleSideFlow(): void {
    // WI-1521-followup (Issue 2): no full-screen blocking loader on initial page arrival anymore —
    // it used to freeze the whole page ("Preparing color variants…") for as long as the locked
    // color's initial-side preview+export chain took, exactly the same complaint WI-1521 fix #1
    // already fixed for the "add one new color" click. Per-side/per-color in-progress indicators
    // (isPreviewLoading()/isColorPreparing()/getVariantSideImages()'s isLoading field — all already
    // exist from WI-1521 fix #1) give feedback instead while the user can start working immediately.
    // NOTE: this function's OTHER job — silently auto-saving once the locked color's initial exports
    // finish, without navigating away (waitingForExportsToSave/shouldNavigateAfterSave below,
    // resolved via checkAllExportsComplete()) — is completely unaffected; only the loader.show() call
    // itself is removed. The bail-out loader.hide() a few lines down (no variants to process) and
    // checkAllExportsComplete()'s own loader.hide() are both harmless no-ops now that show() is never
    // called for this path — same reasoning already established for toggleColorSelection's single
    // color add in WI-1521 fix #1.

    // Prepare products data for saving
    this.productsToSave = this.rebuildProductsFromSelectedProducts();

    // Reset tracking
    this.pendingExports.clear();
    this.waitingForExportsToSave = true;
    this.shouldNavigateAfterSave = false;

    // Product request mode: build per-variant side set from StyleSideArtworks
    const prMode = this.stateService.getProductRequestMode();
    const isProductRequestMode = prMode?.isProductRequestMode;
    const prVariantSides = new Map<string, Set<string>>();
    if (isProductRequestMode) {
      const styleSideArtworks: any[] = prMode.styleSideArtworks || [];
      const requestedStyles: any[] = prMode.requestedStyles || [];
      const dbStyleToUnique = new Map<number, string>();
      for (const rs of requestedStyles) {
        if (rs.StyleId && rs.ID) {
          dbStyleToUnique.set(Number(rs.StyleId), String(rs.ID));
        }
      }
      for (const ssa of styleSideArtworks) {
        const dbStyleId = Number(ssa.StyleId || 0);
        const uniqueStyleId = dbStyleToUnique.get(dbStyleId) || String(dbStyleId);
        const sideName = (ssa.SideName || 'front').toLowerCase().replace(/\s+/g, '_');
        if (!ssa.ArtworkImageUrl) continue;
        if (!prVariantSides.has(uniqueStyleId)) {
          prVariantSides.set(uniqueStyleId, new Set());
        }
        prVariantSides.get(uniqueStyleId)!.add(sideName);
      }
    }

    // Set up tracking entries and collect variants to process
    let hasVariants = false;
    const variantsToProcess: { product: ProductColorVariant, variant: ColorVariant, initialSide: string }[] = [];

    this.selectedProducts.forEach(product => {
      const initialSide = this.getProductCurrentSide(product.productId);
      // Generate DesignId for this product
      this.getDesignId(product.productId);
      product.selectedColors.forEach(variant => {
        const variantKey = `${product.productId}_${variant.styleId}`;

        // Seed from any sides already tracked for this variant. On the back-from-pricing
        // round-trip, restoreFromSavedData has rebuilt variantActiveSides from variant.sides,
        // so this preserves secondary imprints (e.g. Back) that would otherwise be reset to the
        // initial side and lost. Falls back to the initial side on a fresh load. (initialSide is
        // kept first: its project is reused below and the product image comes from the front.)
        const restoredActiveSides = this.variantActiveSides.get(variantKey);
        let activeSides = (restoredActiveSides && restoredActiveSides.length > 0)
          ? [initialSide, ...restoredActiveSides.filter(s => s !== initialSide)]
          : [initialSide];
        // In product request mode, determine active sides from artwork data
        if (isProductRequestMode) {
          const artworkSides = prVariantSides.get(String(variant.styleId));
          if (artworkSides && artworkSides.size > 0) {
            const productSides = this.getProductAvailableSides(product.productId);
            const availableSideNames = new Set(productSides.map(s => s.name));
            const filteredSides = Array.from(artworkSides).filter(s => availableSideNames.has(s));
            if (filteredSides.length > 0) {
              // Ensure initialSide is first (project was created for it)
              if (!filteredSides.includes(initialSide)) {
                filteredSides.unshift(initialSide);
              } else {
                // Move initialSide to front
                const idx = filteredSides.indexOf(initialSide);
                if (idx > 0) {
                  filteredSides.splice(idx, 1);
                  filteredSides.unshift(initialSide);
                }
              }
              activeSides = filteredSides;
            }
          }
        }

        this.variantActiveSides.set(variantKey, activeSides);
        hasVariants = true;
        this.pendingExports.set(variantKey, { pending: activeSides.length, completed: 0, sideJobIds: new Map() });
        variantsToProcess.push({ product, variant, initialSide });
      });
    });

    if (!hasVariants) {
      this.waitingForExportsToSave = false;
      this.loader.hide();
      return;
    }

    // For the initial side, reuse the projectId already created on master-product-listing page.
    // Only create new projects when templateId changes (different side).
    variantsToProcess.forEach(({ product, variant, initialSide }) => {
      const variantKey = `${product.productId}_${variant.styleId}`;

      // In edit mode: register all existing side projects and use saved thumbnails
      if (this.isEditMode && variant.sides && variant.sides.length > 0) {
        let allSidesHaveData = true;
        const activeSides: string[] = [];
        const isLockedVariant = Number(variant.styleId) === Number(product.lockedStyleId);

        variant.sides.forEach((side: any) => {
          const sideNameNorm = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
          if (!sideNameNorm) return;

          // For non-locked variants sourced from AlternateDesigns, include all sides even when
          // a thumbnail is absent — the DB entry proves the side was published for this variant
          // and the thumbnail will be regenerated via export. Only skip thumbnail-less sides for
          // replica/customProductImage variants (no AlternateDesigns) where the side is inferred.
          const hasThumbnail = side.ThumbnailUrl || side.ThumbnailRelativeUrl;
          const isAlternateDesignSource = variant.variantSource === 'alternateDesign';
          if (!isLockedVariant && !hasThumbnail && !isAlternateDesignSource) return;
          activeSides.push(sideNameNorm);

          // Register project for this side
          if (side.ProjectId) {
            this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, side.ProjectId);
          }

          // Use existing thumbnail from DB as preview (PngRelativeUrlWithProduct from CustomProductImage)
          const thumbnailUrl = side.ThumbnailUrl || side.ThumbnailRelativeUrl || '';
          if (thumbnailUrl) {
            const sideConfig = this.getSideConfigForCurrentSide(sideNameNorm);
            if (sideConfig) {
              const cacheKey = `${product.productId}_${variant.styleId}_${sideConfig.displayName}`;
              // Handle both full URLs and relative paths
              const fullUrl = thumbnailUrl.startsWith('http') ? thumbnailUrl : this.joinUrl(environment.imgBaseURL, thumbnailUrl);
              this.backgroundUrls.set(cacheKey, fullUrl);
            }
          }

          // Mark export as completed if DownloadUrl exists
          if (side.DownloadUrl && side.JobId) {
            const existingExport = this.pendingExports.get(variantKey);
            if (existingExport) {
              existingExport.sideJobIds.set(sideNameNorm, side.JobId);
              if (!existingExport.sideDownloadUrls) {
                existingExport.sideDownloadUrls = new Map();
              }
              existingExport.sideDownloadUrls.set(sideNameNorm, side.DownloadUrl);
            }
          } else {
            allSidesHaveData = false;
          }
        });

        // Set all sides as active for this variant and correct the pending export count.
        // The pendingExports entry was initialized with activeSides.length = 1 (initialSide only)
        // at the top of initializeSingleSideFlow before edit-mode side data was available.
        // Now that we know the true number of active sides (which may be > 1 for variants with
        // multiple AlternateDesigns), update the pending count so markExportCompleted tracks correctly.
        if (activeSides.length > 0) {
          this.variantActiveSides.set(variantKey, activeSides);
          const exportEntry = this.pendingExports.get(variantKey);
          if (exportEntry && exportEntry.pending !== activeSides.length) {
            exportEntry.pending = activeSides.length;
          }
        }

        // If all sides already have exports, mark each active side as completed so the
        // pending counter reaches the correct total (activeSides.length, not just 1).
        // Use setTimeout so the loader.show() call above gets at least one rendering frame
        // before the synchronous markExportCompleted chain fires hide() — without this delay
        // both show() and hide() emit on the Subject in the same JS tick and Angular never
        // paints the loader overlay.
        if (allSidesHaveData) {
          const completedSides = activeSides.length > 0 ? activeSides : [initialSide];
          const capturedVariantKey = variantKey;
          setTimeout(() => {
            completedSides.forEach(sideName => {
              const existingExport = this.pendingExports.get(capturedVariantKey);
              const jobId = existingExport?.sideJobIds?.get(sideName) || '';
              this.markExportCompleted(capturedVariantKey, sideName, jobId);
            });
          }, 0);
          // Legacy single-call path kept as a no-op placeholder (the forEach above handles it)
          return; // skip the else branch below
        } else {
          // Some sides are missing exports. For AlternateDesign variants, loop over every
          // active side and trigger export individually using that side's own saved ProjectId.
          // For single-project variants (no alternate design), fall back to initialSide only.
          const sidesNeedingExport = activeSides.length > 0 ? activeSides : [initialSide];
          let anySideHandled = false;

          sidesNeedingExport.forEach(sideName => {
            // Check if this specific side already has its export data (DownloadUrl + JobId).
            // The sideJobIds map was populated earlier in the variant.sides loop above.
            const existingExport = this.pendingExports.get(variantKey);
            const savedJobId = existingExport?.sideJobIds?.get(sideName);
            const savedDownloadUrl = existingExport?.sideDownloadUrls?.get(sideName);
            const alreadyHasExport = !!savedJobId && !!savedDownloadUrl;
            if (alreadyHasExport) {
              // Count this side as complete so the pending gate advances correctly
              this.markExportCompleted(variantKey, sideName, savedJobId!);
              anySideHandled = true;
              return;
            }

            // Find the saved ProjectId for this specific side
            const sideProjectId = this.variantSideProjects.get(`${variantKey}_${sideName}`)
              || (sideName === initialSide ? variant.projectId : null);

            if (sideProjectId) {
              this.variantSideProjects.set(`${variantKey}_${sideName}`, sideProjectId);
              this.generatePreviewForCurrentSide(product, variant, sideName);
              this.exportCurrentSide(product, variant, sideName);
              anySideHandled = true;
            } else {
              // No project for this side — mark as completed to avoid blocking the gate
              this.markExportCompleted(variantKey, sideName, '');
              anySideHandled = true;
            }
          });

          if (!anySideHandled) {
            // Fallback: if somehow no side was processed, unblock the gate
            this.markExportCompleted(variantKey, initialSide, '');
          }
        }
      } else if (variant.projectId) {
        // Create flow: reuse existing project from master-product-listing for the initial side
        this.variantSideProjects.set(`${variantKey}_${initialSide}`, variant.projectId);
        this.generatePreviewForCurrentSide(product, variant, initialSide);
        this.exportCurrentSide(product, variant, initialSide);

        // Product request mode: create projects for additional sides (beyond initialSide)
        if (isProductRequestMode) {
          const activeSides = this.variantActiveSides.get(variantKey) || [initialSide];
          const additionalSides = activeSides.filter(s => s !== initialSide);
          if (additionalSides.length > 0) {
          }
          additionalSides.forEach(sideName => {
            const sideInfo = this.getSideInfoForSide(product.productId, sideName);
            if (!sideInfo || !sideInfo.templateId || isNaN(sideInfo.templateId)) {
              this.markExportCompleted(variantKey, sideName, '');
              return;
            }

            // Ensure variant.sides includes this side for the payload
            if (variant.sides) {
              const alreadyInSides = variant.sides.some((s: any) => {
                const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
                return sName === sideName;
              });
              if (!alreadyInSides) {
                variant.sides.push({
                  SideName: sideName,
                  PrintPlacement: sideInfo.printPlacement || '',
                  ThumbnailUrl: null, ThumbnailRelativeUrl: null,
                  Customizations: null, ElementPlacement: null, ZonePlacement: null
                });
              }
            }

            // Get logo for this side (artwork URL set in preSelectProductRequestColors)
            const logoUrl = this.getSideLogoUrl(variant, sideName) || this.designData?.designUrl || '';
            const mediaPosition = this.getMediaPositionForSide(variant, sideName);
            const projectTitle = this.generateRandomProjectTitle();

            this.buildSeedCustomization(logoUrl, product.productId, sideName, mediaPosition).then(customizationObject =>
              this.createProjectForSide(sideInfo.templateId, projectTitle, customizationObject)
                .then(projectId => {
                  if (projectId) {
                    this.variantSideProjects.set(`${variantKey}_${sideName}`, projectId);
                    this.fetchAndStoreMetadata(projectId, variant, sideName);
                    this.generatePreviewForCurrentSide(product, variant, sideName);
                    this.exportCurrentSide(product, variant, sideName);
                  } else {
                    this.markExportCompleted(variantKey, sideName, '');
                  }
                })
                .catch(err => {
                  console.error(`❌ Error creating project for ${variant.colorName} - ${sideName}:`, err);
                  this.markExportCompleted(variantKey, sideName, '');
                })
            );
          });
        } else {
          // Back-from-pricing round-trip (non-edit, non-PR): regenerate preview/export for any
          // additional sides restored beyond the initial side, reusing each side's saved project
          // (restoreFromSavedData repopulated variantSideProjects + backgroundUrls from
          // variant.sides). Without this the extra side is dropped and its pending export hangs.
          const restoredActiveSides = this.variantActiveSides.get(variantKey) || [initialSide];
          const additionalRestoredSides = restoredActiveSides.filter(s => s !== initialSide);
          additionalRestoredSides.forEach(sideName => {
            const sideProjectId = this.variantSideProjects.get(`${variantKey}_${sideName}`);
            if (sideProjectId) {
              this.generatePreviewForCurrentSide(product, variant, sideName);
              this.exportCurrentSide(product, variant, sideName);
            } else {
              this.markExportCompleted(variantKey, sideName, '');
            }
          });
        }
      } else {
        this.markExportCompleted(variantKey, initialSide, '');
      }
    });

    // DIAGNOSTIC: variantActiveSides after initializeSingleSideFlow
    this.variantActiveSides.forEach((sides, key) => {
    });
  }


  /**
   * Get the side config (display name, image field, background key) for the current side
   */
  getSideConfigForCurrentSide(sideName?: string): { displayName: string, imageField: string, backgroundKey: string } | null {
    const sideConfigs: { [key: string]: { displayName: string, imageField: string, backgroundKey: string } } = {
      'front': { displayName: 'Front', imageField: 'ImageFilePath_Front', backgroundKey: 'background_front' },
      'back': { displayName: 'Back', imageField: 'ImageFilePath_Back', backgroundKey: 'background_back' },
      'left_sleeve': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'left sleeve': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'sleeve_left': { displayName: 'Left', imageField: 'ImageFilePath_SleeveLeft', backgroundKey: 'background_sleeve_left' },
      'right_sleeve': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' },
      'right sleeve': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' },
      'sleeve_right': { displayName: 'Right', imageField: 'ImageFilePath_SleeveRight', backgroundKey: 'background_sleeve_right' }
    };

    const targetSide = sideName || 'front';
    const normalized = targetSide.toLowerCase().replace(/\s+/g, '_');
    const nameLower = targetSide.toLowerCase();

    // Direct match
    if (sideConfigs[normalized]) return sideConfigs[normalized];
    if (sideConfigs[nameLower]) return sideConfigs[nameLower];

    // Partial matching
    for (const [key, config] of Object.entries(sideConfigs)) {
      if (normalized.includes(key) || key.includes(normalized)) return config;
    }

    // Canonical fallback. The DB Name form has NO separator ("sleeveright"/"sleeveleft"),
    // which none of the keys above match (they all have a space/underscore), so a sleeve would
    // wrongly fall through to the front default — handing the FRONT image/zone to the editor and
    // export for a sleeve side. canonicalSide normalizes every variant ("sleeveright",
    // "Right Sleeve", "sleeve_right" → "sleeve_right") to a key that exists here.
    const canonical = this.canonicalSide(targetSide);
    if (sideConfigs[canonical]) return sideConfigs[canonical];

    return sideConfigs['front']; // Default to front
  }

  /**
   * Generate preview for only the current side of a variant
   */
  generatePreviewForCurrentSide(product: ProductColorVariant, variant: ColorVariant, sideName?: string): void {
    const targetSide = sideName || this.getProductCurrentSide(product.productId);
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) return;

    const sideConfig = this.getSideConfigForCurrentSide(targetSide);
    if (!sideConfig) return;

    // Check if style has any image (generateSidePreview has its own fallback chain
    // across ImageFilePath_Front/Back/SleeveLeft/SleeveRight and product.productImage)
    if (!variantStyle[sideConfig.imageField]
        && !variantStyle.ImageFilePath_Front
        && !variantStyle.ImageFilePath_Back
        && !variantStyle.ImageFilePath_SleeveLeft
        && !variantStyle.ImageFilePath_SleeveRight) return;

    // Get the template ID for the target side — prefer variant's per-side saved TemplateId in edit mode
    let templateId: number | undefined;
    if (this.isEditMode && variant.sides) {
      const variantSide = variant.sides.find((s: any) => {
        const sn = (s.SideName || '').toLowerCase().replace(/\s+/g, '_');
        return sn === targetSide || sn.includes(targetSide) || targetSide.includes(sn);
      });
      if (variantSide?.TemplateId) {
        templateId = parseInt(variantSide.TemplateId);
      }
    }
    if (!templateId) {
      // getProductAvailableSides() names are always the canonicalized left_sleeve/right_sleeve
      // form, while targetSide can arrive as the raw, unnormalized spelling (e.g. "sleeveleft",
      // from getProductCurrentSide() once the default side falls back to a sleeve instead of
      // "front" — "front" has no raw-vs-canonical ambiguity, which is why this only broke once a
      // product's Front side was deactivated). A strict equality match silently found nothing,
      // leaving templateId undefined and exiting below with zero signal (no preview, no loading
      // state, no error) — getSideInfoForSide() already does the canonical-aware lookup.
      templateId = this.getSideInfoForSide(product.productId, targetSide)?.templateId;
    }
    if (!templateId) return;

    const side = {
      SideName: targetSide,
      DisplayName: sideConfig.displayName,
      BackgroundKey: sideConfig.backgroundKey,
      ImageField: sideConfig.imageField,
      TemplateId: templateId,
      PageNumber: 1
    };


    // When the current projectId has text elements, the SDK cannot render them — route to
    // the export-API path so the preview is composited server-side (same approach as
    // refreshPreviewAfterEditorClose uses for the editor-close case).
    if (this.currentProjectHasTextElements) {
      const sideNorm = targetSide.toLowerCase().replace(/\s+/g, '_');
      const variantKey = `${product.productId}_${variant.styleId}`;
      const projectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`) || variant.projectId || '';
      const displayName = sideConfig.displayName;
      const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;

      // Skip if already cached or in-flight
      if (this.backgroundUrls.has(cacheKey) || this.generatingPreviews.has(cacheKey)) {
        return;
      }

      // Resolve product image URL for this variant's side
      let productImageUrl: string = variantStyle[sideConfig.imageField] || '';
      if (!productImageUrl) {
        productImageUrl = variantStyle.ImageFilePath_Front
          || variantStyle.ImageFilePath_Back
          || variantStyle.ImageFilePath_SleeveLeft
          || variantStyle.ImageFilePath_SleeveRight
          || (product as any).productImage || '';
      }
      if (productImageUrl && !productImageUrl.startsWith('http')) {
        productImageUrl = this.joinUrl(this.imgBaseURL, productImageUrl);
      }

      if (!productImageUrl || !/^https?:\/\//i.test(productImageUrl)) {
        this.backgroundUrls.set(cacheKey, '');
        this.checkAndTriggerSave();
        return;
      }

      // Resolve zone dimensions and offset from THIS side's PrintPlacement (canonical match)
      let zoneDimensions = '300x300';
      let zoneOffset = '100x100';
      const sideInfo = this.getSideInfoForSide(product.productId, sideNorm);
      if (sideInfo?.printPlacement) {
        try {
          const pp = typeof sideInfo.printPlacement === 'string'
            ? JSON.parse(sideInfo.printPlacement) : sideInfo.printPlacement;
          if (pp.Width && pp.Height) zoneDimensions = `${pp.Width}x${pp.Height}`;
          if (pp.X !== undefined && pp.Y !== undefined) zoneOffset = `${pp.X}x${pp.Y}`;
        } catch (e) {
        }
      }

      // Gate: mark generating + pendingUploads before delegating (refreshPreviewViaExport
      // decrements pendingUploads and clears generatingPreviews on completion).
      this.generatingPreviews.add(cacheKey);
      this.pendingUploads++;

      this.refreshPreviewViaExport(projectId, productImageUrl, zoneDimensions, zoneOffset, cacheKey, product, variant, displayName, sideNorm);
      return;
    }

    this.generateSidePreview(product, variant, side, variantStyle);
  }

  /**
   * Export only the current side for a variant
   */
  exportCurrentSide(product: ProductColorVariant, variant: ColorVariant, sideName?: string): void {
    const targetSide = sideName || this.getProductCurrentSide(product.productId);
    const variantKey = `${product.productId}_${variant.styleId}`;
    const projectKey = `${variantKey}_${targetSide}`;
    // The edit generation this export belongs to (see printGens) — checked again when it completes.
    const exportGen = this.printGens.begin(this.printGenKey(variantKey, targetSide));

    // Use per-side project if available, fallback to variant's main projectId
    const projectId = this.variantSideProjects.get(projectKey) || variant.projectId;

    if (!projectId) {
      this.markExportCompleted(variantKey, targetSide, '');
      return;
    }

    const sideConfig = this.getSideConfigForCurrentSide(targetSide);
    if (!sideConfig) {
      this.markExportCompleted(variantKey, targetSide, '');
      return;
    }

    const fileName = `${projectId}_${targetSide.toLowerCase().replace(/\s+/g, '_')}`;
    const apiUrl = `${environment.baseUrl}/api/designhuddle/projects/${projectId}/export-job`;

    // Frame/asset aspect guard: a logo element whose frame no longer matches the artwork's aspect
    // (media swapped in the editor without the frame being re-fitted) exports STRETCHED. Never
    // export that — flag the side so the designer re-adds the logo (see frame-fit.ts).
    this.isSideFrameDistorted(product, variant, targetSide).then(distorted => {
    if (distorted) {
      this.flagPrintExportDistorted(product, variant, targetSide);
      this.markExportCompleted(variantKey, targetSide, '');
      return;
    }

    // Resolve the side's physical zone size (inches) BEFORE building the export body,
    // awaiting a DH /data fetch only when inches aren't already stored (the fresh-create
    // path). width = round(zoneInches × 300) → DH renders at 300 DPI at true print size.
    // (dpi/customizations were dropped: ExportProjectRequest binds neither — they were dead.)
    this.resolveZoneInchesForSide(projectId, variant, targetSide).subscribe((zoneInches) => {
      const widthPx = this.computeExportWidthPx(zoneInches);
      if (widthPx == null) {
      }

      const body: any = {
        format: 'png',
        fileName: fileName,
      };
      if (widthPx != null) body.width = widthPx;


      this.http.post<any>(apiUrl, body).pipe(
        catchError(error => {
          console.error(`❌ Export error for ${variant.colorName} - ${targetSide}:`, error);
          return of(null);
        })
      ).subscribe({
        next: (response: any) => {
          let jobId = '';
          if (response) {
            jobId = response?.data?.job_id || '';
            if (jobId) {
              this.notification.success(`Export started for ${variant.colorName} - ${sideConfig.displayName}`, 'Success');
              // Start polling for download URL — polling will call markExportCompleted when done
              this.pollExportJobProgress(projectId, jobId, variantKey, targetSide, exportGen);
              return; // Don't mark as completed yet — wait for polling to finish
            }
          }
          // No jobId obtained — mark as completed immediately
          this.markExportCompleted(variantKey, targetSide, '');
        }
      });
    });
    }); // end isSideFrameDistorted
  }

  /**
   * Match a side by name in a sides array (helper)
   */
  private matchSideByName(sides: any[], sideName: string): any {
    const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
    return sides.find((side: any) => {
      const sName = (side.SideName || side.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
    });
  }

  /**
   * Does a master-product Side.Name refer to the same physical side as an active technical token?
   * The active token is always clean (front/back/left_sleeve/right_sleeve); the raw Side.Name can
   * be any DB spelling — separator-less "sleeveright", reversed "Right Sleeve", "Shoulder Right".
   * First a plain substring check (covers the clean cases), then canonicalSide() for separator /
   * word-order differences. THE CATCH: for the left/right sleeve buckets a bare "contains left/
   * right" is not enough — "Left Chest"/"Right Chest"/"Right Front" would collapse into a sleeve —
   * so a canonical sleeve match ALSO requires the name to be genuinely sleeve-like (contains
   * "sleeve" or "shoulder"). front/back have no such ambiguity.
   */
  private sideNameMatchesActiveToken(rawSideName: string, activeToken: string): boolean {
    const sName = (rawSideName || '').toLowerCase().replace(/\s+/g, '_');
    const active = (activeToken || '').toLowerCase().replace(/\s+/g, '_');
    if (!sName || !active) return false;
    if (sName === active || sName.includes(active) || active.includes(sName)) return true;
    const sCanon = this.canonicalSide(sName);
    if (this.canonicalSide(active) !== sCanon) return false;
    if (sCanon === 'sleeve_left' || sCanon === 'sleeve_right') {
      const collapsed = sName.replace(/[_-]+/g, '');
      return collapsed.includes('sleeve') || collapsed.includes('shoulder');
    }
    return true; // front / back are unambiguous
  }

  /**
   * Handle side change from side selector buttons.
   * Creates a brand new project for the new side's template for all variants.
   */
  onSideChange(newSideName: string): void {
    if (this.isSwitchingSide) return;

    this.isSwitchingSide = true;
    this.loader.show('Switching side…', 'Generating product previews. This may take a moment.');

    // Clear old caches
    this.backgroundUrls.clear();
    this.generatingPreviews.clear();

    // Reset export tracking
    this.pendingExports.clear();
    this.waitingForExportsToSave = true;
    this.shouldNavigateAfterSave = false;

    // Set up tracking entries first
    const variantsToProcess: { product: ProductColorVariant, variant: ColorVariant }[] = [];
    this.selectedProducts.forEach(product => {
      // Update the product's current side
      this.currentSideNameMap.set(String(product.productId), newSideName);
      product.selectedColors.forEach(variant => {
        const variantKey = `${product.productId}_${variant.styleId}`;
        this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
        variantsToProcess.push({ product, variant });
      });
    });

    // Create new projects for all variants - each product uses its own side's templateId.
    // Skip project creation for sides that already have a project from the editor, AND (new)
    // reuse a side-level shared project — see findSideProjectOwner / pendingSideProjectCreations —
    // instead of independently creating one per color, which was the reported duplicate-project bug.
    const createPromises = variantsToProcess.map(({ product, variant }) => {
      const variantKey = `${product.productId}_${variant.styleId}`;
      const newSide = this.getSideInfoForSide(product.productId, newSideName);
      if (!newSide) {
        this.markExportCompleted(variantKey, newSideName, '');
        return Promise.resolve();
      }

      // Check if this side already has a project from the editor (this variant's OWN saved side).
      // No separate "register as shared" call needed — findSideProjectOwner scans
      // variantSideProjects directly, so setting it here is already enough for another color to
      // find and reuse this project later.
      const existingSideProjectId = this.getSideProjectIdFromVariantSides(variant, newSideName);
      if (existingSideProjectId) {
        variant.projectId = existingSideProjectId;
        this.variantSideProjects.set(`${variantKey}_${newSideName}`, existingSideProjectId);
        this.generatePreviewForCurrentSide(product, variant, newSideName);
        this.exportCurrentSide(product, variant, newSideName);
        return Promise.resolve();
      }

      const sideKey = `${product.productId}_${newSideName}`;
      const newLogoUrl = variant.sideLogos?.[newSideName] || '';
      const sameLogo = !newLogoUrl; // no custom override = safe to share the generic project

      if (sameLogo) {
        // Reuse a project already known for this side — either from an earlier operation
        // (toggleColorSelection, ensurePendingSideProjectsBeforeSave, a prior onSideChange call,
        // or the editor) or from ANOTHER variant's creation kicked off earlier in this very same
        // .map() call (pendingSideProjectCreations, set synchronously below before any await, so a
        // sibling processed later in this loop always sees it in time).
        const inFlight = this.pendingSideProjectCreations.get(sideKey);
        const already = this.getSharedSideProjectId(product, newSideName);
        if (inFlight || already) {
          const waitFor = inFlight || Promise.resolve(already as string);
          return waitFor.then(sharedProjectId => {
            if (!sharedProjectId) {
              // The owner's creation failed — nothing to borrow; become the new owner instead.
              return this.createAndOwnSideProject(product, variant, newSideName, newSide, sideKey, true);
            }
            const ownerKey = this.findSideProjectOwnerKey(product, newSideName);
            const ownerExportTracking = this.pendingExports.get(ownerKey);
            const ownerJobId = ownerExportTracking?.sideJobIds?.get(newSideName) || '';
            const ownerDownloadUrl = ownerExportTracking?.sideDownloadUrls?.get(newSideName) || '';
            this.generatePreviewForCurrentSide(product, variant, newSideName);
            if (ownerDownloadUrl) {
              const tracking = this.pendingExports.get(variantKey);
              if (tracking) {
                if (!tracking.sideDownloadUrls) {
                  tracking.sideDownloadUrls = new Map<string, string>();
                }
                tracking.sideDownloadUrls.set(newSideName, ownerDownloadUrl);
              }
            }
            this.markExportCompleted(variantKey, newSideName, ownerJobId);
          });
        }
      }

      // No shared/in-flight project to borrow (or this variant has its own custom logo for this
      // side and can't share regardless) — create independently. Only register this creation as
      // the side-level shared owner when it represents the generic/default design (sameLogo) —
      // never hand out a project seeded from one color's own custom logo to another color.
      return this.createAndOwnSideProject(product, variant, newSideName, newSide, sideKey, sameLogo);
    });

    Promise.all(createPromises).then(() => {
      this.isSwitchingSide = false;
      this.productsToSave = this.rebuildProductsFromSelectedProducts();
    }).catch(err => {
      console.error('❌ Error during side switch:', err);
      this.isSwitchingSide = false;
      this.loader.hide();
    });
  }

  /**
   * Create a brand-new DH project for (product, side) for `variant`, and wire it up exactly like
   * the pre-existing onSideChange create path did (variant.projectId, metadata fetch, preview,
   * export) — extracted so it can be shared between the "nobody has this side yet" path and the
   * "the shared/in-flight owner's creation failed, fall back to becoming the new owner" path.
   *
   * `registerAsShared` is false when `variant` has its own custom logo override for this side (see
   * sameLogo in onSideChange): such a project represents ONLY that color's design and must never be
   * tracked in `pendingSideProjectCreations` — a sibling color processed in the same tick must NOT
   * borrow an in-flight custom-logo project meant for just one color. (No separate "register as
   * shared" step is needed once the creation resolves — the `variantSideProjects.set(...)` a few
   * lines below is already enough for findSideProjectOwner's scan to discover it later.)
   */
  private createAndOwnSideProject(
    product: ProductColorVariant,
    variant: ColorVariant,
    newSideName: string,
    newSide: { templateId: number },
    sideKey: string,
    registerAsShared: boolean
  ): Promise<void> {
    const variantKey = `${product.productId}_${variant.styleId}`;
    const designUrl = this.designData?.designUrl || this.designData?.previewUrl || '';
    const mediaPosition = this.getMediaPositionForSide(variant, newSideName);
    const projectTitle = this.generateRandomProjectTitle();

    const creation: Promise<string> = this.buildSeedCustomization(designUrl, product.productId, newSideName, mediaPosition)
      .then(customizationObject => this.createProjectForSide(newSide.templateId, projectTitle, customizationObject));

    if (registerAsShared) {
      // Set synchronously (before any await settles) so a sibling variant processed later in the
      // SAME onSideChange .map() call sees this in-flight creation and borrows it instead of
      // independently racing to create its own — see pendingSideProjectCreations doc comment.
      this.pendingSideProjectCreations.set(sideKey, creation);
    }

    return creation.then(projectId => {
      if (registerAsShared) this.pendingSideProjectCreations.delete(sideKey);
      if (projectId) {
        variant.projectId = projectId;
        this.variantSideProjects.set(`${variantKey}_${newSideName}`, projectId);
        // Hit metadata API to get elementPlacement/zonePlacement
        this.fetchAndStoreMetadata(projectId, variant, newSideName);
        this.generatePreviewForCurrentSide(product, variant, newSideName);
        this.exportCurrentSide(product, variant, newSideName);
      } else {
        this.markExportCompleted(variantKey, newSideName, '');
      }
    }).catch(err => {
      if (registerAsShared) this.pendingSideProjectCreations.delete(sideKey);
      console.error(`❌ [onSideChange] Error creating project for side ${newSideName}:`, err);
      this.markExportCompleted(variantKey, newSideName, '');
    });
  }

  /**
   * Check if a side already has a project from the editor (via variant.sides data)
   */
  getSideProjectIdFromVariantSides(variant: ColorVariant, sideName: string): string | null {
    if (!variant.sides || variant.sides.length === 0) return null;
    const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
    const side = variant.sides.find((s: any) => {
      const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
    });
    return side?.ProjectId || null;
  }

  /**
   * Side-level project reuse — the single source of truth query: "has ANY already-selected color
   * already got a DH project registered for this side, regardless of who created it or when".
   *
   * Deliberately scans `variantSideProjects` (the map every project-creation/restore path in this
   * file already writes into — page load, edit-mode restore, onSideChange, toggleColorSelection,
   * ensurePendingSideProjectsBeforeSave, the editor) rather than relying on a separately-maintained
   * "shared project" cache. An earlier version of this fix used exactly such a cache
   * (`sharedSideProjects`, populated only by a `registerSharedSideProject()` call added to each NEW
   * reuse-aware creation path) — and it missed `initializeSingleSideFlow`'s create-flow branch
   * (~line 2059: `variantSideProjects.set(initialSide, variant.projectId)` for the locked/default
   * color's own initial-side project at page load), which never got a matching
   * `registerSharedSideProject()` call. Since the locked color's initial side is the single most
   * common side every subsequently-added color needs, that one miss meant the fix never actually
   * engaged for the common case — every added color kept creating its own fresh project for that
   * side, reproducing the exact "still creates a project for every color of the same side" bug.
   * Deriving straight from `variantSideProjects` instead makes this correct regardless of which of
   * the many creation/restore sites populated it, with no separate bookkeeping to keep in sync.
   *
   * Returns the owning color (not just a project id) since callers also need it to borrow that
   * color's pendingExports tracking (jobId/downloadUrl) or saved design (logo/placement).
   *
   * Skips any color with its OWN custom logo override for this side (`sideLogos[sideName]` set) —
   * such a color's project was seeded specifically from ITS logo and must never be handed to a
   * sibling color expecting the generic/default design, even though it's a perfectly real,
   * perfectly reusable project for that one owning color itself. This mirrors the `sameLogo` gate
   * every creation path already applies before treating a freshly-created project as shareable.
   */
  private findSideProjectOwner(product: ProductColorVariant, sideName: string): ColorVariant | null {
    for (const c of product.selectedColors) {
      if (c.sideLogos?.[sideName]) continue;
      if (this.variantSideProjects.get(`${product.productId}_${c.styleId}_${sideName}`)) {
        return c;
      }
    }
    return null;
  }

  /**
   * Returns the project id already created for this (product, side) by ANY already-selected color,
   * or null if no color has one yet. See findSideProjectOwner's doc for why this scans rather than
   * reads a separate cache.
   */
  private getSharedSideProjectId(product: ProductColorVariant, sideName: string): string | null {
    const owner = this.findSideProjectOwner(product, sideName);
    return owner ? this.variantSideProjects.get(`${product.productId}_${owner.styleId}_${sideName}`)! : null;
  }

  /**
   * Resolve which already-selected color "owns" the side-level project for (product, side) — so
   * its pendingExports tracking (jobId/downloadUrl) can be borrowed. Falls back to the locked/
   * default color's key when no owner can be resolved (nobody has a project for this side yet),
   * which reproduces the original pre-fix "only check the locked color" behavior as a safe degrade.
   */
  private findSideProjectOwnerKey(product: ProductColorVariant, sideName: string): string {
    const owner = this.findSideProjectOwner(product, sideName);
    if (owner) return `${product.productId}_${owner.styleId}`;
    return `${product.productId}_${product.lockedStyleId}`;
  }

  /**
   * Calculate media_position from elementPlacement and zonePlacement.
   * scale_width = elementPlacement.width / zonePlacement.width
   * x = (elementPlacement.left + elementPlacement.width / 2 - zonePlacement.left) / zonePlacement.width
   * y = (elementPlacement.top + elementPlacement.height / 2 - zonePlacement.top) / zonePlacement.height
   */
  calculateMediaPosition(elementPlacement: any, zonePlacement: any): any {
    if (!elementPlacement || !zonePlacement) return null;
    if (!zonePlacement.width || !zonePlacement.height) return null;

    // NO REAL PLACEMENT: a freshly-created DH project reports elementPlacement === zonePlacement
    // (the design auto-fits the zone, e.g. {width:2,height:2,left:0,top:0}). That computes to a
    // CENTERED media_position {scale_width:1, x:0.5, y:0.5}, which is NOT a user placement — yet
    // being truthy it was overriding the top-anchor default in BOTH the preview and the export,
    // so every thumbnail rendered centered. Return null here so callers apply the top-anchor
    // default (computeTopAnchor / vertical_align). A genuine editor move makes element != zone.
    if (elementPlacement.left === zonePlacement.left
        && elementPlacement.top === zonePlacement.top
        && elementPlacement.width === zonePlacement.width
        && elementPlacement.height === zonePlacement.height) {
      return null;
    }

    const scale_width = elementPlacement.width / zonePlacement.width;
    const x = (elementPlacement.left + elementPlacement.width / 2 - zonePlacement.left) / zonePlacement.width;
    const y = (elementPlacement.top + elementPlacement.height / 2 - zonePlacement.top) / zonePlacement.height;

    // PRISTINE TOP-ANCHOR (not a user edit): the DH project is now SEEDED top-anchored
    // (buildTopAnchoredLogoCustomization), so an unedited design comes back with its TOP edge on
    // the zone top AND horizontally centered — i.e. the exact seed. Treat only THAT structural
    // signature as "no placement" (callers then re-derive the identical top-anchor via
    // computeTopAnchor). This replaces the old ±0.02 near-CENTER heuristic, which also nulled
    // GENUINE user placements that happened to land near the zone center (the revert-after-edit
    // bug). Epsilon is tight (1e-6): a design the user drags vertically off the top (top edge no
    // longer == zone top) or horizontally off-center is now honored exactly as placed.
    const EPS = 1e-6;
    const centeredX = Math.abs(x - 0.5) < EPS;
    const topAligned = Math.abs(elementPlacement.top - zonePlacement.top) < EPS * (Math.abs(zonePlacement.height) || 1);
    if (centeredX && topAligned) {
      return null;
    }

    return { scale_width, x, y };
  }

  /**
   * Get the REAL user media_position for a side from variant.sides placement data (set in
   * the DH editor). Returns null when there is no saved placement — callers then apply the
   * top-anchor default via computeTopAnchor(), using the exact logoUrl + zone they render
   * with (see note on computeTopAnchor).
   */
  getMediaPositionForSide(variant: ColorVariant, sideName: string): any {
    if (!variant.sides || variant.sides.length === 0) return null;
    const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
    const side = variant.sides.find((s: any) => {
      const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
    });
    // Support both camelCase (from parseProjectMetadata) and PascalCase (from DB payload)
    const rawElement = side?.elementPlacement || side?.ElementPlacement;
    const rawZone = side?.zonePlacement || side?.ZonePlacement;
    if (!rawElement || !rawZone) return null;
    // Normalize before use — see normalizePlacement. A bare JSON.parse here used to throw straight
    // out of generateSidePreview, leaving generatingPreviews / pendingUploads un-decremented and
    // hanging the save gate on a permanent spinner.
    const elementPlacement = this.normalizePlacement(rawElement);
    const zonePlacement = this.normalizePlacement(rawZone);
    if (!elementPlacement || !zonePlacement) return null;
    return this.calculateMediaPosition(elementPlacement, zonePlacement);
  }

  /**
   * Target print resolution (DPI) for DesignHuddle print-file exports.
   * DH exports are pixel-driven: output_px = physical_inches × DPI. Sending
   * width = zoneInches × TARGET_DPI makes DH render the print file at 300 DPI
   * at the template's true physical print size (e.g. 12" → 3600px).
   */
  private readonly TARGET_DPI = 300;

  /**
   * Read a side's PHYSICAL zone size in INCHES from the already-stored placement.
   * Uses the INCH-valued zonePlacement/ZonePlacement (from DH element.zone) — NOT the
   * pixel PrintPlacement. Returns null when not present/parseable so the caller can
   * decide to fetch it or fall back.
   */
  private getZoneInchesForSide(variant: ColorVariant, sideName: string): { width: number; height: number } | null {
    if (!variant?.sides || variant.sides.length === 0) return null;
    const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
    const side = variant.sides.find((s: any) => {
      const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
    });
    // Support camelCase (parseProjectMetadata) and PascalCase (DB payload); value may be a JSON string.
    const rawZone = side?.zonePlacement || side?.ZonePlacement;
    if (!rawZone) return null;
    let zone: any;
    try {
      zone = typeof rawZone === 'string' ? JSON.parse(rawZone) : rawZone;
    } catch {
      return null;
    }
    const w = Number(zone?.width);
    const h = Number(zone?.height);
    if (!isFinite(w) || w <= 0) return null;
    return { width: w, height: (isFinite(h) && h > 0) ? h : w };
  }

  /**
   * Resolve a side's zone inches, awaiting a DH /data fetch ONLY when the inches are
   * not already stored (the fresh-create path — where zonePlacement is null at export
   * time because fetchAndStoreMetadata is fire-and-forget). Always emits (never errors):
   * emits null only when the inches are genuinely unresolvable, so export never breaks.
   */
  private resolveZoneInchesForSide(projectId: string, variant: ColorVariant, sideName: string): Observable<{ width: number; height: number } | null> {
    const stored = this.getZoneInchesForSide(variant, sideName);
    if (stored) return of(stored);
    if (!projectId) return of(null);

    return this.designHuddleService.getProjectData(projectId).pipe(
      map((response: any) => {
        const parsed = this.parseProjectMetadata(response, projectId);
        const zone = parsed?.zonePlacement;
        // Store back so subsequent exports/previews for this side reuse it (mirrors fetchAndStoreMetadata).
        if (zone && variant.sides) {
          const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
          const side = variant.sides.find((s: any) => {
            const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
            return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
          });
          if (side) {
            side.elementPlacement = parsed.elementPlacement;
            side.zonePlacement = zone;
          }
        }
        const w = Number(zone?.width);
        const h = Number(zone?.height);
        if (!isFinite(w) || w <= 0) return null;
        return { width: w, height: (isFinite(h) && h > 0) ? h : w };
      }),
      catchError((err: any) => {
        return of(null);
      })
    );
  }

  /**
   * Compute the DH export pixel width from zone inches × TARGET_DPI.
   * Returns null when inches are unresolvable (caller falls back to prior no-width behavior).
   * Clamped to a sane positive bound so a corrupt zone value can't push an absurd pixel
   * count into DH and, transitively, into the API's image decoder.
   */
  private computeExportWidthPx(zoneInches: { width: number; height: number } | null): number | null {
    if (!zoneInches || !isFinite(zoneInches.width) || zoneInches.width <= 0) return null;
    const px = Math.round(zoneInches.width * this.TARGET_DPI);
    if (!isFinite(px) || px <= 0) return null;
    return Math.min(px, 10000); // hard cap — mirrors the API-side max-dimension guard
  }

  /**
   * Parse/validate a raw placement (DB-sourced JSON string, or an already-parsed object) into a
   * FRESH { width, height, left, top } literal of finite numbers. Returns null on malformed JSON
   * or non-positive/absent dimensions, so callers fall back to the top-anchor default instead of
   * throwing or emitting NaN into a DesignHuddle request.
   *
   * The fresh literal is deliberate: never spread or Object.assign a parsed placement into
   * anything handed to the DH SDK — a __proto__/constructor key in a DB string would ride along.
   */
  private normalizePlacement(raw: any): { width: number; height: number; left: number; top: number } | null {
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        console.error('❌ Malformed placement JSON — ignoring saved placement:', e);
        return null;
      }
    }
    if (!parsed || typeof parsed !== 'object') return null;

    const width = Number(parsed.width);
    const height = Number(parsed.height);
    const left = Number(parsed.left);
    const top = Number(parsed.top);
    if (!Number.isFinite(width) || width <= 0) return null;
    if (!Number.isFinite(height) || height <= 0) return null;

    return {
      width,
      height,
      left: Number.isFinite(left) ? left : 0,
      top: Number.isFinite(top) ? top : 0
    };
  }

  // ---- Top-anchor default placement (used only when there is no user placement) ----

  // Cache of design natural pixel dimensions keyed by URL (image loaded once).
  private designDimsCache = new Map<string, { w: number; h: number }>();

  /**
   * media_position that centers the design horizontally and anchors its TOP edge to
   * the top of the print zone ("chest print"). scale_width = design width / zone width;
   * x,y = the design's center point as fractions of the zone (0-1).
   */
  private topAnchoredMediaPosition(designW: number, designH: number, zoneW: number, zoneH: number): { scale_width: number; x: number; y: number } {
    const designAspect = designH / designW;
    const zoneAspect = zoneH / zoneW;
    let scale_width = 1;
    let heightFrac = designAspect / zoneAspect; // design height as fraction of zone height at full width
    if (heightFrac > 1) {                        // design taller than zone: fit by height instead
      scale_width = 1 / heightFrac;
      heightFrac = 1;
    }
    return { scale_width, x: 0.5, y: heightFrac / 2 }; // y centers the midpoint so top edge = 0
  }

  /**
   * Top-anchor media_position for the EXACT logo + zone a caller is about to render/export.
   * Callers pass the same `logoUrl` they put in the customization and the same
   * `zoneDimensions` ("WxH") they resolved from PrintPlacement — this avoids the URL/zone
   * mismatches that a centralized re-resolution would introduce. Returns null (→ DH default)
   * when the design dimensions aren't cached yet, warming them for the next pass.
   */
  private computeTopAnchor(logoUrl: string, zoneDimensions: string): { scale_width: number; x: number; y: number } | null {
    if (!logoUrl) return null;
    const dims = this.designDimsCache.get(logoUrl);
    if (!dims) {
      this.warmDesignDims(logoUrl); // load once; ready on the next call
      return null;
    }
    const [zoneW, zoneH] = (zoneDimensions || '').split('x').map(Number);
    if (!zoneW || !zoneH) return null;
    return this.topAnchoredMediaPosition(dims.w, dims.h, zoneW, zoneH);
  }

  /**
   * "WxH" print-zone dimensions for a side, resolved from THAT side's PrintPlacement
   * (canonical match). Empty string when the side has no PrintPlacement — never another
   * side's zone. Used to keep the export's top-anchor consistent with the preview.
   */
  private getZoneDimensionsForSide(productId: string, sideName: string): string {
    const info = this.getSideInfoForSide(productId, sideName);
    if (info?.printPlacement) {
      try {
        const pp = typeof info.printPlacement === 'string' ? JSON.parse(info.printPlacement) : info.printPlacement;
        if (pp?.Width && pp?.Height) return `${pp.Width}x${pp.Height}`;
      } catch { /* ignore malformed PrintPlacement */ }
    }
    return '';
  }

  /** Load a design image once and cache its natural pixel dimensions. */
  private warmDesignDims(designUrl: string): void {
    if (!designUrl || this.designDimsCache.has(designUrl)) return;
    // Fire and forget — getDesignDims caches on success. A failure leaves the cache cold, so
    // computeTopAnchor keeps returning null and the caller's vertical_align:'top' fallback stands.
    this.getDesignDims(designUrl).catch(() => null);
  }

  /** True when the URL points at an SVG, ignoring any query string or fragment. */
  private isSvgUrl(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0].split('#')[0];
    return path.toLowerCase().endsWith('.svg');
  }

  /**
   * A reported 300x150 for an SVG is the CSS default size for a replaced element with no intrinsic
   * dimensions — not the artwork's real aspect ratio. It must not be cached: topAnchoredMediaPosition
   * scales purely on designH/designW, so a bogus 2:1 would mis-place every viewBox-only logo on
   * export while still passing a naive `> 0` check.
   */
  private isBrowserDefaultSvgSize(url: string, width: number, height: number): boolean {
    return this.isSvgUrl(url) && width === 300 && height === 150;
  }

  /** Numeric SVG length in user units. Returns 0 for percentages or units we can't resolve here. */
  private parseSvgLength(value: string | null): number {
    if (!value) return 0;
    const match = /^\s*([0-9]*\.?[0-9]+)\s*(px)?\s*$/i.exec(value);
    return match ? Number(match[1]) : 0;
  }

  /**
   * Intrinsic dimensions of an SVG read from the document itself, because SVGs frequently declare
   * only a viewBox and no width/height. Prefers explicit width/height, falls back to the viewBox.
   * Resolves null when the file can't be read (storage-account CORS, network, malformed markup),
   * leaving the caller to fall back to the <img> path.
   */
  private async resolveSvgDims(designUrl: string): Promise<{ w: number; h: number } | null> {
    try {
      const response = await fetch(designUrl);
      if (!response.ok) return null;

      const svg = new DOMParser().parseFromString(await response.text(), 'image/svg+xml').documentElement;
      if (!svg || svg.nodeName.toLowerCase() !== 'svg') return null;

      const width = this.parseSvgLength(svg.getAttribute('width'));
      const height = this.parseSvgLength(svg.getAttribute('height'));
      if (width > 0 && height > 0) return { w: width, h: height };

      const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
      if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
        return { w: viewBox[2], h: viewBox[3] };
      }
    } catch {
      // Fall through to the <img> path.
    }
    return null;
  }

  /** Natural pixel dimensions via an <img> load, cached on success. */
  private loadImageDims(designUrl: string): Promise<{ w: number; h: number } | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0
          && !this.isBrowserDefaultSvgSize(designUrl, img.naturalWidth, img.naturalHeight)) {
          const dims = { w: img.naturalWidth, h: img.naturalHeight };
          this.designDimsCache.set(designUrl, dims);
          resolve(dims);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = designUrl;
    });
  }

  /**
   * Await the design's natural dimensions (loading + caching once). Callers await this
   * BEFORE kicking off preview/export generation so the top-anchor default is ready on the
   * very first pass — otherwise the initial variant thumbnails render centered while the
   * image is still loading. Resolves null on failure (falls back to DH default).
   */
  private getDesignDims(designUrl: string): Promise<{ w: number; h: number } | null> {
    if (!designUrl) return Promise.resolve(null);
    const cached = this.designDimsCache.get(designUrl);
    if (cached) return Promise.resolve(cached);

    // SVG artwork: read the real dimensions out of the file, since an <img> load reports the
    // browser's 300x150 default for the common viewBox-only case. Falls back to the <img> path,
    // which rejects that default rather than caching a wrong aspect ratio.
    if (this.isSvgUrl(designUrl)) {
      return this.resolveSvgDims(designUrl).then((dims) => {
        if (dims) {
          this.designDimsCache.set(designUrl, dims);
          return dims;
        }
        return this.loadImageDims(designUrl);
      });
    }

    return this.loadImageDims(designUrl);
  }

  /**
   * Build logo customization object, including media_position if available.
   */
  buildLogoCustomization(logoUrl: string, mediaPosition: any): any {
    const logo: any = { url: logoUrl };
    if (mediaPosition) {
      logo.media_position = mediaPosition;
    }
    return { classes: { logo } };
  }

  /**
   * SINGLE SOURCE OF TRUTH for the default logo placement across the three DH render surfaces
   * (editor project seed, preview, export). Returns a logo customization whose media_position is:
   *  - the provided userMediaPosition (a real editor placement) when present, else
   *  - the top-anchor computed for this exact logo + zone (default "chest print"), else
   *  - omitted (cold designDimsCache) so the caller's vertical_align:'top' remains the fallback.
   * Mirrors the inline logic the export path used to duplicate; keep this the ONLY implementation.
   */
  private buildTopAnchoredLogoCustomization(logoUrl: string, zoneDimensions: string, userMediaPosition: any = null): any {
    const customization = this.buildLogoCustomization(logoUrl, userMediaPosition);
    if (!userMediaPosition && customization?.classes?.logo) {
      const topAnchor = this.computeTopAnchor(logoUrl, zoneDimensions);
      if (topAnchor) {
        customization.classes.logo.media_position = topAnchor;
      }
    }
    return customization;
  }

  /**
   * Build a seed customization for a DH project creation site, warming the design dims first so
   * the top-anchor default is deterministic on the FIRST create pass (a cold designDimsCache would
   * otherwise make computeTopAnchor return null → project seeded centered). A real user placement
   * wins outright and needs no dims. Used by EVERY project-seed site so the editor, preview and
   * export all open top-anchored — the "single source of truth" goal.
   */
  private buildSeedCustomization(logoUrl: string, productId: string, sideNameNorm: string, userMediaPosition: any = null): Promise<any> {
    const build = () => {
      const zoneDimensions = this.getZoneDimensionsForSide(productId, sideNameNorm);
      return this.buildTopAnchoredLogoCustomization(logoUrl, zoneDimensions, userMediaPosition);
    };
    if (userMediaPosition) {
      return Promise.resolve(build());
    }
    // getDesignDims resolves fast when already cached and null on load failure (top-anchor is
    // then omitted → DH default). Either way build() runs once dims are settled.
    return this.getDesignDims(logoUrl).then(() => build());
  }

  /**
   * Resolve the logo URL an export/preview will actually render for a side (create-flow / fallback
   * path — NOT the edit-mode DB customizations branch). Extracted from createSideCustomizations so
   * the export caller can warm the SAME URL's dims before building the (synchronous) customization.
   */
  private resolveExportLogoUrl(product: ProductColorVariant, variant: ColorVariant, pageName: string): string {
    const pageNameLower = pageName.toLowerCase();
    const sideNameNormalized = pageNameLower.replace(/\s+/g, '_');
    const sideConfig = this.getSideConfigForCurrentSide(sideNameNormalized);
    const displayName = sideConfig?.displayName || '';

    let logoUrl = this.designData?.designUrl || this.designData?.previewUrl || '';

    const sideNameVariations = [pageName, pageName.replace(/\s+/g, '_'), pageNameLower, sideNameNormalized];
    if (displayName && !sideNameVariations.includes(displayName)) {
      sideNameVariations.push(displayName);
    }
    for (const nameVariation of sideNameVariations) {
      if (variant.sideLogos && variant.sideLogos[nameVariation]) {
        logoUrl = variant.sideLogos[nameVariation];
        break;
      }
    }

    // Fall back to variant-level custom logo
    if (!logoUrl || logoUrl === this.designData?.previewUrl || logoUrl === this.designData?.designUrl) {
      if (variant.variantDesignUrl && !variant.useDefaultDesign) {
        logoUrl = variant.variantDesignUrl;
      }
    }

    // WI-1522 fix #1/#2: this create-flow path previously had NO fallback to the locked/default
    // variant's per-side logo at all — a new color's own sideLogos/variantDesignUrl are unset by
    // design (see toggleColorSelection), so a still-unresolved logo here fell straight through to
    // the generic primary design, which is exactly why a print export of a NEW color's secondary
    // imprint (e.g. Full Back) rendered with the primary/LC logo or nothing. Falls back to the
    // locked variant's CURRENT per-side logo (getLockedVariantLogoForSide reads it live, so a
    // later edit to the locked color's side is picked up on the next export too, not just whatever
    // it looked like when this color was added).
    const stillUnresolved = !logoUrl || logoUrl === this.designData?.previewUrl || logoUrl === this.designData?.designUrl;
    if (stillUnresolved && Number(variant.styleId) !== Number(product.lockedStyleId)) {
      const lockedVariant = product.selectedColors.find(
        (c: ColorVariant) => Number(c.styleId) === Number(product.lockedStyleId)
      );
      const lockedLogo = this.getLockedVariantLogoForSide(lockedVariant, sideNameNormalized);
      if (lockedLogo) {
        logoUrl = lockedLogo;
      }
    }

    return logoUrl;
  }

  /**
   * Build a full DSHDLib.createProject customizations object from raw getProjectData response.
   * Reproduces ALL design elements (logo, text, images, fonts, colors) from the source project,
   * not just the logo URL, so the new variant starts with the same design state as the locked one.
   *
   * DH createProject customizations shape: { classes: { [className]: { property: value, ... } } }
   * Each element may appear under multiple class names — we emit one entry per class.
   *
   * @param response        Raw getProjectData API response (pages[].elements)
   * @param fallbackLogoUrl Logo URL to use if no logo element is found in the project data
   * @param fallbackMedia   media_position to use as fallback for the logo element
   */
  buildFullCustomizationsFromProjectData(response: any, fallbackLogoUrl: string, fallbackMedia: any): any {
    const root = response?.pages ? response : (response?.data || response);
    const pages: any[] = root?.pages || [];

    // classes accumulates: { className: { ...elementProperties } }
    const classes: { [className: string]: any } = {};

    for (const page of pages) {
      const elements = page.elements || {};
      for (const elementId of Object.keys(elements)) {
        const element = elements[elementId];
        const elementClasses: string[] = element.element_classes || [];

        // Build the per-class customization payload for this element
        const payload: any = {};

        // Image / logo elements
        if (element.type === 'image' || element.type === 'logo') {
          const imageUrl = element.original_image_url || element.image_url || element.url || element.src || '';
          if (imageUrl) {
            payload.url = imageUrl;
          }
          if (element.media_position) {
            payload.media_position = element.media_position;
          }
        }

        // Text elements — carry over content and styling
        if (element.type === 'text') {
          if (element.text !== undefined && element.text !== null) {
            payload.text = element.text;
          }
          if (element.font_family) {
            payload.font_family = element.font_family;
          }
          if (element.font_size !== undefined) {
            payload.font_size = element.font_size;
          }
          if (element.font_color) {
            payload.font_color = element.font_color;
          }
          if (element.text_align) {
            payload.text_align = element.text_align;
          }
          if (element.bold !== undefined) {
            payload.bold = element.bold;
          }
          if (element.italic !== undefined) {
            payload.italic = element.italic;
          }
          if (element.underline !== undefined) {
            payload.underline = element.underline;
          }
          // Carry over letter spacing, line height if present
          if (element.letter_spacing !== undefined) {
            payload.letter_spacing = element.letter_spacing;
          }
          if (element.line_height !== undefined) {
            payload.line_height = element.line_height;
          }
        }

        // For any element type, carry over position/size if they are user-adjustable
        // (media_position already handled for images above)

        // Write payload under each class name this element belongs to
        if (Object.keys(payload).length > 0) {
          for (const cls of elementClasses) {
            if (!cls) continue;
            // Merge into existing entry for this class (multiple elements can share a class)
            classes[cls] = Object.assign({}, classes[cls] || {}, payload);
          }
        }
      }
    }

    // Ensure the logo class is present (fall back to saved logo URL if not in project data)
    if (!classes['logo']) {
      const logo: any = { url: fallbackLogoUrl };
      if (fallbackMedia) {
        logo.media_position = fallbackMedia;
      }
      classes['logo'] = logo;
    } else {
      // If the project's logo element had no URL (e.g., blank), use fallback
      if (!classes['logo'].url && fallbackLogoUrl) {
        classes['logo'].url = fallbackLogoUrl;
      }
      // If the project's logo element had no media_position, use fallback
      if (!classes['logo'].media_position && fallbackMedia) {
        classes['logo'].media_position = fallbackMedia;
      }
    }

    return { classes };
  }

  /**
   * Count how many text elements exist in a raw getProjectData response.
   * Used for logging only (shows how many text elements will be reproduced).
   */
  private countTextElementsInProjectData(response: any): number {
    const root = response?.pages ? response : (response?.data || response);
    const pages: any[] = root?.pages || [];
    let count = 0;
    for (const page of pages) {
      const elements = page.elements || {};
      for (const elementId of Object.keys(elements)) {
        if (elements[elementId]?.type === 'text') {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Parse project metadata to extract element and zone placement from the logo element.
   */
  parseProjectMetadata(metadata: any, projectId: string): any {
    const root = metadata?.pages ? metadata : (metadata?.data || metadata);
    const pages = root?.pages || [];
    if (pages.length === 0) return null;

    for (const page of pages) {
      const elements = page.elements || {};
      for (const elementId of Object.keys(elements)) {
        const element = elements[elementId];
        const classes = element.element_classes || [];
        if (classes.includes('logo')) {
          const sideData: any = {
            projectId: projectId,
            elementPlacement: {
              height: element.height,
              width: element.width,
              left: element.left,
              top: element.top
            },
            zonePlacement: null,
            // Extract logo URL from the element
            logoUrl: element.original_image_url || element.image_url || element.url || element.src || null,
            // Extract asset_id for fetching the actual logo file
            assetId: element.asset_id || null
          };
          if (element.zone) {
            sideData.zonePlacement = {
              height: element.zone.height,
              width: element.zone.width,
              left: element.zone.left,
              top: element.zone.top
            };
          }
          return sideData;
        }
      }
    }
    return { projectId, elementPlacement: null, zonePlacement: null, logoUrl: null };
  }

  /**
   * Fetch metadata for a project and store elementPlacement/zonePlacement in variant.sides.
   */
  fetchAndStoreMetadata(projectId: string, variant: ColorVariant, sideName: string): void {
    this.designHuddleService.getProjectData(projectId).subscribe({
      next: (response: any) => {
        const parsedData = this.parseProjectMetadata(response, projectId);

        if (parsedData && variant.sides) {
          const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
          const side = variant.sides.find((s: any) => {
            const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
            return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
          });
          if (side) {
            side.elementPlacement = parsedData.elementPlacement;
            side.zonePlacement = parsedData.zonePlacement;
            // Tags the placement with the project it was actually fetched from — see
            // isSideFrameDistorted's matching check for why: this call is fire-and-forget, and
            // exportCurrentSide (which reads elementPlacement for the stretched-logo guard) runs
            // immediately after, in the same tick, at every call site — well before this response
            // can land. Without this tag, a Replace Artwork/new-imprint call briefly compares the
            // PREVIOUS logo's still-fitted frame against the NEW logo's aspect ratio (a near-
            // guaranteed mismatch that has nothing to do with the new project, which DH always
            // auto-fits correctly), producing a false "Logo is stretched" warning even though
            // nothing is actually wrong and no API call failed.
            side.elementPlacementProjectId = projectId;
          }
        }
      },
      error: (err: any) => {
      }
    });
  }

  /**
   * Open DesignHuddle editor for a side. Reuses existing project if one exists.
   */
  openEditorForSide(product: ProductColorVariant, variant: ColorVariant, displaySideName: string): void {
    const sideNameNorm = displaySideName.toLowerCase().replace(/\s+/g, '_');
    const variantKey = `${product.productId}_${variant.styleId}`;

    const isLockedVariantEntry = Number(variant.styleId) === Number(product.lockedStyleId);
    // Dump the relevant variantSideProjects entries and variant.sides ProjectIds for diagnosis
    const mapDump: Record<string, string> = {};
    this.variantSideProjects.forEach((v, k) => { if (k.startsWith(`${product.productId}_`)) mapDump[k] = v; });
    const sidesDump = (variant.sides || []).map((s: any) => ({ SideName: s.SideName || s.Name, ProjectId: s.ProjectId }));

    // Find template and side info
    const productSides = this.getProductAvailableSides(product.productId);
    const sideInfo = productSides.find(s =>
      s.name === sideNameNorm ||
      s.displayName === displaySideName ||
      s.name.includes(sideNameNorm) ||
      sideNameNorm.includes(s.name)
    );
    if (!sideInfo) {
      return;
    }

    // Get product image URL for the editor
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) return;

    const sideConfig = this.getSideConfigForCurrentSide(sideNameNorm);
    if (!sideConfig) return;

    let productImageUrl = variantStyle[sideConfig.imageField] || '';
    if (productImageUrl && !productImageUrl.startsWith('http')) {
      productImageUrl = this.joinUrl(this.imgBaseURL, productImageUrl);
    }

    // Parse PrintPlacement for zone dimensions and offset
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    if (sideInfo.printPlacement) {
      try {
        const pp = typeof sideInfo.printPlacement === 'string'
          ? JSON.parse(sideInfo.printPlacement) : sideInfo.printPlacement;
        if (pp.Width && pp.Height) zoneDimensions = `${pp.Width}x${pp.Height}`;
        if (pp.X !== undefined && pp.Y !== undefined) zoneOffset = `${pp.X}x${pp.Y}`;
      } catch (e) {
      }
    }

    // Store editor context
    this.editorProduct = product;
    this.editorVariant = variant;
    this.editorSideName = sideNameNorm;
    this.editorProductName = product.productName + ' - ' + displaySideName;
    // Static container id strategy (mirrors edit-master-product which works for N opens):
    // we manually empty the container's children on close so the SDK finds a clean element
    // on the next Editors.insert call.  No unique-id / Editors.remove needed.
    this.showEditorModal = true;
    this.isEditorLoading = true;

    // Reset text-element flag — will be re-evaluated when the editor closes and project
    // data is fetched.  This ensures a fresh open/close cycle always re-checks.
    this.currentProjectHasTextElements = false;

    const DSHDLib = (window as any).DSHDLib;
    if (!DSHDLib) {
      console.error('❌ DesignHuddle SDK not loaded');
      this.showEditorModal = false;
      return;
    }

    // Check if a project already exists for this variant/side
    const existingProjectId = this.variantSideProjects.get(`${variantKey}_${sideNameNorm}`) ||
                              this.getSideProjectIdFromVariantSides(variant, sideNameNorm);


    // Helper to insert editor after DOM is ready.
    // Uses a plain 100ms setTimeout (same as edit-master-product which reliably supports
    // multiple open/close cycles).  The container is static so no two-phase microtask
    // fence is required — Angular has already rendered the *ngIf wrapper by the time
    // the timeout fires.
    const containerId = this.editorContainerId;
    const insertEditor = (projectId: string) => {
      setTimeout(() => {
        const el = document.getElementById(containerId);
        if (!el) {
          console.error(`❌ [insertEditor] container #${containerId} NOT FOUND in DOM — showEditorModal=${this.showEditorModal}. All ids in DOM:`, Array.from(document.querySelectorAll('[id]')).map((e: any) => e.id).filter(id => id.startsWith('dh-')));
          this.isEditorLoading = false;
          return;
        }
        // In guest mode (guest:true in DSHDLib.configure), Editors.insert takes the async
        // branch and returns undefined — the editor instance is delivered via the 3rd-argument
        // callback instead.  We pass a callback to capture the instance for later .remove().
        try {
          DSHDLib.Editors.insert(containerId, {
            project_id: projectId,
            product_image_url: productImageUrl,
            product_image_width: 500,
            product_image_canvas_zone_dimensions: zoneDimensions,
            product_image_canvas_zone_offset: zoneOffset,
            // Belt-and-suspenders only: this param MAY set the zone's default vertical alignment,
            // but it is NOT verified to reposition the already-seeded logo element on the DH JS SDK
            // v1 editor. The SOURCE OF TRUTH for the editor's initial placement is the project seed
            // (createProject customizations.classes.logo.media_position, built top-anchored via
            // buildSeedCustomization); the seed opens top-anchored regardless of this param. If DH
            // support later confirms an editor-level element-anchor param, it can supplement the seed.
            product_image_canvas_zone_vertical_align: 'top'
          }, (err: any, instance: any) => {
            if (err) {
              console.error(`❌ [insertEditor] Editors.insert callback error:`, err);
            } else {
              // Store the instance so closeEditorModal can call instance.remove()
              // to deregister it from the SDK's internal registry before the next open.
              this.editorInstance = instance || null;
              // Override DH's own Upload-button flow so in-editor uploads run through the same
              // transparent-padding trim pipeline as the upload-design page (see service doc).
              if (this.editorInstance) {
                this.uploadOverride.wireMediaUploadOverride(this.editorInstance);
              }
            }
          });
        } catch (insertErr) {
          console.error(`❌ [insertEditor] DSHDLib.Editors.insert threw:`, insertErr);
        }
        this.isEditorLoading = false;
      }, 100);
    };

    if (existingProjectId) {
      this.editorProjectId = existingProjectId;
      // EDIT MODE cross-session guard: a project id loaded from the DB was created by a guest
      // session that no longer exists. With guest:true, DesignHuddle authorizes editor access only
      // for the owning guest, so opening it directly fails inside the editor iframe with
      // "Access to this project is not authorized" (a network error that never reaches the
      // Editors.insert callback, so it can't be caught reactively). Reproduce it under the current
      // guest session first — only once; the reproduced id is then cached and guest-owned, so
      // subsequent opens reuse it directly. In the create flow the project was made by this
      // browser's live guest session and opens directly.
      if (this.isEditMode && !this.guestOwnedProjectIds.has(existingProjectId)) {
        this.reproduceProjectUnderGuestAndInsert(existingProjectId, product, variant, sideInfo, sideNameNorm, variantKey, displaySideName, insertEditor);
      } else {
        // Reuse the existing DH project — no createProject call needed.
        // existingProjectId may have resolved via the getSideProjectIdFromVariantSides fallback
        // (variant.sides[].ProjectId) rather than the variantSideProjects map itself — register it
        // here so every other consumer keyed off that map (propagateEditedDesignToAllColors, etc.)
        // sees it too, instead of each one needing its own fallback.
        this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, existingProjectId);
        insertEditor(existingProjectId);
      }
    } else {
      // No existing project for this variant/side.
      // If this is NOT the locked variant, try to clone the locked variant's project
      // for the same side so the editor pre-loads the locked design as a starting point.
      const isLockedVariant = Number(variant.styleId) === Number(product.lockedStyleId);

      if (!isLockedVariant) {
        // Look up the locked variant's project_id for this same side
        let lockedVariant = product.selectedColors.find(
          c => Number(c.styleId) === Number(product.lockedStyleId)
        );
        const lockedKey = `${product.productId}_${product.lockedStyleId}`;
        let lockedProjectId = this.variantSideProjects.get(`${lockedKey}_${sideNameNorm}`)
          || (lockedVariant ? this.getSideProjectIdFromVariantSides(lockedVariant, sideNameNorm) : null);

        // The locked/default color hasn't touched this side yet — fall back to whichever
        // already-selected color owns a project for (product, side) (see findSideProjectOwner /
        // toggleColorSelection's reuse branch). Without this, a color that was silently BORROWING
        // another non-locked color's unedited design (never registered in variantSideProjects for
        // itself, by design) would clone a BLANK project the first time its editor is opened,
        // losing the shared design entirely.
        if (!lockedProjectId) {
          const owner = this.findSideProjectOwner(product, sideNameNorm);
          if (owner) {
            lockedProjectId = this.variantSideProjects.get(`${product.productId}_${owner.styleId}_${sideNameNorm}`) || null;
            lockedVariant = owner;
          }
        }


        if (lockedProjectId) {
          // Reproduce the locked variant's design as a new project owned by the current guest
          // session, using DSHDLib.createProject.
          //
          // WHY NOT REST CLONE:  The backend clone endpoint (/api/projects/{id}/clone) was
          // tried with the guest bearer token from getGuestAccessToken, but the cloned project
          // still returned "Access to this project is not authorized" when the SDK editor tried
          // to load it.  The root cause is that DH assigns ownership based on the API token
          // used to *create* the project — the REST clone runs server-side with the partner
          // token regardless of what guest token we forward, so the cloned project ends up
          // partner-owned and the guest SDK session is denied.
          //
          // SOLUTION:  Use the SDK's DSHDLib.createProject which always creates the project
          // under the *current guest session* — guaranteeing the editor can open it.  We seed
          // the new project with the same template_id (already in sideInfo) and reconstruct
          // the customization object from the locked variant's saved logo URL and element
          // placements.  The locked variant's design edits (element positions, logo upload)
          // are preserved via the customization object exactly as they were when the editor
          // closed on the locked variant.
          //
          // NOTE:  The backend cloneProject endpoint is intentionally kept intact — it may be
          // useful for other flows or future partner-side operations.


          // Build fallback logo-only customization from the locked variant's saved data
          // (used when getProjectData fails or returns no elements).
          const lockedLogoUrl = lockedVariant
            ? (this.getSideLogoUrl(lockedVariant, sideNameNorm) || this.designData?.designUrl || this.designData?.previewUrl || '')
            : (this.designData?.designUrl || this.designData?.previewUrl || '');

          // Retrieve element/zone placements from the locked variant's sides so the logo
          // is positioned in the same spot as on the locked variant.
          const lockedSidePlacement = lockedVariant?.sides
            ? (() => {
                const s = this.matchSideByName(lockedVariant.sides, sideNameNorm);
                return (s?.elementPlacement || s?.zonePlacement)
                  ? { elementPlacement: s.elementPlacement, zonePlacement: s.zonePlacement }
                  : null;
              })()
            : null;

          const lockedMediaPosition = lockedSidePlacement
            ? this.calculateMediaPosition(lockedSidePlacement.elementPlacement, lockedSidePlacement.zonePlacement)
            : this.getMediaPositionForSide(lockedVariant || variant, sideNameNorm);

          // Route the logo-only fallback (used when getProjectData fails) through the shared
          // top-anchor helper so a locked variant with no real placement still seeds top-anchored.
          // (The primary path reproduces ALL elements via buildFullCustomizationsFromProjectData and
          // is intentionally left untouched.) Dims may be cold here — this is the rare error path;
          // computeTopAnchor omits media_position if so and warms for next time.
          const lockedZoneDimensions = this.getZoneDimensionsForSide(product.productId, sideNameNorm);
          const fallbackCustomization = this.buildTopAnchoredLogoCustomization(lockedLogoUrl, lockedZoneDimensions, lockedMediaPosition);

          const projectTitle = variant.projectName || product.productName + '_' + displaySideName;

          // Inner function: call DSHDLib.createProject with the given customizations object,
          // then wire up the project for the new variant.
          const doCreateProject = (customizations: any) => {
            DSHDLib.createProject({
              template_id: sideInfo.templateId,
              project_title: toDesignHuddleProjectTitle(projectTitle),
              customizations: customizations
            }, (err: any, projectData: any) => {
              if (err) {
                console.error('❌ [openEditorForSide] sdk-reproduce createProject failed — falling back to blank project:', err);
                this.createProjectFallback(product, variant, sideInfo, sideNameNorm, variantKey, displaySideName, insertEditor);
                return;
              }

              const newProjectId = projectData?.project_id;
              if (!newProjectId) {
                console.error('❌ [openEditorForSide] sdk-reproduce createProject returned no project_id — falling back:', projectData);
                this.createProjectFallback(product, variant, sideInfo, sideNameNorm, variantKey, displaySideName, insertEditor);
                return;
              }

              this.editorProjectId = newProjectId;
              this.guestOwnedProjectIds.add(newProjectId);

              // Cache so subsequent opens for this variant/side reuse the same project
              this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, newProjectId);

              // Update variant.sides so the project ID persists in the data model
              if (variant.sides) {
                const matchedSide = this.matchSideByName(variant.sides, sideNameNorm);
                if (matchedSide) {
                  matchedSide.ProjectId = newProjectId;
                }
              }

              insertEditor(newProjectId);
            });
          };

          // Fetch the locked project's full element data so we can reproduce ALL design changes
          // (text content, fonts, colors, logo position, etc.) — not just the logo URL.
          // getProjectData returns pages[].elements where each element has element_classes,
          // type, and editable properties (url, media_position, text, font_*, etc.).
          this.designHuddleService.getProjectData(lockedProjectId).subscribe({
            next: (projectDataResponse: any) => {
              const fullCustomizations = this.buildFullCustomizationsFromProjectData(projectDataResponse, lockedLogoUrl, lockedMediaPosition);
              const classCount = Object.keys(fullCustomizations.classes || {}).length;
              const textCount = this.countTextElementsInProjectData(projectDataResponse);
              doCreateProject(fullCustomizations);
            },
            error: (err: any) => {
              // getProjectData failed — fall back to logo-only customization so we don't regress
              doCreateProject(fallbackCustomization);
            }
          });

          return; // async path — do not fall through to createProject below
        }
        // No locked project available for this side — fall through to createProject
      }

      // Locked variant (or non-locked with no locked project to clone): create from template
      this.createProjectFallback(product, variant, sideInfo, sideNameNorm, variantKey, displaySideName, insertEditor);
    }
  }

  /**
   * Create a new DH project from template for a given variant/side.
   * Called as the final fallback when no existing or cloneable project is found.
   */
  private createProjectFallback(
    product: ProductColorVariant,
    variant: ColorVariant,
    sideInfo: any,
    sideNameNorm: string,
    variantKey: string,
    displaySideName: string,
    insertEditor: (projectId: string) => void
  ): void {
    const DSHDLib = (window as any).DSHDLib;
    const existingLogoUrl = this.getSideLogoUrl(variant, sideNameNorm)
      || this.designData?.designUrl || this.designData?.previewUrl || '';
    const mediaPosition = this.getMediaPositionForSide(variant, sideNameNorm);
    const projectTitle = variant.projectName || product.productName + '_' + displaySideName;

    // Seed the DH project top-anchored (chest print) so the EDITOR opens top-anchored — the
    // project seed, not the unverified Editors.insert vertical_align param, is the source of truth.
    // buildSeedCustomization warms the design dims first so a default (no user placement) design is
    // seeded top-anchored rather than centered on the first open.
    this.buildSeedCustomization(existingLogoUrl, product.productId, sideNameNorm, mediaPosition).then(customizationObject => {
      DSHDLib.createProject({
        template_id: sideInfo.templateId,
        project_title: toDesignHuddleProjectTitle(projectTitle),
        customizations: customizationObject
      }, (err: any, projectData: any) => {
        if (err) {
          console.error('❌ Error creating project for editor:', err);
          this.isEditorLoading = false;
          this.showEditorModal = false;
          return;
        }

        const projectId = projectData.project_id;
        this.editorProjectId = projectId;
        this.guestOwnedProjectIds.add(projectId);

        // Store the new project so it's used for export/preview
        this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, projectId);

        // Also update variant.sides if available
        if (variant.sides) {
          const side = this.matchSideByName(variant.sides, sideNameNorm);
          if (side) {
            side.ProjectId = projectId;
          }
        }

        insertEditor(projectId);
      });
    });
  }

  /**
   * Reproduce an existing DH project under the CURRENT guest session and open it in the editor.
   *
   * WHY: with guest:true, DesignHuddle authorizes editor access only for the guest session that
   * created the project. In edit mode the stored project ids come from the DB (created by a guest
   * session that no longer exists), so opening them directly fails inside the editor iframe with
   * "Access to this project is not authorized" — a network error that never reaches the
   * Editors.insert callback, so it cannot be caught reactively. We fetch the source project's full
   * design via the backend (the partner token can read any project), re-create it with
   * DSHDLib.createProject (which always owns the new project under the current guest), cache the
   * new id (so later opens reuse it directly), point variant.sides at it (so Save persists the
   * owned, edited project), and open THAT. Mirrors the non-locked "sdk-reproduce" branch.
   */
  private reproduceProjectUnderGuestAndInsert(
    sourceProjectId: string,
    product: ProductColorVariant,
    variant: ColorVariant,
    sideInfo: any,
    sideNameNorm: string,
    variantKey: string,
    displaySideName: string,
    insertEditor: (projectId: string) => void
  ): void {
    const DSHDLib = (window as any).DSHDLib;
    const logoUrl = this.getSideLogoUrl(variant, sideNameNorm)
      || this.designData?.designUrl || this.designData?.previewUrl || '';
    const mediaPosition = this.getMediaPositionForSide(variant, sideNameNorm);
    const fallbackCustomization = this.buildLogoCustomization(logoUrl, mediaPosition);
    const projectTitle = variant.projectName || product.productName + '_' + displaySideName;

    const doCreateProject = (customizations: any) => {
      DSHDLib.createProject({
        template_id: sideInfo.templateId,
        project_title: toDesignHuddleProjectTitle(projectTitle),
        customizations: customizations
      }, (err: any, projectData: any) => {
        const newProjectId = projectData?.project_id;
        if (err || !newProjectId) {
          console.error(`❌ [reproduce] createProject failed for source ${sourceProjectId} — falling back to blank project:`, err || projectData);
          this.createProjectFallback(product, variant, sideInfo, sideNameNorm, variantKey, displaySideName, insertEditor);
          return;
        }
        this.editorProjectId = newProjectId;
        this.guestOwnedProjectIds.add(newProjectId);
        this.variantSideProjects.set(`${variantKey}_${sideNameNorm}`, newProjectId);
        if (variant.sides) {
          const matchedSide = this.matchSideByName(variant.sides, sideNameNorm);
          if (matchedSide) matchedSide.ProjectId = newProjectId;
        }
        insertEditor(newProjectId);
      });
    };

    // Fetch the source project's full element data (logo, text, positions, fonts, colors) so the
    // reproduction is faithful. getProjectData goes through the backend/partner token, which can
    // read any project regardless of guest ownership.
    this.designHuddleService.getProjectData(sourceProjectId).subscribe({
      next: (projectDataResponse: any) => {
        const fullCustomizations = this.buildFullCustomizationsFromProjectData(projectDataResponse, logoUrl, mediaPosition);
        doCreateProject(fullCustomizations);
      },
      error: (err: any) => {
        doCreateProject(fallbackCustomization);
      }
    });
  }

  // Hides the modal and deregisters the editor instance/container from the DesignHuddle SDK —
  // the part of closing that must ALWAYS happen, whether the user saves or dismisses without
  // saving. Split out of closeEditorModal() so dismissEditorModal() (the top-bar X — close
  // without saving) can reuse it without also running the metadata-fetch/preview-refresh below.
  private teardownEditor(): void {
    const containerIdAtClose = this.editorContainerId;


    // Step 1: Hide the modal (same order as edit-master-product which works for N opens).
    // Angular's *ngIf DOM removal runs during the NEXT change-detection cycle, so the
    // container element is still present synchronously right after this assignment.
    this.showEditorModal = false;

    // Step 2a: Call editorInstance.remove() to deregister the editor from the SDK's internal
    // registry (keyed by the instance's own random id, not by container id or project id).
    // Without this call, Editors.insert on the next open silently no-ops because the SDK
    // still considers a session active.  DOM child removal alone does not touch the registry.
    if (this.editorInstance && typeof this.editorInstance.remove === 'function') {
      try {
        this.editorInstance.remove();
      } catch (removeErr) {
      }
    } else {
    }
    this.editorInstance = null;

    // Step 2b: Also manually clear the container's children as a belt-and-suspenders measure.
    // editorInstance.remove() already does this asynchronously (setTimeout 0), but doing it
    // synchronously here ensures the DOM is clean before Angular's *ngIf runs its removal.
    const containerElAtClose = document.getElementById(containerIdAtClose);
    if (containerElAtClose) {
      while (containerElAtClose.firstChild) {
        containerElAtClose.removeChild(containerElAtClose.firstChild);
      }
    } else {
    }
  }

  // Top-bar X: close the editor WITHOUT saving — tears down the SDK instance (required either
  // way, see teardownEditor) but skips the metadata-fetch/preview-refresh below, so whatever the
  // user changed in this session never overwrites the card's existing preview/logo.
  dismissEditorModal(): void {
    this.teardownEditor();
    this.editorProjectId = null;
    this.editorProduct = null;
    this.editorVariant = null;
    this.editorSideName = null;
    this.editorApplyToAllColors = false;
    this.editorApplyToAllColorsExcludeStyleIds = new Set();
  }

  /**
   * WI-1578: in-page replacement for window.confirm() — tri-state result, since the X button needs
   * to mean something distinct from the Cancel button whenever Cancel itself is a real (non-abort)
   * choice (editImprintDesignForAllColors' "keep their design, apply to everyone else" option):
   *   true  — Confirm button
   *   false — Cancel button (an explicit, meaningful decline — caller decides what that means)
   *   null  — X button / closed without choosing — always treated as "do nothing at all"
   * A caller that only ever needs a plain yes/no can keep writing `if (!confirmed) return;` — both
   * false and null are falsy, so that guard still means what it always did.
   */
  private showConfirm(message: string, opts?: { title?: string; confirmLabel?: string; cancelLabel?: string; destructive?: boolean; allowOverlayDismiss?: boolean; compactText?: boolean; warn?: boolean }): Promise<boolean | null> {
    this.confirmModalTitle = opts?.title || 'Please confirm';
    this.confirmModalMessage = message;
    this.confirmModalConfirmLabel = opts?.confirmLabel || 'Continue';
    this.confirmModalCancelLabel = opts?.cancelLabel || 'Cancel';
    this.confirmModalDestructive = !!opts?.destructive;
    this.confirmModalAllowOverlayDismiss = opts?.allowOverlayDismiss !== false;
    this.confirmModalCompactText = !!opts?.compactText;
    this.confirmModalWarn = !!opts?.warn;
    this.showConfirmModal = true;
    return new Promise<boolean | null>(resolve => {
      this.confirmModalResolve = resolve;
    });
  }

  /** Splits confirmModalMessage on newlines for the template, same text a window.confirm() would wrap. */
  get confirmModalMessageLines(): string[] {
    return (this.confirmModalMessage || '').split('\n');
  }

  /** Overlay click — a no-op when this prompt was opened with allowOverlayDismiss: false. */
  onConfirmModalOverlayClick(): void {
    if (this.confirmModalAllowOverlayDismiss) {
      this.confirmModalClose();
    }
  }

  confirmModalAccept(): void {
    this.showConfirmModal = false;
    const resolve = this.confirmModalResolve;
    this.confirmModalResolve = null;
    if (resolve) resolve(true);
  }

  /** Cancel button — an explicit, meaningful decline (not the same as X/closed, see showConfirm). */
  confirmModalDecline(): void {
    this.showConfirmModal = false;
    const resolve = this.confirmModalResolve;
    this.confirmModalResolve = null;
    if (resolve) resolve(false);
  }

  /** X button (and overlay click, when allowed) — always "do nothing at all", never a real choice. */
  confirmModalClose(): void {
    this.showConfirmModal = false;
    const resolve = this.confirmModalResolve;
    this.confirmModalResolve = null;
    if (resolve) resolve(null);
  }

  /**
   * Close editor modal, fetch metadata, and refresh preview.
   */
  closeEditorModal(): void {
    const projectId = this.editorProjectId;
    const product = this.editorProduct;
    const variant = this.editorVariant;
    const sideName = this.editorSideName;
    // WI-1578: captured as locals before the reset below (same reason projectId/product/variant/
    // sideName are) — applyLogoAfterEditorClose runs inside async subscribe callbacks below, well
    // after this.editorApplyToAllColors/editorApplyToAllColorsExcludeStyleIds are reset for the
    // next editor open.
    const applyToAllColors = this.editorApplyToAllColors;
    const applyToAllColorsExcludeStyleIds = this.editorApplyToAllColorsExcludeStyleIds;

    this.teardownEditor();

    if (projectId && product && variant && sideName) {
      this.isFetchingEditorMetadata = true;

      this.designHuddleService.getProjectData(projectId).subscribe({
        next: (response: any) => {
          const parsedData = this.parseProjectMetadata(response, projectId);

          // Text elements cannot be rendered by the SDK's getVariableTemplatePreviewURL — the
          // SDK path composites only image/logo elements. Use the export API instead so text
          // is rendered server-side and the resulting thumbnail is accurate.
          const hasTextElements = this.projectDataHasTextElements(response);

          // Persist the result so that subsequent variant selections on the same project
          // (different color, same projectId) can also use the export path.
          this.currentProjectHasTextElements = hasTextElements;

          // Store placements on variant.sides
          if (parsedData && variant.sides) {
            const side = this.matchSideByName(variant.sides, sideName);
            if (side) {
              side.elementPlacement = parsedData.elementPlacement;
              side.zonePlacement = parsedData.zonePlacement;
            }
          }

          // The designer just edited this side: drop any stale "logo is stretched" flag so the
          // next export re-evaluates the (possibly re-fitted) frame from scratch.
          this.clearPrintExportDistorted(product, variant, sideName);

          const mediaPosition = parsedData ? this.calculateMediaPosition(parsedData.elementPlacement, parsedData.zonePlacement) : null;
          const assetId = parsedData?.assetId;

          // If we have an asset_id, fetch the actual logo file via backend, upload to Azure
          if (assetId && projectId) {
            this.http.get<any>(`${environment.baseUrl}/api/DesignHuddle/projects/${projectId}/assets/${assetId}`).subscribe({
              next: (assetResponse: any) => {
                const azureLogoUrl = assetResponse?.azureUrl || null;
                const logoUrl = azureLogoUrl || parsedData?.logoUrl
                  || this.getSideLogoUrl(variant, sideName)
                  || this.designData?.designUrl || this.designData?.previewUrl || '';
                this.applyLogoAfterEditorClose(product, variant, sideName, logoUrl, mediaPosition, parsedData, hasTextElements, applyToAllColors, applyToAllColorsExcludeStyleIds);
              },
              error: (err: any) => {
                console.error('❌ Failed to fetch asset:', err);
                const logoUrl = parsedData?.logoUrl
                  || this.getSideLogoUrl(variant, sideName)
                  || this.designData?.designUrl || this.designData?.previewUrl || '';
                this.applyLogoAfterEditorClose(product, variant, sideName, logoUrl, mediaPosition, parsedData, hasTextElements, applyToAllColors, applyToAllColorsExcludeStyleIds);
              }
            });
          } else {
            // No asset_id — use direct logo URL
            const logoUrl = parsedData?.logoUrl
              || this.getSideLogoUrl(variant, sideName)
              || this.designData?.designUrl || this.designData?.previewUrl || '';
            this.applyLogoAfterEditorClose(product, variant, sideName, logoUrl, mediaPosition, parsedData, hasTextElements, applyToAllColors, applyToAllColorsExcludeStyleIds);
          }
        },
        error: (err: any) => {
          console.error('❌ Failed to fetch editor metadata:', err);
          this.applyLogoAfterEditorClose(product, variant, sideName, null, null, null, false, applyToAllColors, applyToAllColorsExcludeStyleIds);
        }
      });
    }

    // Clear editor state
    this.editorProjectId = null;
    this.editorProduct = null;
    this.editorVariant = null;
    this.editorSideName = null;
    this.editorApplyToAllColors = false;
    this.editorApplyToAllColorsExcludeStyleIds = new Set();
  }

  /**
   * Apply logo URL after editor close: update sideLogos, refresh preview, export, mark migrated.
   * WI-1578: when applyToAllColors is set (editor opened via an imprint row's "Edit Design"
   * button), also shares the just-edited project with every other selected color for this side
   * once this variant's own refresh/export has been kicked off — see
   * propagateEditedDesignToAllColors. excludeStyleIds skips colors the user chose to leave alone
   * (the "already has its own design" confirm) when this session was opened.
   */
  applyLogoAfterEditorClose(product: ProductColorVariant, variant: ColorVariant, sideName: string, logoUrl: string | null, mediaPosition: any, parsedData: any, hasTextElements: boolean = false, applyToAllColors: boolean = false, applyToAllColorsExcludeStyleIds: Set<number> = new Set()): void {
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const displayName = this.getDisplaySideName(sideNorm);

    // Update variant.sideLogos under all name variations
    if (logoUrl) {
      if (!variant.sideLogos) {
        variant.sideLogos = {};
      }
      variant.sideLogos[sideName] = logoUrl;
      variant.sideLogos[sideNorm] = logoUrl;
      variant.sideLogos[displayName] = logoUrl;
    }

    // Refresh the preview with updated logo URL and media_position
    this.refreshPreviewAfterEditorClose(product, variant, sideName, mediaPosition, logoUrl, hasTextElements);

    // Mark InkSoft variant as migrated (only sets the flag, does NOT propagate to replicas here)
    if (this.isInksoftProduct && !variant.isMigrated) {
      variant.isMigrated = true;
    }

    // The locked/default variant always stays 'customizedProduct' (DesignId=true, IsDefault=true) —
    // only non-locked variants get flipped to 'alternateDesign' when their editor is opened/edited.
    const isLockedVariantForSource = Number(variant.styleId) === Number(product.lockedStyleId);
    if (isLockedVariantForSource) {
      if (variant.variantSource !== 'customizedProduct') {
        variant.variantSource = 'customizedProduct';
      }
    } else if (variant.variantSource !== 'customizedProduct' && variant.variantSource !== 'alternateDesign') {
      variant.variantSource = 'alternateDesign';
    }

    // Only propagate to replicas for InkSoft migration (not for normal huddle product edits)
    if (this.isInksoftProduct && logoUrl && variant.variantSource === 'customizedProduct') {
      const replicas = product.selectedColors.filter(v =>
        v.variantSource === 'customProductImage' && v !== variant
      );
      if (replicas.length > 0) {
        const variantKey = `${product.productId}_${variant.styleId}`;
        const srcProjectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`);

        replicas.forEach(replica => {
          const replicaKey = `${product.productId}_${replica.styleId}`;

          // Update replica sideLogos
          if (!replica.sideLogos) replica.sideLogos = {};
          replica.sideLogos[sideName] = logoUrl;
          replica.sideLogos[sideNorm] = logoUrl;
          replica.sideLogos[displayName] = logoUrl;

          // Copy projectId from source variant
          if (srcProjectId) {
            this.variantSideProjects.set(`${replicaKey}_${sideNorm}`, srcProjectId);
          }

          // Clear old cached preview. NOTE: this goes through generatePreviewForCurrentSide ->
          // generateSidePreview, whose own guard skips entirely if backgroundUrls already has a
          // value for this key — the delete is required here for the regeneration to actually run.
          const replicaCacheKey = `${product.productId}_${replica.styleId}_${displayName}`;
          this.backgroundUrls.delete(replicaCacheKey);
          this.generatingPreviews.delete(replicaCacheKey);

          // Set up export tracking
          const existingExport = this.pendingExports.get(replicaKey);
          if (existingExport) {
            existingExport.pending++;
          } else {
            this.pendingExports.set(replicaKey, { pending: 1, completed: 0, sideJobIds: new Map() });
          }

          this.invalidatePrintExport(product, replica, sideNorm);

          // Generate preview + export with replica's color
          this.generatePreviewForCurrentSide(product, replica, sideNorm);
          this.exportCurrentSide(product, replica, sideNorm);

          // Mark replica as migrated too
          if (!replica.isMigrated) {
            replica.isMigrated = true;
          }

        });
      }
    }

    this.isFetchingEditorMetadata = false;

    // WI-1578: the locked/base variant's own refresh/export above has already been kicked off —
    // now share the exact same edited project with every OTHER selected color for this side. The
    // project id read here is whatever openEditorForSide/the DH editor session just left in
    // variantSideProjects for (product, variant, side) — the SAME id throughout the edit, since
    // Save & Close edits the existing project in place rather than creating a new one.
    if (applyToAllColors) {
      const totalColors = (product.selectedColors || []).length;
      const skipped = applyToAllColorsExcludeStyleIds.size;
      const colorCount = totalColors - skipped;
      const colorLabel = `${colorCount} color${colorCount !== 1 ? 's' : ''}`;
      // No full-page blocking loader — every propagated color's own Design Variants card now
      // shows its own progressive preview (fast placeholder, then the "Loading exported image"
      // refining badge) as renderSharedProjectPreview processes it, same reasoning as the
      // Replace Artwork fix: an overlay on top of that just hides the per-card feedback for no
      // reason.
      this.propagateEditedDesignToAllColors(product, variant, sideName, logoUrl, applyToAllColorsExcludeStyleIds, mediaPosition)
        .then(() => {
          const skippedNote = skipped > 0 ? ` (${skipped} color${skipped !== 1 ? 's' : ''} kept its own design)` : '';
          this.notification.success(`Design applied to ${colorLabel}${skippedNote}.`, 'Success');
        });
    }
  }

  /**
   * WI-1578: shares the project just edited via the locked/base color's "Edit Design" session with
   * every OTHER selected color for the same side — same technique propagateLogoToReplicas already
   * uses for InkSoft customProductImage replicas (point variantSideProjects at the shared project
   * id so each color's preview/export renders the identical graphic size/position/rotation/text
   * with its own garment photo), generalized here to every selected color on the product. Mirrors
   * applyImprintGraphicToVariants' wait-for-completion shape so the caller's loader/toast only
   * resolves once every color's preview + export has actually finished.
   *
   * excludeStyleIds: colors the user chose to leave alone via the "already has its own design"
   * confirm — Cancel there does NOT abort the whole edit, it only protects these specific colors;
   * every other color (including ones with no customization of their own) still gets the design.
   */
  private async propagateEditedDesignToAllColors(product: ProductColorVariant, sourceVariant: ColorVariant, sideName: string, logoUrl: string | null, excludeStyleIds: Set<number> = new Set(), mediaPosition: any = null): Promise<void> {
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const displayName = this.getDisplaySideName(sideNorm);
    const sourceKey = `${product.productId}_${sourceVariant.styleId}`;
    const sourceSideMapKey = `${sourceKey}_${sideNorm}`;
    // Fallback mirrors every other project-id resolution in this file (openEditorForSide,
    // editImprintDesignForAllColors, etc.) — openEditorForSide's reuse-existing branch resolves
    // via this same fallback but never writes the result back into variantSideProjects, so a
    // DB-loaded (edit mode) project for the locked color's side can be open and editable yet
    // absent from the map. Without the fallback here, that left this function silently returning
    // before propagating anything — the edit would apply to the locked color's own card but never
    // reach any other color.
    const sourceProjectId = this.variantSideProjects.get(sourceSideMapKey)
      || this.getSideProjectIdFromVariantSides(sourceVariant, sideNorm);
    if (!sourceProjectId) return;
    if (!this.variantSideProjects.has(sourceSideMapKey)) {
      this.variantSideProjects.set(sourceSideMapKey, sourceProjectId);
    }

    const others = (product.selectedColors || [])
      .filter(v => v !== sourceVariant && !excludeStyleIds.has(Number(v.styleId)));
    if (others.length === 0) return;

    // Pass A: synchronous, for EVERY propagated color at once — bookkeeping (sideLogos,
    // variantSideProjects, pendingExports) plus the fast, stateless, low-res placeholder. This is
    // cheap and doesn't touch the shared DH project's export-job endpoint at all, so there's no
    // reason to make any color wait in a queue just to get its FIRST bit of visual feedback — only
    // the slow, real export in Pass B needs to be bounded.
    for (const variant of others) {
      const variantKey = `${product.productId}_${variant.styleId}`;
      const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;

      if (logoUrl) {
        if (!variant.sideLogos) variant.sideLogos = {};
        variant.sideLogos[sideNorm] = logoUrl;
        variant.sideLogos[displayName] = logoUrl;
      }

      // Share the just-edited project directly — identical graphic + size/position/rotation/text
      // for every color; DH's product_image compositing swaps in each color's own garment photo.
      this.variantSideProjects.set(`${variantKey}_${sideNorm}`, sourceProjectId);

      // WI-1524: the designer just re-fit this side's frame on the source color — any stale
      // "stretched" flag on another color for the same side is equally stale now that they share it.
      this.clearPrintExportDistorted(product, variant, sideName);

      // Bump render generation so an in-flight (pre-edit) preview can't overwrite the fresh one
      // once it resolves — same guard refreshPreviewAfterEditorClose uses. Deliberately NOT
      // backgroundUrls.delete(cacheKey): this color's existing preview stays visible until the fast
      // placeholder (fired right below, for every color immediately) or the final export overwrites
      // it, instead of flashing to blank the moment propagation starts.
      this.previewGeneration.set(cacheKey, (this.previewGeneration.get(cacheKey) || 0) + 1);
      this.invalidatePrintExport(product, variant, sideName);
      // Mark as generating BEFORE firing the fast placeholder below, not after — fireFastPlaceholderPreview's
      // own async callback checks `generatingPreviews.has(cacheKey)` before writing its result (so a
      // late low-res arrival can't clobber a result the slow path already produced), and for colors
      // that haven't reached Pass B yet (still queued behind MAX_CONCURRENT_VARIANT_PREVIEWS other
      // colors), that check would otherwise fail — generatingPreviews was only ever added inside
      // renderSharedProjectPreview (Pass B), which hasn't run for this color yet — silently
      // discarding the fast placeholder result for every color beyond the first few queue slots.
      this.generatingPreviews.add(cacheKey);

      const existingExport = this.pendingExports.get(variantKey);
      if (existingExport) {
        existingExport.pending++;
      } else {
        this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
      }

      this.fireFastPlaceholderForSharedProject(product, variant, sideNorm, cacheKey, logoUrl, mediaPosition);
    }

    // Pass B: bounded concurrency for the SLOW, real work (export-job against the shared project).
    // Every propagated color shares the exact same DH project as the source color, and firing ALL
    // of their preview + print-export requests simultaneously (the original bug) made several
    // concurrent export-job calls hit that ONE project at once — confirmed live to sometimes come
    // back with no file for some colors even though nothing on our side actually errored. Going
    // fully sequential fixed that but made a borrowing color's wait grow with every other color
    // ahead of it. MAX_CONCURRENT_VARIANT_PREVIEWS in flight at once keeps contention low while
    // letting most colors' real exports finish close to as fast as the owner's does.
    await this.processWithConcurrencyLimit(others, this.MAX_CONCURRENT_VARIANT_PREVIEWS, async (variant) => {
      const variantKey = `${product.productId}_${variant.styleId}`;
      const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;
      const startCompleted = this.pendingExports.get(variantKey)?.completed || 0;

      // Deliberately NOT generatePreviewForCurrentSide(): its sameLogo check (variant.sideLogos set
      // in Pass A => not "sameLogo") would send this variant through createFreshProjectAndPreview,
      // which overwrites variantSideProjects with a BRAND NEW project a moment later — silently
      // undoing the shared assignment and defeating the entire point of this method (the very bug
      // it exists to avoid — "don't create a project per color, reuse the locked one").
      // renderSharedProjectPreview calls refreshPreviewViaExport directly with sourceProjectId, the
      // same way openEditorForSide's own reuse branch does, guaranteeing every propagated color
      // renders from — and only from — the one edited project.
      this.renderSharedProjectPreview(product, variant, sideNorm, displayName, sourceProjectId, cacheKey);
      this.exportCurrentSide(product, variant, sideNorm);

      // Wait for THIS color's preview + print-export to actually settle before this worker slot
      // picks up the next color — see the comment above.
      await this.waitUntil(() => {
        const tracking = this.pendingExports.get(variantKey);
        const wanted = startCompleted + 1;
        if (!tracking || tracking.completed < wanted) return false;
        return !this.generatingPreviews.has(cacheKey) && !this.uploadingBackgrounds.has(cacheKey);
      });
    });
  }

  /**
   * WI-1578: render a color's preview from an EXPLICITLY-KNOWN, already-shared project — bypassing
   * generatePreviewForCurrentSide/generateSidePreview's own reuse-vs-create decision entirely, since
   * that decision (keyed off variant.sideLogos) would wrongly treat this color as needing its own
   * fresh project (see the call site comment in propagateEditedDesignToAllColors). Resolves the same
   * product_image_url / zone dimensions-offset refreshPreviewAfterEditorClose resolves for the
   * single-variant case, then hands off to refreshPreviewViaExport directly.
   */
  private resolveSharedPreviewInputs(product: ProductColorVariant, variant: ColorVariant, sideNorm: string, cacheKey: string): { productImageUrl: string; zoneDimensions: string; zoneOffset: string; templateId?: number } | null {
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) {
      this.backgroundUrls.set(cacheKey, '');
      return null;
    }

    const sideConfig = this.getSideConfigForCurrentSide(sideNorm);
    if (!sideConfig) {
      this.backgroundUrls.set(cacheKey, '');
      return null;
    }

    let productImageUrl = variantStyle[sideConfig.imageField]
      || variantStyle.ImageFilePath_Front
      || variantStyle.ImageFilePath_Back
      || variantStyle.ImageFilePath_SleeveLeft
      || variantStyle.ImageFilePath_SleeveRight
      || (product as any).productImage || '';
    if (productImageUrl && !productImageUrl.startsWith('http')) {
      productImageUrl = this.joinUrl(this.imgBaseURL, productImageUrl);
    }
    if (!productImageUrl || !/^https?:\/\//i.test(productImageUrl)) {
      console.error(`❌ resolveSharedPreviewInputs: no absolute product_image_url for ${cacheKey}`);
      this.backgroundUrls.set(cacheKey, '');
      this.checkAndTriggerSave();
      return null;
    }

    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    const sideInfo = this.getSideInfoForSide(product.productId, sideNorm);
    if (sideInfo?.printPlacement) {
      try {
        const pp = typeof sideInfo.printPlacement === 'string' ? JSON.parse(sideInfo.printPlacement) : sideInfo.printPlacement;
        if (pp.Width && pp.Height) zoneDimensions = `${pp.Width}x${pp.Height}`;
        if (pp.X !== undefined && pp.Y !== undefined) zoneOffset = `${pp.X}x${pp.Y}`;
      } catch (e) {
      }
    }

    return { productImageUrl, zoneDimensions, zoneOffset, templateId: sideInfo?.templateId };
  }

  /**
   * Fires ONLY the fast, stateless, low-res placeholder for a propagated color — safe to call
   * immediately, synchronously, for every color in a batch, unlike renderSharedProjectPreview below
   * (which must stay bounded/queued per-project to avoid concurrent export-job calls hitting DH).
   * Lets every color's card show SOMETHING right away instead of some of them sitting on a full
   * spinner until their turn comes up in the bounded queue.
   */
  private fireFastPlaceholderForSharedProject(product: ProductColorVariant, variant: ColorVariant, sideNorm: string, cacheKey: string, logoUrl: string | null, mediaPosition: any): void {
    const inputs = this.resolveSharedPreviewInputs(product, variant, sideNorm, cacheKey);
    if (!inputs?.templateId) return;
    this.fireFastPlaceholderPreview(inputs.templateId, this.buildLogoCustomization(logoUrl || '', mediaPosition), inputs.productImageUrl, inputs.zoneDimensions, inputs.zoneOffset, mediaPosition, cacheKey);
  }

  private renderSharedProjectPreview(product: ProductColorVariant, variant: ColorVariant, sideNorm: string, displayName: string, projectId: string, cacheKey: string): void {
    const inputs = this.resolveSharedPreviewInputs(product, variant, sideNorm, cacheKey);
    if (!inputs) return;

    this.generatingPreviews.add(cacheKey);
    this.pendingUploads++;
    this.refreshPreviewViaExport(projectId, inputs.productImageUrl, inputs.zoneDimensions, inputs.zoneOffset, cacheKey, product, variant, displayName, sideNorm);
  }

  /**
   * Refresh the side preview after editor close by regenerating with updated logo URL and media_position.
   * Uses storeTemplateCustomizationObject + customizations_hash for accurate preview.
   * When hasTextElements is true, routes to refreshPreviewViaExport instead — the SDK's
   * getVariableTemplatePreviewURL cannot render text elements, so the export API is used.
   */
  refreshPreviewAfterEditorClose(product: ProductColorVariant, variant: ColorVariant, sideName: string, mediaPosition: any, editorLogoUrl?: string | null, hasTextElements: boolean = false): void {
    // The design (incl. anything uploaded in-editor) may have changed: any print export tracked so
    // far shows the pre-edit design. Done before the early returns below — if no re-export can be
    // started, the save-time check refuses to fall back to the stale file.
    this.invalidatePrintExport(product, variant, sideName, true);
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) return;

    const sideConfig = this.getSideConfigForCurrentSide(sideName);
    if (!sideConfig) return;

    let productImageUrl = variantStyle[sideConfig.imageField] || '';
    if (productImageUrl && !productImageUrl.startsWith('http')) {
      productImageUrl = this.joinUrl(this.imgBaseURL, productImageUrl);
    }

    // Resolve the correct logo URL: editorLogoUrl > sideLogos > edit-mode customizations > designData
    let logoUrl = editorLogoUrl || '';
    if (!logoUrl) {
      // Check side-specific custom logo (same logic as getSideLogoUrl)
      const sideNameVariations = [sideName, sideConfig.displayName, sideName.replace(/\s+/g, '_')];
      for (const nameVariation of sideNameVariations) {
        if (variant.sideLogos && variant.sideLogos[nameVariation]) {
          logoUrl = variant.sideLogos[nameVariation];
          break;
        }
      }
    }
    if (!logoUrl) {
      // In edit mode, extract from saved Customizations per side
      const sideLogoFromCust = this.getLogoUrlFromSideCustomizations(variant, sideName);
      if (sideLogoFromCust) {
        logoUrl = sideLogoFromCust;
      }
    }
    if (!logoUrl) {
      // Fall back to variant-level custom logo
      if (variant.variantDesignUrl && !variant.useDefaultDesign) {
        logoUrl = variant.variantDesignUrl;
      }
    }
    if (!logoUrl) {
      // Final fallback to design data
      logoUrl = this.designData?.designUrl || this.designData?.previewUrl || '';
    }

    // Find templateId + THIS side's PrintPlacement (canonical match)
    const sideInfo = this.getSideInfoForSide(product.productId, sideName);
    if (!sideInfo) return;

    // Parse PrintPlacement
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    if (sideInfo.printPlacement) {
      try {
        const pp = typeof sideInfo.printPlacement === 'string'
          ? JSON.parse(sideInfo.printPlacement) : sideInfo.printPlacement;
        if (pp.Width && pp.Height) zoneDimensions = `${pp.Width}x${pp.Height}`;
        if (pp.X !== undefined && pp.Y !== undefined) zoneOffset = `${pp.X}x${pp.Y}`;
      } catch (e) {
      }
    }

    const displayName = sideConfig.displayName;
    const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;

    // Bump the render generation: this is now the newest render for this key, so any fresh
    // preview still in flight in generateSidePreview must not write its (pre-edit) result over
    // ours when it eventually resolves. Deliberately NOT backgroundUrls.delete(cacheKey) — the
    // pre-edit preview stays visible (refining badge overlaid, via generatingPreviews below) until
    // the fast placeholder / final export actually replaces it, instead of flashing to blank.
    this.previewGeneration.set(cacheKey, (this.previewGeneration.get(cacheKey) || 0) + 1);
    this.generatingPreviews.add(cacheKey);
    this.pendingUploads++;

    // Progressive preview, same as generateSidePreview: fire the fast, stateless, projectless
    // preview immediately so the card shows SOMETHING right after Save & Close instead of staying
    // on a full spinner for the whole slow export-job pipeline below. Purely additive — never
    // touches generatingPreviews/pendingUploads, so the save-flow gate and WI-1524's failure
    // tracking still depend entirely on the slow path's own completion.
    this.fireFastPlaceholderPreview(sideInfo.templateId, this.buildLogoCustomization(logoUrl, mediaPosition), productImageUrl, zoneDimensions, zoneOffset, mediaPosition, cacheKey);

    // When text elements are present, the SDK cannot render them — use export API instead.
    if (hasTextElements) {
      const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
      const variantKey = `${product.productId}_${variant.styleId}`;
      const projectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`) || variant.projectId || '';
      this.refreshPreviewViaExport(projectId, productImageUrl, zoneDimensions, zoneOffset, cacheKey, product, variant, displayName, sideName);
    } else {
      // Build customization object with correct logo URL + media_position.
      // Pass INLINE (not via storeTemplateCustomizationObject + customizations_hash): the hash
      // indirection drops the logo's media_position, which made this preview disagree with the
      // inline-path previews (generatePreviewsForVariant) and the export. Inline keeps them all
      // driven by the same media_position, so preview and export match.
      const customizationClasses: any = {
        logo: {
          url: logoUrl
        }
      };
      if (mediaPosition) {
        customizationClasses.logo.media_position = mediaPosition;
      }
      // No editor placement → top-anchor via product_image_canvas_zone_vertical_align:"top" would
      // apply to the old SDK-preview call; the export path top-anchors via media_position instead
      // (createSideCustomizations) since export-job never sees vertical_align.

      // Same reasoning as generateSidePreview: route through a fresh per-side DH project + the
      // export-job API instead of the stateless SDK preview, so this persisted thumbnail gets the
      // export path's independent (zone-safe) resolution knob instead of the fixed 300/500 canvas.
      const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
      const variantKey = `${product.productId}_${variant.styleId}`;
      const projectTitle = this.generateRandomProjectTitle();

      (window as any).DSHDLib.createProject(
        {
          template_id: sideInfo.templateId,
          project_title: toDesignHuddleProjectTitle(projectTitle),
          customizations: { classes: customizationClasses }
        },
        (error: any, projectData: any) => {
          if (error || !projectData?.project_id) {
            console.error('❌ createProject failed during preview refresh:', error);
            this.generatingPreviews.delete(cacheKey);
            this.pendingUploads--;
            this.backgroundUrls.set(cacheKey, '');
            this.checkAndTriggerSave();
            return;
          }

          const newProjectId = projectData.project_id;
          this.variantSideProjects.set(`${variantKey}_${sideNorm}`, newProjectId);
          this.refreshPreviewViaExport(newProjectId, productImageUrl, zoneDimensions, zoneOffset, cacheKey, product, variant, displayName, sideName);
        }
      );
    }

    // Also re-export the side since design may have changed
    const variantKey = `${product.productId}_${variant.styleId}`;
    const existingExport = this.pendingExports.get(variantKey);
    if (existingExport) {
      existingExport.pending++;
    } else {
      this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
    }
    this.exportCurrentSide(product, variant, sideName);
  }

  /**
   * Check whether a raw getProjectData response contains any element with type === "text".
   * Used to decide between SDK-preview path and export-API path after editor close.
   */
  private projectDataHasTextElements(response: any): boolean {
    const root = response?.pages ? response : (response?.data || response);
    const pages: any[] = root?.pages || [];
    for (const page of pages) {
      const elements = page.elements || {};
      for (const elementId of Object.keys(elements)) {
        if (elements[elementId]?.type === 'text') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * WI-1521 fix #3: exponential backoff delay for export-job polling (preview poll in
   * refreshPreviewViaExport + print poll in pollExportJobProgress). Both used to poll at a fixed
   * 2s interval — App Insights shows GetExportJobProgress p95 ~864ms, so most jobs were finishing
   * well inside that interval and just waiting out the rest of it. Starting fast (~500ms) and
   * backing off catches quick jobs almost immediately while still not hammering the API for a
   * slow one. `attempt` is 1-based (the value right before scheduling the NEXT poll). This only
   * changes WHEN poll() re-invokes itself — the terminal markExportCompleted()/backgroundUrls
   * calls in both functions are untouched, so WI-1524's printExportFailures tracking (which reads
   * state at those terminal calls) is unaffected.
   */
  private nextPollDelayMs(attempt: number): number {
    const initialDelayMs = 500;
    const maxDelayMs = 4000;
    const backoffFactor = 1.6;
    const delay = initialDelayMs * Math.pow(backoffFactor, Math.max(0, attempt - 1));
    return Math.min(delay, maxDelayMs);
  }

  /**
   * Refresh the post-editor preview by creating an export job with the product_image overlay.
   * Used when the project contains text elements, which the SDK cannot render client-side.
   * Polls until the export completes, then uses the backend-uploaded Azure URL as the preview.
   */
  private refreshPreviewViaExport(
    projectId: string,
    productImageUrl: string,
    zoneDimensions: string,
    zoneOffset: string,
    cacheKey: string,
    product: ProductColorVariant,
    variant: ColorVariant,
    displayName: string,
    sideName: string
  ): void {
    if (!projectId) {
      this.generatingPreviews.delete(cacheKey);
      this.pendingUploads--;
      this.backgroundUrls.set(cacheKey, '');
      this.checkAndTriggerSave();
      return;
    }

    const [zoneW, zoneH] = zoneDimensions.split('x').map(Number);
    const [offX, offY] = zoneOffset.split('x').map(Number);

    const fileName = this.buildAzureFileName(product.productId, variant.styleId, sideName) || `${projectId}_${sideName}_preview`;

    const exportUrl = `${environment.baseUrl}/api/designhuddle/projects/${projectId}/export-job`;

    // The project already contains the latest design state from the editor — no customizations
    // override needed. We only need product_image so DH composites the design onto the product.
    // Flatten the transparent product image onto white first — see ProductImageMatteService
    // for why (DH has no background/matte param of its own, confirmed against its API docs).
    this.productImageMatteService.getFlattenedProductImageUrl(productImageUrl).pipe(
      switchMap(flattenedProductImageUrl => this.http.post<any>(exportUrl, {
        format: 'png',
        fileName: fileName,
        // width (top-level, 1600 — was 800) is the FINAL OUTPUT canvas resolution DH renders the
        // whole composite at; it does NOT share a coordinate space with product_image.width or
        // canvas_zone, so bumping it is safe and gives every print zone more absolute pixels
        // regardless of its physical size — this is the correct place to fix blur on small zones.
        // product_image.width stays 500 (unchanged): canvas_zone.dimensions/offset below are pixel
        // coordinates calibrated against THAT value, and changing it would shrink/shift every
        // saved print-zone placement relative to the (now larger) garment photo.
        width: 1600,
        product_image: {
          url: flattenedProductImageUrl,
          width: 500,
          canvas_zone: {
            dimensions: `${zoneW}x${zoneH}`,
            offset: `${offX}x${offY}`
          }
        }
      })),
      timeout(30000),
      catchError(err => {
        console.error('❌ Export-based preview failed for', cacheKey, err);
        return of(null);
      })
    )
      .subscribe({
        next: (response: any) => {
          const jobId = response?.data?.job_id || '';
          if (!jobId) {
            this.generatingPreviews.delete(cacheKey);
            this.pendingUploads--;
            this.backgroundUrls.set(cacheKey, '');
            this.checkAndTriggerSave();
            return;
          }

          // Poll the export job until azure_url is available (backend uploads it to Azure on
          // completion). WI-1521 fix #3: exponential backoff (nextPollDelayMs, starts ~500ms)
          // instead of a fixed 2s interval, bounded by maxTotalWaitMs (~ same overall ceiling as
          // the previous 30 attempts × 2000ms) so it still eventually gives up.
          const pollUrl = `${environment.baseUrl}/api/designhuddle/export-job/${projectId}/${jobId}`;
          let attempts = 0;
          const pollStartTime = Date.now();
          const maxTotalWaitMs = 60000;

          const poll = () => {
            attempts++;
            this.http.get<any>(pollUrl).pipe(timeout(5000)).subscribe({
              next: (res: any) => {
                const data = res?.data || res;
                const isCompleted = data?.completed === true || data?.status === 'completed';

                if (isCompleted) {
                  this.generatingPreviews.delete(cacheKey);
                  // Always route through uploadPreviewToAzure — even when the backend already
                  // uploaded a raw azure_url — so this export-API preview passes through the same
                  // fixed-square normalization as the SDK-preview path (see
                  // ProductPreviewNormalizeService). Using a backend-provided azure_url directly
                  // here would keep DH's native, per-template aspect ratio and reintroduce the
                  // same Shopify-listing size inconsistency the normalization step exists to fix.
                  const sourceUrl = data?.azure_url || data?.thumbnail_url || data?.output_url || data?.download_url || data?.url || '';
                  if (sourceUrl) {
                    this.uploadPreviewToAzure(sourceUrl, cacheKey, product.productId, variant.styleId, displayName);
                  } else {
                    this.pendingUploads--;
                    this.backgroundUrls.set(cacheKey, '');
                    this.checkAndTriggerSave();
                  }
                } else if (Date.now() - pollStartTime < maxTotalWaitMs) {
                  setTimeout(poll, this.nextPollDelayMs(attempts));
                } else {
                  this.generatingPreviews.delete(cacheKey);
                  this.pendingUploads--;
                  this.backgroundUrls.set(cacheKey, '');
                  this.checkAndTriggerSave();
                }
              },
              error: () => {
                if (Date.now() - pollStartTime < maxTotalWaitMs) {
                  setTimeout(poll, this.nextPollDelayMs(attempts));
                } else {
                  this.generatingPreviews.delete(cacheKey);
                  this.pendingUploads--;
                  this.backgroundUrls.set(cacheKey, '');
                  this.checkAndTriggerSave();
                }
              }
            });
          };

          poll();
        }
      });
  }

  /**
   * Promise wrapper around DSHDLib.createProject()
   */
  createProjectForSide(templateId: number, title: string, customizations: any): Promise<string> {
    return new Promise((resolve) => {
      if (typeof (window as any).DSHDLib === 'undefined') {
        console.error('❌ DesignHuddle SDK not available');
        resolve('');
        return;
      }

    (window as any).DSHDLib.createProject(
        {
          template_id: templateId,
          project_title: toDesignHuddleProjectTitle(title),
          customizations: customizations
        },
        (error: any, projectData: any) => {
          if (error) {
            console.error('❌ Error creating project:', error, '| Params:', { template_id: templateId, customizations });
            resolve('');
          } else {
            // Created under the current guest session → safe to open directly later.
            if (projectData.project_id) this.guestOwnedProjectIds.add(projectData.project_id);
            resolve(projectData.project_id);
          }
        }
      );
    });
  }

  /**
   * Get available sides excluding the currently displayed side (for dropdown)
   */
  getOtherSides(productId: string): { name: string, displayName: string }[] {
    const sides = this.getProductAvailableSides(productId);
    const currentSide = this.getProductCurrentSide(productId);
    return sides.filter(s => s.name !== currentSide);
  }

  /**
   * Get sides that can still be added to a specific variant (sides not yet active for this variant)
   */
  getAvailableSidesForVariant(product: ProductColorVariant, variant: ColorVariant): { name: string, displayName: string }[] {
    const variantKey = `${product.productId}_${variant.styleId}`;
    const currentSide = this.getProductCurrentSide(product.productId);
    const activeSides = this.variantActiveSides.get(variantKey) || [currentSide];
    // variantActiveSides stores whatever raw spelling the side arrived under (e.g. "sleeveleft"),
    // while getProductAvailableSides().name is always the canonicalized left_sleeve/right_sleeve
    // form — a raw string comparison never matched, so a side already shown as a Design Variant
    // card (e.g. "Left Sleeve") still showed up as addable in its own "+ Add Side" dropdown.
    const activeCanon = new Set(activeSides.map(s => this.canonicalSide(s)));
    const productSides = this.getProductAvailableSides(product.productId);
    return productSides.filter(s => !activeCanon.has(this.canonicalSide(s.name)));
  }

  /**
   * Add a new side to a specific variant. Creates a project for the side's template,
   * generates preview, and exports.
   */
  /**
   * Public entry point for the "+ Add Side" control. Propagates the added imprint location to
   * EVERY color variant of the product so all variants stay in parity — each variant persists the
   * side in its own variant.sides[] (so it round-trips on "Back to Color Selection") instead of
   * only the one the user acted on. Each variant resolves its own logo inside
   * addSideToSingleVariant; the per-variant guards make re-invoking on a variant that already has
   * the side a no-op.
   */
  addSideToVariant(product: ProductColorVariant, variant: ColorVariant, newSideName: string): void {
    if (!newSideName) return;
    const variants = (product.selectedColors && product.selectedColors.length > 0)
      ? product.selectedColors
      : [variant];
    variants.forEach(v => this.addSideToSingleVariant(product, v, newSideName));
  }

  /** Adds a single imprint location to ONE variant. */
  private addSideToSingleVariant(product: ProductColorVariant, variant: ColorVariant, newSideName: string): void {
    if (!newSideName) return;

    const variantKey = `${product.productId}_${variant.styleId}`;
    const currentSide = this.getProductCurrentSide(product.productId);
    const activeSides = this.variantActiveSides.get(variantKey) || [currentSide];

    // Already has this side
    // NOT a plain `activeSides.includes(newSideName)` check — in the create flow, variant.sides
    // (and so variantActiveSides, restored from it) can already list every side the MASTER PRODUCT
    // supports from the very first load, not just sides this variant has genuinely been
    // customized for (see applyImprintGraphicToVariants' alreadyHasSide fix for the full trace).
    // A plain includes() check here would silently no-op this entire call — no project created, no
    // export, nothing — for a side that was never actually added, simply because its name already
    // appeared in that stale list. Checking for a REAL registered project is immune to that.
    if (this.variantSideProjects.get(`${variantKey}_${newSideName}`)) return;

    const newSide = this.getSideInfoForSide(product.productId, newSideName);
    if (!newSide) return;


    // Add to active sides
    activeSides.push(newSideName);
    this.variantActiveSides.set(variantKey, activeSides);

    // Also push to variant.sides so prepareFinalPayloadAndSave includes it in the payload
    if (!variant.sides) {
      variant.sides = [];
    }
    const alreadyInSides = variant.sides.some((s: any) => {
      const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === newSideName;
    });
    if (!alreadyInSides) {
      variant.sides.push({
        SideName: newSideName,
        PrintPlacement: newSide.printPlacement || '',
        ThumbnailUrl: null,
        ThumbnailRelativeUrl: null,
        Customizations: null,
        ElementPlacement: null,
        ZonePlacement: null
      });
    }

    // Set up export tracking for this new side
    const existingExport = this.pendingExports.get(variantKey);
    if (existingExport) {
      existingExport.pending++;
    } else {
      this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
    }

    // Resolve logo URL: check variant's side logo, locked variant's side logo, then default designData
    let logoUrl = this.designData?.designUrl || this.designData?.previewUrl || '';
    // Check variant's own side-specific logo
    const sideNameVariations = [newSideName, this.getDisplaySideName(newSideName)];
    for (const nameVariation of sideNameVariations) {
      if (variant.sideLogos && variant.sideLogos[nameVariation]) {
        logoUrl = variant.sideLogos[nameVariation];
        break;
      }
    }
    // Extract logo from variant's own saved Customizations for this side, if any. WI-1522: no
    // longer gated on isEditMode — in create mode variant.sides[thisSide].Customizations is simply
    // null (addSideToSingleVariant seeds it that way just above when the side is first added), so
    // this was already a no-op in create mode; ungating it just makes it consistent with the
    // locked-variant fallback below instead of looking like a deliberately-skipped step.
    if (logoUrl === (this.designData?.designUrl || this.designData?.previewUrl || '')) {
      const sideLogoFromCust = this.getLogoUrlFromSideCustomizations(variant, newSideName);
      if (sideLogoFromCust) {
        logoUrl = sideLogoFromCust;
      }
    }
    // Fallback to locked variant's per-side logo if still default.
    // WI-1522 fix #2: this used to be gated on isEditMode, so adding a secondary imprint side to a
    // color in CREATE mode (e.g. via "+ Add Side" on a non-locked color, or the bulk imprint tool)
    // kept the primary logo instead of inheriting the locked color's current design for that side.
    // getLockedVariantLogoForSide reads the locked variant's LIVE state, so a later edit to the
    // locked color's side is picked up here too, not a frozen snapshot from whenever this ran.
    const lockedVariant = product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId));
    if (logoUrl === (this.designData?.designUrl || this.designData?.previewUrl || '')) {
      const lockedLogo = this.getLockedVariantLogoForSide(lockedVariant, newSideName);
      if (lockedLogo) {
        logoUrl = lockedLogo;
      }
    }
    const mediaPosition = this.getMediaPositionForSide(variant, newSideName);


    // Check if this side already has a project from the editor (variant.sides data)
    const existingSideProjectId = this.getSideProjectIdFromVariantSides(variant, newSideName);
    if (existingSideProjectId) {
      this.variantSideProjects.set(`${variantKey}_${newSideName}`, existingSideProjectId);
      // Clear whatever frame is currently cached on this variant.sides entry BEFORE exporting —
      // isSideFrameDistorted's staleness guard (elementPlacementProjectId vs side.ProjectId) can't
      // catch a stale frame here, because side.ProjectId IS existingSideProjectId (that's literally
      // where it was just read from), so currentProjectId/frameProjectId trivially "match" even
      // though this ElementPlacement was never actually verified as fit to THIS variant's current
      // logo for this side — e.g. the locked variant's un-cloned `sides: product.Sides` reference
      // (see loadSelectedProducts) can carry a template-default frame for a side never customized
      // yet. Nulling it reproduces the same fail-safe "no frame yet" state a genuinely new side gets
      // (the push below, `ElementPlacement: null`), so the check skips (not distorted) until the
      // fetchAndStoreMetadata call just below lands with the real, freshly-fit frame — fixing the
      // false "Logo is stretched" warning reported on Front/Back when added after a side (e.g. a
      // Sleeve) already populated variantSideProjects for this variant, even though the real export
      // is correct.
      const staleSide = this.matchSideByName(variant.sides || [], newSideName);
      if (staleSide) {
        staleSide.elementPlacement = null;
        staleSide.ElementPlacement = null;
        staleSide.zonePlacement = null;
        staleSide.ZonePlacement = null;
        staleSide.elementPlacementProjectId = null;
      }
      this.fetchAndStoreMetadata(existingSideProjectId, variant, newSideName);
      this.generatePreviewForCurrentSide(product, variant, newSideName);
      this.exportCurrentSide(product, variant, newSideName);
      return;
    }

    // Check if ANY already-selected color (not just the locked/default color) already has a
    // project for this NEW side — see findSideProjectOwner's doc comment. This is the same
    // "only checked the locked color" bug already fixed in toggleColorSelection/onSideChange/
    // ensurePendingSideProjectsBeforeSave/generateSidePreview, just reached via a different
    // entry point here: addSideToVariant's `variants.forEach` (above) calls this once per
    // already-selected color, synchronously, with nothing awaited in between. The side is brand
    // new to every one of them, so the locked color almost never already has it either — meaning
    // every non-locked variant used to independently create its own duplicate project, exactly
    // the "select multiple colors, add a side, multiple projectIds created" bug reported after
    // fixing the single-color-add case.
    // WI-1522: compare the locked variant's CURRENT logo against the FINAL resolved logoUrl above
    // (post locked-variant fallback), not the raw sideLogos entry — otherwise a plain new color
    // (no sideLogos override of its own, now correctly falling back to the locked variant's logo)
    // would still look like "different logo" here and get its own redundant DH project instead of
    // reusing the shared one.
    const lockedLogoUrl = this.getLockedVariantLogoForSide(lockedVariant, newSideName);
    const sameLogo = lockedLogoUrl === logoUrl;
    const sideKey = `${product.productId}_${newSideName}`;
    const inFlight = sameLogo ? this.pendingSideProjectCreations.get(sideKey) : undefined;
    const already = sameLogo ? this.getSharedSideProjectId(product, newSideName) : null;

    const createOwnProject = () => {
      // Guard against invalid template_id
      if (!newSide.templateId || isNaN(newSide.templateId)) {
        console.error(`❌ Invalid templateId (${newSide.templateId}) for side ${newSideName} — cannot create project`);
        this.markExportCompleted(variantKey, newSideName, '');
        return;
      }
      // Template or logo changed — create new project, increment DesignId (seeded top-anchored).
      // Mark in-flight SYNCHRONOUSLY (before the first await) so a sibling variant processed later
      // in the SAME variants.forEach pass sees it and borrows instead of racing to create its own.
      this.incrementDesignIdOffset(product.productId, variant.styleId);
      const projectTitle = this.generateRandomProjectTitle();
      const creation: Promise<string> = this.buildSeedCustomization(logoUrl, product.productId, newSideName, mediaPosition)
        .then(customizationObject => this.createProjectForSide(newSide.templateId, projectTitle, customizationObject));
      if (sameLogo) {
        this.pendingSideProjectCreations.set(sideKey, creation);
      }
      creation.then(projectId => {
        if (sameLogo) this.pendingSideProjectCreations.delete(sideKey);
        if (projectId) {
          this.variantSideProjects.set(`${variantKey}_${newSideName}`, projectId);
          // Hit metadata API to get elementPlacement/zonePlacement
          this.fetchAndStoreMetadata(projectId, variant, newSideName);
          this.generatePreviewForCurrentSide(product, variant, newSideName);
          this.exportCurrentSide(product, variant, newSideName);
        } else {
          this.markExportCompleted(variantKey, newSideName, '');
        }
      }).catch(err => {
        if (sameLogo) this.pendingSideProjectCreations.delete(sideKey);
        console.error(`❌ Error adding side ${newSideName}:`, err);
        this.markExportCompleted(variantKey, newSideName, '');
      });
    };

    if (inFlight || already) {
      const waitFor = inFlight || Promise.resolve(already as string);
      waitFor.then(async sharedProjectId => {
        if (!sharedProjectId) {
          // The owner's creation failed — nothing to borrow; become the new owner instead.
          createOwnProject();
          return;
        }
        // Reuse an already-created project for this side — carry over JobId. Do NOT set
        // variantSideProjects for this non-owning variant: leaving it unset ensures
        // openEditorForSide() hits the clone branch on first open instead of directly reusing the
        // shared project (which would let one color's edit corrupt every color borrowing it) —
        // same invariant every other reuse branch in this file already follows.
        const ownerKey = this.findSideProjectOwnerKey(product, newSideName);

        // `sharedProjectId` only means the OWNER's DH PROJECT has been created — this variant's
        // own PREVIEW can render off that project right away (it only needs the project to exist,
        // not the owner's print export to finish), so fire it here instead of after the export-tail
        // wait below. Previously this call sat after that wait, so every borrowing color's preview
        // visibly lagged behind the owner's (primary color's) preview by however long the owner's
        // full export job took — the owner started rendering immediately after its own project
        // creation, while every other color sat blocked on the owner's entire export pipeline
        // before its own preview even began.
        this.generatePreviewForCurrentSide(product, variant, newSideName);

        // The owner's own print EXPORT for this side (a separate, subsequent exportCurrentSide
        // call-chain: create export-job -> poll -> download url) hasn't necessarily even started
        // yet, let alone finished. Reading the owner's sideDownloadUrls/sideJobIds right here,
        // immediately, found nothing (too early) and called markExportCompleted with an empty
        // jobId — wrongly flagging "no print file for this side" before the owner's export ever got
        // a chance to run. Wait until the owner's export for THIS side has actually concluded
        // (succeeded — sideDownloadUrls has an entry — or failed — printExportFailures is set)
        // before copying its result, bounded the same way every other export wait in this file is
        // so a stuck owner can't hang forever. This wait now runs IN PARALLEL with the preview
        // above, not before it.
        const ownerFailureKey = `${ownerKey}_${this.canonicalSide(newSideName)}`;
        await this.waitUntil(() => {
          const t = this.pendingExports.get(ownerKey);
          return !!t?.sideDownloadUrls?.get(newSideName) || this.printExportFailures.has(ownerFailureKey);
        }, 45000);

        const ownerExportTracking = this.pendingExports.get(ownerKey);
        const ownerJobId = ownerExportTracking?.sideJobIds?.get(newSideName) || '';
        // Copy the OWNER's download URL into THIS variant's own tracking before calling
        // markExportCompleted below — markExportCompleted checks the CALLING variant's own
        // sideDownloadUrls (this.pendingExports.get(variantKey)), never the owner's. Without this,
        // a borrowing variant's tracking never learns the real print file exists and gets flagged
        // "no print file for this side" even though the owner's export genuinely succeeded — same
        // owner-download-URL copy every other reuse branch in this file already does (see
        // onSideChange, toggleColorSelection's locked-color reuse, ensurePendingSideProjectsBeforeSave).
        const ownerDownloadUrl = ownerExportTracking?.sideDownloadUrls?.get(newSideName) || '';
        if (ownerDownloadUrl) {
          const ownTracking = this.pendingExports.get(variantKey);
          if (ownTracking) {
            if (!ownTracking.sideDownloadUrls) {
              ownTracking.sideDownloadUrls = new Map<string, string>();
            }
            ownTracking.sideDownloadUrls.set(newSideName, ownerDownloadUrl);
          }
        }
        // Preview was already fired above (in parallel with this wait) — not repeated here.
        this.markExportCompleted(variantKey, newSideName, ownerJobId);
      });
    } else {
      createOwnProject();
    }
  }

  // ==================== End Single-Side Flow Methods ====================

  /**
   * Convert side name to display format (e.g., 'sleeve_left' -> 'Left Sleeve')
   */
  getDisplaySideName(sideName: string): string {
    const sideNameLower = sideName.toLowerCase().replace(/\s+/g, '_');

    if (sideNameLower.includes('back')) {
      return 'Back';
    } else if (sideNameLower.includes('sleeve_left') || sideNameLower.includes('left')) {
      return 'Left';
    } else if (sideNameLower.includes('sleeve_right') || sideNameLower.includes('right')) {
      return 'Right';
    } else if (sideNameLower.includes('front')) {
      return 'Front';
    }

    return sideName; // Return original if no match
  }

  /**
   * Get the preview URL for a specific side of a variant
   * Returns the Azure uploaded URL if available, otherwise empty string (loading)
   */
  getVariantSidePreviewUrl(product: ProductColorVariant, variant: ColorVariant, sideName: string): string {
    const cacheKey = `${product.productId}_${variant.styleId}_${sideName}`;

    // Return generated/uploaded preview URL if available
    if (this.backgroundUrls.has(cacheKey)) {
      return this.backgroundUrls.get(cacheKey)!;
    }

    // While generating, show the plain product side image as placeholder (not blank)
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (variantStyle) {
      const sideConfig = this.getSideConfigForCurrentSide(
        sideName.toLowerCase().replace(/\s+/g, '_')
      );
      let sideImage = sideConfig ? variantStyle[sideConfig.imageField] : '';
      if (!sideImage) {
        sideImage = variantStyle.ImageFilePath_Front || variantStyle.ImageFilePath_Back || '';
      }
      if (sideImage) {
        return sideImage.startsWith('http') ? sideImage : this.joinUrl(this.imgBaseURL, sideImage);
      }
    }

    return '';
  }

  /**
   * Check if a preview is currently being generated or uploaded.
   *
   * Previous impl added `|| !this.backgroundUrls.has(cacheKey)` as a third OR clause, which
   * conflated "no preview yet" with "actively loading". Any early-return in the preview
   * pipeline (missing projectId, no project details yet, no pageId, no image for this side,
   * DSHDLib SDK not loaded, invalid product_image_url, etc.) meant backgroundUrls was never
   * set and the per-side spinner spun forever. Now "loading" strictly means active work.
   */
  isPreviewLoading(product: ProductColorVariant, variant: ColorVariant, sideName: string): boolean {
    const cacheKey = `${product.productId}_${variant.styleId}_${sideName}`;
    return this.generatingPreviews.has(cacheKey) || this.uploadingBackgrounds.has(cacheKey);
  }

  /**
   * WI-1524 fix #1: true when this side's preview finished (not loading) but ended up with an
   * empty backgroundUrls entry — every failure branch in generateSidePreview/refreshPreviewViaExport
   * /uploadPreviewToAzure sets `backgroundUrls.set(cacheKey, '')` on failure (a "no image at all"
   * skip never touches backgroundUrls at all, so `.get(cacheKey) === ''` only ever means "we tried
   * and it failed", not "nothing to show"). Drives a visible per-side error + Retry instead of a
   * silently-blank thumbnail.
   */
  hasPreviewFailed(product: ProductColorVariant, variant: ColorVariant, sideName: string): boolean {
    if (this.isPreviewLoading(product, variant, sideName)) return false;
    const cacheKey = `${product.productId}_${variant.styleId}_${sideName}`;
    return this.backgroundUrls.get(cacheKey) === '';
  }

  /**
   * WI-1524 fix #1: retry a failed preview for exactly this color+side. Clears the failed
   * backgroundUrls entry first — generateSidePreview/generatePreviewForCurrentSide's text-element
   * branch both skip via `backgroundUrls.has(cacheKey)` (true even for a stored '' value), so a
   * stale failure entry would otherwise block the retry from ever re-running. Re-enters through
   * generatePreviewForCurrentSide — the same dispatcher every other preview call site uses — so it
   * naturally picks whichever underlying path (SDK preview vs export-API preview) applies, rather
   * than duplicating that branch decision here.
   */
  retrySidePreview(product: ProductColorVariant, variant: ColorVariant, rawSideName: string): void {
    const sideConfig = this.getSideConfigForCurrentSide(rawSideName);
    const displayName = sideConfig?.displayName || rawSideName;
    const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;
    this.backgroundUrls.delete(cacheKey);
    this.generatePreviewForCurrentSide(product, variant, rawSideName);
  }

  /**
   * Fetch project details for all products that have projectId
   */
  fetchAllProjectDetails(): void {
    const projectIds = new Set<string>();

    // Collect unique projectIds from all products
    this.productsDataFromPreviousPage.forEach((product: any) => {
      if (product.ProjectId) {
        projectIds.add(product.ProjectId);
      }
    });

    if (projectIds.size === 0) {
      return;
    }

    this.loader.show('Loading project details…', 'Please wait.');

    const fetchCalls = Array.from(projectIds).map(projectId => {
      return this.http.get<any>(`${environment.baseUrl}/api/designhuddle/projects/${projectId}/details`).pipe(
        map((response: any) => {
          return { projectId, details: response };
        }),
        catchError(error => {
          console.error(`❌ Error fetching project details for ${projectId}:`, error);
          return of({ projectId, details: null, error });
        })
      );
    });

    forkJoin(fetchCalls).subscribe({
      next: (results) => {
        results.forEach((result: any) => {
          if (result.details) {
            this.projectDetailsMap.set(result.projectId, result.details);
          }
        });


        // Now generate previews using DesignHuddle SDK (after project details are available)
        this.uploadBackgroundsForAllVariants();

        // After project details fetched, export all sides and then save
        this.exportAllSidesAndSave();
      },
      error: (error) => {
        console.error('❌ Error fetching project details:', error);
        this.loader.hide();
      }
    });
  }

  /**
   * Export all sides for initial variant (from master-product-list) and save
   */
  exportAllSidesAndSave(): void {

    // Prepare products data for saving (only initial variants)
    const productsWithVariants = this.selectedProducts.map(product => {
      const originalData = this.productsDataFromPreviousPage.find(
        p => String(p.ProductId) === String(product.productId)
      );

      return {
        ...originalData,
        ProductId: originalData?.ProductId || product.productId,
        productName: product.productName,
        productCategory: product.productCategory,
        productImage: product.productImage,
        availableColors: product.availableColors,
        lockedStyleId: product.lockedStyleId, // Include lockedStyleId for IsDefault check
        selectedColors: product.selectedColors.map(variant => {
          // Get templateId with fallbacks (check Sides if not at product level)
          let templateId = variant.templateId || originalData?.TemplateId;
          if (!templateId) {
            const sides = variant.sides || originalData?.Sides;
            if (sides && sides.length > 0) {
              const sideWithTemplate = sides.find((s: any) => s.TemplateId);
              if (sideWithTemplate) {
                templateId = parseInt(sideWithTemplate.TemplateId) || 0;
              }
            }
          }

          return {
            ...variant,
            productId: product.productId,
            projectId: variant.projectId || originalData?.ProjectId,
            templateId: templateId,
            sides: variant.sides || originalData?.Sides
          };
        })
      };
    });

    this.productsToSave = productsWithVariants;

    // Reset tracking
    this.pendingExports.clear();
    this.waitingForExportsToSave = true; // Save after initial exports complete
    this.shouldNavigateAfterSave = false; // Don't navigate on initial page load

    // Trigger exports for all variants
    let hasExports = false;
    this.selectedProducts.forEach(product => {
      product.selectedColors.forEach(variant => {
        if (variant.projectId) {
          hasExports = true;
          this.exportVariantAllSides(product, variant);
        }
      });
    });

    if (!hasExports) {
      this.waitingForExportsToSave = false;
      this.loader.hide();
      this.prepareFinalPayloadAndSave(productsWithVariants);
    }
  }

  toggleColorSelection(product: ProductColorVariant, color: any): void {
    // Prevent unchecking the locked color (selected from previous page)
    if (Number(color.ID) === Number(product.lockedStyleId)) {
      this.notification.warning('This color was selected in the previous step and cannot be removed.', 'Locked');
      return;
    }

    const index = product.selectedColors.findIndex(
      c => Number(c.styleId) === Number(color.ID)
    );

    if (index > -1) {
      // Remove color - also clean up per-variant tracking
      const removedVariant = product.selectedColors[index];
      const removedKey = `${product.productId}_${removedVariant.styleId}`;
      this.variantActiveSides.delete(removedKey);
      // Clean up side projects for this variant
      this.variantSideProjects.forEach((_, key) => {
        if (key.startsWith(removedKey + '_')) this.variantSideProjects.delete(key);
      });

      // Collect URLs used by OTHER remaining colors (so we don't delete shared blobs)
      const remainingUrls = new Set<string>();
      this.backgroundUrls.forEach((url, key) => {
        if (!key.startsWith(removedKey + '_')) {
          remainingUrls.add(url);
        }
      });

      // Collect Azure blob URLs for this color's sides — only delete if not shared with remaining colors
      const urlsToDelete: string[] = [];
      this.backgroundUrls.forEach((url, key) => {
        if (key.startsWith(removedKey + '_')) {
          if (!remainingUrls.has(url)) {
            urlsToDelete.push(url);
          }
        }
      });

      // Always clean up the removed color's backgroundUrls entries (regardless of Azure deletion)
      this.backgroundUrls.forEach((_, key) => {
        if (key.startsWith(removedKey + '_')) this.backgroundUrls.delete(key);
      });

      if (urlsToDelete.length > 0) {
        this.http.post(`${environment.baseUrl}/api/DesignHuddle/DeleteColorSideBlobs`, { Urls: urlsToDelete })
          .subscribe(
            () => { },
            (err) => console.error('Failed to delete color side blobs:', err)
          );
      }

      product.selectedColors.splice(index, 1);
    } else {
      // Get original product data to retrieve default projectId
      const originalData = this.productsDataFromPreviousPage.find(
        p => String(p.ProductId) === String(product.productId)
      );

      // In edit mode, copy locked variant's enriched sides (has Customizations, ElementPlacement, ZonePlacement, TemplateId)
      const lockedVariantForSides = this.isEditMode
        ? product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId))
        : null;
      let variantSides = originalData?.Sides;
      if (lockedVariantForSides?.sides) {
        // Deep copy locked variant's enriched sides, but clear thumbnail/download URLs
        // (new color will generate its own previews and exports)
        variantSides = JSON.parse(JSON.stringify(lockedVariantForSides.sides));
        variantSides.forEach((s: any) => {
          s.ThumbnailUrl = null;
          s.ThumbnailRelativeUrl = '';
          s.DownloadUrl = null;
          s.ProjectId = null;
          s.JobId = '';
        });
      } else if (variantSides) {
        // Create flow: deep copy originalData.Sides and clear ProjectId so that
        // getSideProjectIdFromVariantSides() returns null for this non-locked variant.
        // Without this, the locked variant's ProjectId would be inherited and
        // openEditorForSide() would skip the clone branch on first open.
        variantSides = JSON.parse(JSON.stringify(variantSides));
        variantSides.forEach((s: any) => {
          s.ProjectId = null;
        });
      }

      // Add color with default design data
      // In edit mode, use locked variant's customizations (has correct logo URL from DB)
      const newVariant: ColorVariant = {
        colorName: color.Name,
        colorCode: color.HtmlColor1,
        styleId: color.ID,
        useDefaultDesign: true,
        projectId: originalData?.ProjectId,
        customizations: lockedVariantForSides?.customizations || originalData?.Customizations,
        templateId: lockedVariantForSides?.templateId || originalData?.TemplateId,
        jobId: originalData?.JobId,
        sides: variantSides,
        projectName: originalData?.ProjectName || originalData?.ProjectTitle || originalData?.Description
      };

      product.selectedColors.push(newVariant);

      const variantKey = `${product.productId}_${newVariant.styleId}`;

      // Copy active sides from the locked color variant (so new color gets same sides)
      const lockedKey = `${product.productId}_${product.lockedStyleId}`;
      const currentSide = this.getProductCurrentSide(product.productId);
      const lockedSides = this.variantActiveSides.get(lockedKey) || [currentSide];
      const sidesToAdd = [...lockedSides];
      this.variantActiveSides.set(variantKey, [...sidesToAdd]);


      // WI-1521 fix #1: no full-screen blocking loader for a single color add anymore — it used
      // to freeze the whole page ("Preparing color variant…") for as long as every side's
      // preview+export chain took (reported 30s-3min). The swatch/selection above is already
      // added synchronously, and per-side spinners (getVariantSideImages()/isPreviewLoading(),
      // plus the swatch's own isColorPreparing()) give in-progress feedback for just this color
      // while the user keeps working — selecting other colors, other products, etc.
      // checkAllExportsComplete() still calls loader.hide(), which is a harmless no-op here now
      // that show() is never called for this path.

      // For each side: reuse locked variant's projectId if same logo, else create new (eagerly
      // only for the side actually in view — see isCurrentSide below).
      this.pendingExports.set(variantKey, { pending: sidesToAdd.length, completed: 0, sideJobIds: new Map() });
      const lockedVariantRef = product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId));

      // Safety fallback: `currentSide` (the globally-viewed side) is normally one of sidesToAdd
      // (it seeded lockedSides' fallback above), but if the locked color's active-side set was
      // built without it (e.g. the user is viewing a side no color has been given an imprint on
      // yet), guarantee at least ONE side still gets eager treatment so this new color isn't left
      // with literally nothing in flight — mirrors initializeSingleSideFlow always processing at
      // least its initialSide.
      const hasCurrentSideInList = sidesToAdd.includes(currentSide);

      sidesToAdd.forEach(sideName => {
        const sideInfo = this.getSideInfoForSide(product.productId, sideName);
        if (!sideInfo) {
          this.markExportCompleted(variantKey, sideName, '');
          return;
        }

        // Check if ANY already-selected color (not just the locked/default color) already created
        // a project for this side — see findSideProjectOwner's doc comment. NOTE: the old third
        // condition here ("sameTemplate") compared this side's per-side templateId (genuinely
        // different per side — front/back/sleeve each have their own DH template, see
        // getProductAvailableSides/buildAvailableSides) against `lockedVariantRef.templateId`,
        // a SINGLE value stored once per color variant (effectively just the front/initial
        // side's template). That comparison was always false for any non-front side UNLESS
        // lockedSideProjectId was already truthy — in which case the condition ALSO had
        // `|| !!lockedSideProjectId`, forcing it true regardless. So sameTemplate never actually
        // changed the outcome of `lockedSideProjectId && sameTemplate && sameLogo` — it was dead
        // weight. Removed; the two real questions are simply "does any selected color already have
        // a project for this side" and "does the new color have its own logo for this side".
        const sharedSideProjectId = this.getSharedSideProjectId(product, sideName);
        const newLogoUrl = newVariant.sideLogos?.[sideName] || '';
        const sameLogo = !newLogoUrl; // new variant has no custom logo = same as whatever's shared

        // WI-1521-followup (Issue 1): a new color used to eagerly spin up a brand-new DH project
        // for EVERY side copied from the locked color's active-side set, even sides the user isn't
        // even looking at. Reuse (the branch below) is cheap — no new project, just a data
        // carry-over — so it still runs for every side regardless. But when reuse isn't possible
        // (no shared project yet, or the new color needs its own logo) we now only create a new
        // project eagerly for the side the user is CURRENTLY viewing; every other side is deferred
        // exactly like the locked color's own non-initial sides already are (see
        // initializeSingleSideFlow, which only eagerly creates initialSide's project) — picked up
        // lazily either by onSideChange() when the user switches the viewed side into it, or by
        // ensurePendingSideProjectsBeforeSave() as a safety net right before Continue so nothing is
        // ever silently left unexported.
        const isCurrentSide = sideName === currentSide
          || (!hasCurrentSideInList && sideName === sidesToAdd[0]);

        if (sharedSideProjectId && sameLogo) {
          // Carry over the owning color's export data (JobId + DownloadUrl) so the thumbnail
          // renders immediately. The owner may be the locked color OR any other already-selected
          // color that created this side's project first — resolve it rather than assuming locked.
          // Do NOT set variantSideProjects for this non-owning variant: leaving it unset ensures
          // openEditorForSide() hits the clone branch on first open instead of directly reusing the
          // shared project (which would let one color's edit corrupt every color borrowing it).
          const ownerKey = this.findSideProjectOwnerKey(product, sideName);
          const ownerExportTracking = this.pendingExports.get(ownerKey);
          const ownerJobId = ownerExportTracking?.sideJobIds?.get(sideName) || '';
          const ownerDownloadUrl = ownerExportTracking?.sideDownloadUrls?.get(sideName) || '';
          this.generatePreviewForCurrentSide(product, newVariant, sideName);
          // Copy DownloadUrl from the owning variant so the PNG image carries over
          if (ownerDownloadUrl) {
            const newTracking = this.pendingExports.get(variantKey);
            if (newTracking) {
              if (!newTracking.sideDownloadUrls) {
                newTracking.sideDownloadUrls = new Map<string, string>();
              }
              newTracking.sideDownloadUrls.set(sideName, ownerDownloadUrl);
            }
          }
          this.markExportCompleted(variantKey, sideName, ownerJobId);
        } else if (isCurrentSide) {
          // No reuse possible, but this IS the side being viewed right now — create eagerly so
          // the user sees a real preview immediately (same as before for this one side). This color
          // becomes the OWNER of this side's project. Only register it as the side-level SHARED
          // owner when sameLogo is true (a generic/default design) — when this color has its own
          // custom logo override for this side, the project is specific to it and must never be
          // handed out to another color expecting the shared/default design.
          const lockedLogoUrl = this.getLockedVariantLogoForSide(lockedVariantRef, sideName);
          const mediaPosition = this.getMediaPositionForSide(lockedVariantRef || newVariant, sideName);
          this.incrementDesignIdOffset(product.productId, newVariant.styleId);
          const projectTitle = this.generateRandomProjectTitle();
          this.buildSeedCustomization(lockedLogoUrl, product.productId, sideName, mediaPosition).then(customizationObject =>
            this.createProjectForSide(sideInfo.templateId, projectTitle, customizationObject)
            .then(projectId => {
              if (projectId) {
                this.variantSideProjects.set(`${variantKey}_${sideName}`, projectId);
                // No separate "register as shared" step needed — findSideProjectOwner's scan
                // already discovers this via variantSideProjects, skipping it automatically if
                // !sameLogo (this color has its own custom logo override for this side).
                // Hit metadata API to get elementPlacement/zonePlacement
                this.fetchAndStoreMetadata(projectId, newVariant, sideName);
                this.generatePreviewForCurrentSide(product, newVariant, sideName);
                this.exportCurrentSide(product, newVariant, sideName);
              } else {
                this.markExportCompleted(variantKey, sideName, '');
              }
            })
            .catch(err => {
              console.error(`❌ Error creating project for side ${sideName}:`, err);
              this.markExportCompleted(variantKey, sideName, '');
            })
          );
        } else {
          // Not viewable right now and no locked project to reuse yet — defer. Don't count this
          // side against this variant's pendingExports total since nothing was kicked off for it;
          // ensurePendingSideProjectsBeforeSave() (called from continueToNextStep()) adds it back
          // in if the user never happens to switch the view into this side first.
          const tracking = this.pendingExports.get(variantKey);
          if (tracking) {
            tracking.pending = Math.max(0, tracking.pending - 1);
          }
        }
      });
    }

    this.calculateTotalVariants();
  }

  /**
   * Export ALL sides of a variant by calling export-job API for each side
   * Uses project details to get pages and match with sides
   */
  exportVariantAllSides(product: ProductColorVariant, variant: ColorVariant): void {
    if (!variant.projectId) {
      // Re-evaluate completion gate so the loader doesn't hang when this is the only variant.
      this.checkAllExportsComplete();
      return;
    }

    // Get project details from map
    const projectDetails = this.projectDetailsMap.get(variant.projectId);
    if (!projectDetails) {
      // Fetch project details first, then export
      this.http.get<any>(`${environment.baseUrl}/api/designhuddle/projects/${variant.projectId}/details`).subscribe({
        next: (details: any) => {
          this.projectDetailsMap.set(variant.projectId!, details);
          this.doExportAllSides(product, variant, details);
        },
        error: (error) => {
          console.error(`❌ Error fetching project details for ${variant.projectId}:`, error);
          // Re-evaluate gate so a failed details fetch doesn't strand the loader.
          this.checkAllExportsComplete();
        }
      });
      return;
    }

    this.doExportAllSides(product, variant, projectDetails);
  }

  /**
   * Actually perform export for all sides using project details
   */
  doExportAllSides(product: ProductColorVariant, variant: ColorVariant, projectDetails: any): void {
    // Get pages from project details
    const pages = projectDetails?.data?.pages || projectDetails?.pages || [];

    if (pages.length === 0) {
      // Re-evaluate gate so an empty-pages project doesn't strand the loader.
      this.checkAllExportsComplete();
      return;
    }

    const sides = variant.sides || [];

    // Create a unique key for tracking this variant's exports
    const variantKey = `${product.productId}_${variant.styleId}`;

    // Initialize tracking for this variant
    this.pendingExports.set(variantKey, {
      pending: pages.length,
      completed: 0,
      sideJobIds: new Map()
    });

    // Resolve a project-level fallback zone (inches) ONCE — used for any page whose
    // per-side inches aren't stored yet (fresh-create path). Never errors; may emit null.
    const firstPageName = (pages[0]?.name || pages[0]?.page_name || 'front');
    this.resolveZoneInchesForSide(variant.projectId || '', variant, firstPageName).subscribe((fallbackZoneInches) => {
    // Export each page
    pages.forEach((page: any, index: number) => {
      const pageId = page.page_id || page.id || '';
      const pageName = page.name || page.page_name || 'front';
      const pageNumber = page.page_number || page.pageNumber || (index + 1);


      if (!pageId) {
        this.markExportCompleted(variantKey, pageName, '');
        return;
      }

      // Frame/asset aspect guard (see exportCurrentSide / frame-fit.ts): never export a side whose
      // logo frame would render the artwork stretched; flag it for the designer instead.
      this.isSideFrameDistorted(product, variant, pageName).then(distorted => {
      if (distorted) {
        this.flagPrintExportDistorted(product, variant, pageName);
        this.markExportCompleted(variantKey, pageName, '');
        return;
      }

      // Match page name with side to get background image
      const matchedSide = this.matchPageWithSide(pageName, sides, product, variant);

      // Create customization for this specific page/side
      const sideCustomizations = this.createSideCustomizations(product, variant, pageName, matchedSide);

      // fileName = pageId + pageName (side name)
      const fileName = `${pageId}_${pageName.toLowerCase().replace(/\s+/g, '_')}`;

      const apiUrl = `${environment.baseUrl}/api/designhuddle/projects/${variant.projectId}/export-job`;
      // Per-page physical zone inches → pixel width; fall back to the project-level zone
      // resolved once above. width = round(zoneInches × 300) → 300 DPI at true print size.
      const pageZoneInches = this.getZoneInchesForSide(variant, pageName) || fallbackZoneInches;
      const widthPx = this.computeExportWidthPx(pageZoneInches);
      const body: any = {
        format: 'png',
        fileName: fileName,
        page_id: pageId,
      };
      if (widthPx != null) body.width = widthPx;


      // Hard 30s timeout so a hung backend can't strand the gate forever — without it,
      // a request that never resolves means markExportCompleted never fires and the loader hangs.
      this.http.post<any>(apiUrl, body).pipe(
        timeout(30000),
        catchError(error => {
          console.error(`❌ Export error for ${variant.colorName} - ${pageName}:`, error);
          return of(null);
        })
      ).subscribe({
        next: (response: any) => {
          let jobId = '';
          if (response) {
            jobId = response?.data?.job_id || '';
            if (jobId) {

              // Store jobId in variant
              variant.jobId = String(jobId);

              // Also store job_id and page_id in the matching side
              if (matchedSide) {
                matchedSide.JobId = String(jobId);
                matchedSide.PageId = String(pageId);
              }

              this.notification.success(`Export started for ${variant.colorName} - ${pageName}`, 'Success');
              // Start polling for download URL — polling will call markExportCompleted when done
              this.pollExportJobProgress(variant.projectId, jobId, variantKey, pageName);
              return; // Don't mark as completed yet — wait for polling to finish
            } else {
              console.error(`❌ No job_id in response for ${variant.colorName} - ${pageName}:`, response);
            }
          }

          // No jobId obtained — mark as completed immediately
          this.markExportCompleted(variantKey, pageName, '');
        }
      });
      }); // end isSideFrameDistorted
    });
    }); // end resolveZoneInchesForSide (fallback zone) subscribe
  }

  /**
   * Mark an export as completed and check if all exports are done
   */
  /**
   * Poll DesignHuddle export job progress until completed, then store the download URL
   * in the variant's side data so it flows into the payload's DownloadUrl field.
   */
  pollExportJobProgress(projectId: string, jobId: string, variantKey: string, sideName: string, exportGen?: number): void {
    const genKey = this.printGenKey(variantKey, sideName);
    // An export started before the side's last edit (exportGen !== current) is stale: its file shows
    // the old design. Settle it so the pending counters still balance, but never record its URL/job.
    const isStale = () => exportGen !== undefined && !this.printGens.isCurrent(genKey, exportGen);
    const settle = () => isStale() ? this.settleStaleExport(variantKey) : this.markExportCompleted(variantKey, sideName, jobId);
    if (!projectId || !jobId) {
      this.markExportCompleted(variantKey, sideName, jobId || '');
      return;
    }

    const pollUrl = `${environment.baseUrl}/api/designhuddle/export-job/${projectId}/${jobId}`;
    let attempts = 0;
    const pollStartTime = Date.now();
    // WI-1521 fix #3: exponential backoff (nextPollDelayMs, starts ~500ms) instead of a fixed 2s
    // interval — 40s max total wait (same ceiling as the previous 20 attempts × 2000ms) is long
    // enough for typical DH exports, short enough that a hung job surfaces quickly. Falls back to
    // markExportCompleted on timeout to unblock the gate. markExportCompleted's own logic (incl.
    // WI-1524's printExportFailures tracking) is unchanged — only the retry timing changes here.
    const maxTotalWaitMs = 40000;

    const poll = () => {
      attempts++;
      // 5s per-request timeout so a hung poll attempt doesn't burn the whole window.
      this.http.get<any>(pollUrl).pipe(timeout(5000)).subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          const isCompleted = data?.completed === true || data?.status === 'completed';

          if (isCompleted) {
            // Prefer azure_url (already uploaded to Azure by backend), fallback to DesignHuddle URL
            const downloadUrl = data?.azure_url || data?.output_url || data?.download_url || data?.print_file_url || data?.url || '';
            if (isStale()) {
            } else if (downloadUrl) {
              // Store download URL in pendingExports tracking
              const tracking = this.pendingExports.get(variantKey);
              if (tracking) {
                if (!tracking.sideDownloadUrls) {
                  tracking.sideDownloadUrls = new Map<string, string>();
                }
                tracking.sideDownloadUrls.set(sideName, downloadUrl);
              }
              if (exportGen !== undefined) this.printGens.accept(genKey, exportGen);
            }
            // Download URL has been stored — now mark export as completed
            settle();
          } else if (Date.now() - pollStartTime < maxTotalWaitMs) {
            setTimeout(poll, this.nextPollDelayMs(attempts));
          } else {
            // Timed out — mark as completed anyway to unblock save
            settle();
          }
        },
        error: () => {
          if (Date.now() - pollStartTime < maxTotalWaitMs) {
            setTimeout(poll, this.nextPollDelayMs(attempts));
          } else {
            // Error retries exhausted — mark as completed to unblock save
            settle();
          }
        }
      });
    };

    // Poll immediately — most DH exports complete in <1s, no point waiting.
    poll();
  }

  /** A pre-edit export finished: count it toward the pending gate without touching tracking/failures. */
  private settleStaleExport(variantKey: string): void {
    const tracking = this.pendingExports.get(variantKey);
    if (!tracking) return;
    tracking.completed++;
    this.checkAllExportsComplete();
  }

  private printGenKey(variantKey: string, sideName: string): string {
    return `${variantKey}_${this.canonicalSide(sideName)}`;
  }

  /**
   * The side's design changed (editor close, artwork swap, shared-design propagation): the tracked
   * print export (job id + file) now shows the OLD design. Drop it and bump the edit generation so
   * an in-flight pre-edit export is ignored when it lands. Callers start a fresh exportCurrentSide
   * (reserving a pending slot first) so the save gate counts the new export as outstanding.
   * `sharers`: other colours pointing at the same DH project for this side also lose their copied
   * export (no re-export — at save they resolve through the owner's fresh one, or error).
   */
  private invalidatePrintExport(product: ProductColorVariant, variant: ColorVariant, sideName: string, sharers: boolean = false): void {
    const variantKey = `${product.productId}_${variant.styleId}`;
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const key = this.printGenKey(variantKey, sideName);
    this.printGens.bump(key);
    clearTrackedSideExport(this.pendingExports.get(variantKey), sideName, n => this.canonicalSide(n));
    this.printExportFailures.delete(key);

    if (!sharers) return;
    const projectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`);
    if (!projectId) return;
    (product.selectedColors || []).forEach(other => {
      if (other === variant) return;
      const otherKey = `${product.productId}_${other.styleId}`;
      if (this.variantSideProjects.get(`${otherKey}_${sideNorm}`) !== projectId) return;
      const otherGenKey = this.printGenKey(otherKey, sideName);
      this.printGens.accept(otherGenKey, this.printGens.bump(otherGenKey));
      clearTrackedSideExport(this.pendingExports.get(otherKey), sideName, n => this.canonicalSide(n));
    });
  }

  markExportCompleted(variantKey: string, sideName: string, jobId: string): void {
    const tracking = this.pendingExports.get(variantKey);
    if (!tracking) return;

    tracking.completed++;
    if (jobId) {
      tracking.sideJobIds.set(sideName, jobId);
    }

    // WI-1524 fix #1: a side is only genuinely done once it has a real print file. Every call site
    // that reaches "completed" without ever storing a sideDownloadUrls entry for this side —
    // poll timeout, poll error-exhaustion, no job_id, missing project/page data, etc. — funnels
    // through here, so this is the one place that reliably catches all of them instead of hand-
    // checking each call site. Cleared automatically if a later retry succeeds. Keyed on
    // canonicalSide() rather than the raw sideName — this sideName comes from the DH project's own
    // page data (doExportAllSides) or the app's internal side list (exportCurrentSide), which don't
    // always agree on exact string form (e.g. "sleeve_left" vs "left_sleeve"), so a raw-string key
    // could silently fail to match what the UI looks up.
    const failureKey = `${variantKey}_${this.canonicalSide(sideName)}`;
    // The LOOKUP must be just as canonical-aware as the key above — an exact sideName match here
    // missed a download URL that was genuinely stored, just under the OTHER caller's raw string
    // form for the same side (doExportAllSides vs exportCurrentSide, see above), reporting "no
    // print file" even though the export had actually succeeded. Exact match first (cheap, common
    // case both callers agree), falling back to a canonical scan across every stored key.
    let downloadUrl = tracking.sideDownloadUrls?.get(sideName);
    if (!downloadUrl && tracking.sideDownloadUrls) {
      const canonTarget = this.canonicalSide(sideName);
      for (const [storedSideName, url] of tracking.sideDownloadUrls.entries()) {
        if (url && this.canonicalSide(storedSideName) === canonTarget) {
          downloadUrl = url;
          break;
        }
      }
    }
    if (downloadUrl) {
      this.printExportFailures.delete(failureKey);
    } else {
      this.printExportFailures.add(failureKey);
    }


    // Check if all exports for all variants are complete
    this.checkAllExportsComplete();
  }

  /**
   * WI-1524 fix #1: true when this color+side's print export finished without ever producing a
   * downloadable file. Surfaced separately from hasPreviewFailed — the on-screen thumbnail and the
   * print file are two independent DH export chains, so one can succeed while the other fails.
   */
  hasPrintExportFailure(product: ProductColorVariant, variant: ColorVariant, sideName: string): boolean {
    const variantKey = `${product.productId}_${variant.styleId}`;
    return this.printExportFailures.has(`${variantKey}_${this.canonicalSide(sideName)}`);
  }

  /**
   * True when this color+side's logo element frame was found to disagree with the artwork's
   * aspect ratio at export time (see isSideFrameDistorted). The print export was skipped; the
   * only fix is to re-add the logo in the editor so DH re-fits the frame.
   */
  hasPrintExportDistortion(product: ProductColorVariant, variant: ColorVariant, sideName: string): boolean {
    const variantKey = `${product.productId}_${variant.styleId}`;
    return this.printExportDistorted.has(`${variantKey}_${this.canonicalSide(sideName)}`);
  }

  private flagPrintExportDistorted(product: ProductColorVariant, variant: ColorVariant, sideName: string): void {
    const variantKey = `${product.productId}_${variant.styleId}`;
    const key = `${variantKey}_${this.canonicalSide(sideName)}`;
    this.printExportDistorted.add(key);
    console.error(`❌ Print export skipped — logo frame does not match the artwork aspect (would export stretched)`, {
      productId: product.productId, styleId: variant.styleId, side: sideName
    });
  }

  private clearPrintExportDistorted(product: ProductColorVariant, variant: ColorVariant, sideName: string): void {
    const variantKey = `${product.productId}_${variant.styleId}`;
    this.printExportDistorted.delete(`${variantKey}_${this.canonicalSide(sideName)}`);
  }

  /**
   * Export-time guard for the stretched-print-file defect (order S00327511): compares the side's
   * stored logo element frame (`elementPlacement`, read from the DH editor project at editor close
   * or from the DB in edit mode) with the logo artwork's intrinsic aspect ratio. DH renders a
   * vector element by filling its frame, so a mismatch means the exported print file would be
   * non-uniformly scaled. Resolves false (never block) when there is no stored frame — a side that
   * was never edited was seeded via createProject, where DH fits the frame itself — or when the
   * artwork's size cannot be read.
   */
  private async isSideFrameDistorted(product: ProductColorVariant, variant: ColorVariant, sideName: string): Promise<boolean> {
    try {
      const sideNorm = (sideName || '').toLowerCase().replace(/\s+/g, '_');
      const side = variant.sides ? this.matchSideByName(variant.sides, sideNorm) : null;
      const frame = this.normalizePlacement(side?.elementPlacement || side?.ElementPlacement);
      if (!frame) return false;

      // A stored frame is only trustworthy if it actually belongs to this side's CURRENT project.
      // fetchAndStoreMetadata (which refreshes elementPlacement after a project change) is
      // fire-and-forget, and exportCurrentSide — this check's caller — runs immediately after it
      // at every call site, well before that response can land. Without this guard, Replace
      // Artwork or adding a new imprint location briefly compares the PREVIOUS logo's still-fitted
      // frame against the NEW logo's aspect ratio (a near-guaranteed mismatch that says nothing
      // about the new project, which DH always auto-fits correctly on creation), producing a
      // false "Logo is stretched" warning with no actual API failure. elementPlacementProjectId is
      // set by fetchAndStoreMetadata; side.ProjectId (the DB-sourced field, set alongside
      // ElementPlacement at edit-mode load) is the fallback for data that was never refreshed by
      // that call — self-consistent at load time, so this never weakens the original
      // saved-data distortion check.
      const variantKey = `${product.productId}_${variant.styleId}`;
      const currentProjectId = this.variantSideProjects.get(`${variantKey}_${sideNorm}`) || side?.ProjectId || null;
      const frameProjectId = side?.elementPlacementProjectId || side?.ProjectId || null;
      if (currentProjectId && frameProjectId && String(currentProjectId) !== String(frameProjectId)) {
        return false;
      }

      const logoUrl = this.resolveExportLogoUrl(product, variant, sideName);
      if (!logoUrl) return false;

      const dims = await this.getDesignDims(logoUrl);
      if (!dims || !(dims.w > 0) || !(dims.h > 0)) return false;

      return isFrameDistorted(frame, dims.w / dims.h);
    } catch (e) {
      return false;
    }
  }

  /**
   * Every currently-selected color/side flagged as distorted — these BLOCK save (unlike plain
   * export failures, which only confirm()), because the file that would be saved is known-wrong.
   */
  private collectDistortedSides(): string[] {
    const distorted: string[] = [];
    this.selectedProducts.forEach(product => {
      product.selectedColors.forEach(variant => {
        this.getVariantSideImages(product, variant).forEach(side => {
          if (side.printDistorted) {
            distorted.push(`${product.productName} — ${variant.colorName} — ${side.name}`);
          }
        });
      });
    });
    return distorted;
  }

  /**
   * WI-1524 fix #1: retry a failed print export for exactly this color+side, reusing
   * exportCurrentSide — the same single-side export entry point used by every other retry/re-export
   * call site (addSideToSingleVariant, onSideChange, etc.) — rather than a parallel code path.
   * exportCurrentSide itself doesn't bump pendingExports (its callers are expected to have already
   * reserved the pending slot), so this bumps `pending` by one first, matching that convention.
   */
  retryPrintExport(product: ProductColorVariant, variant: ColorVariant, rawSideName: string): void {
    const variantKey = `${product.productId}_${variant.styleId}`;
    this.printExportFailures.delete(`${variantKey}_${this.canonicalSide(rawSideName)}`);
    this.printExportDistorted.delete(`${variantKey}_${this.canonicalSide(rawSideName)}`);

    const tracking = this.pendingExports.get(variantKey);
    if (tracking) {
      tracking.pending++;
    } else {
      this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
    }

    this.exportCurrentSide(product, variant, rawSideName);
  }

  /**
   * Check if all pending exports are complete and trigger save (if waiting for save)
   */
  /**
   * Aggregate export progress across every currently-tracked variant/side — sums the same
   * {pending, completed} counters checkAllExportsComplete() reads per-variant into one overall
   * "X of Y ready" total, for the Continue button's progress text. `total` is `pending` (the
   * count of sides actually dispatched for export), not some larger theoretical count, so it only
   * grows as work is actually kicked off — matching what the user sees happening in real time.
   */
  getOverallExportProgress(): { completed: number; total: number } {
    let completed = 0;
    let total = 0;
    this.pendingExports.forEach(tracking => {
      completed += tracking.completed;
      total += tracking.pending;
    });
    return { completed, total };
  }

  checkAllExportsComplete(): void {
    let allExportsComplete = true;
    let totalPending = 0;
    let totalCompleted = 0;

    this.pendingExports.forEach((tracking, key) => {
      totalPending += tracking.pending;
      totalCompleted += tracking.completed;
      if (tracking.completed < tracking.pending) {
        allExportsComplete = false;
      }
    });


    // Only save when BOTH exports AND uploads are complete
    const allUploadsComplete = this.pendingUploads <= 0;

    if (allExportsComplete && allUploadsComplete && this.waitingForExportsToSave && this.productsToSave.length > 0) {
      this.waitingForExportsToSave = false;

      // Rebuild products from fresh selectedProducts data to get updated jobIds
      const freshProductsData = this.rebuildProductsFromSelectedProducts();

      // Update sides with job_ids before saving
      this.updateSidesWithJobIds();

      // Now save with fresh data
      this.prepareFinalPayloadAndSave(freshProductsData);
    } else if (allExportsComplete && !allUploadsComplete && this.waitingForExportsToSave) {
      // Exports done but uploads still pending - set flag to save after uploads
      this.saveWaitingForUploads = true;
    } else if (allExportsComplete && allUploadsComplete && totalPending > 0 && !this.waitingForExportsToSave) {
      // Exports AND uploads completed but not waiting for save (e.g., adding color or logo update).
      // Only hide when uploads are also done — otherwise the preview generation is still in flight
      // and hiding here would dismiss the "Preparing color variant…" message prematurely.
      this.loader.hide();
    }
  }

  /**
   * Rebuild products data from selectedProducts to get fresh jobIds and other updated properties
   */
  rebuildProductsFromSelectedProducts(): any[] {
    return this.selectedProducts.map(product => {
      const originalData = this.productsDataFromPreviousPage.find(
        p => String(p.ProductId) === String(product.productId)
      );

      return {
        ...originalData,
        ProductId: originalData?.ProductId || product.productId,
        productName: product.productName,
        productCategory: product.productCategory,
        productImage: product.productImage,
        availableColors: product.availableColors,
        lockedStyleId: product.lockedStyleId, // Include lockedStyleId for IsDefault check
        selectedColors: product.selectedColors.map(variant => {
          // Get templateId with fallbacks (check Sides if not at product level)
          let templateId = variant.templateId || originalData?.TemplateId;
          if (!templateId) {
            const sides = variant.sides || originalData?.Sides;
            if (sides && sides.length > 0) {
              const sideWithTemplate = sides.find((s: any) => s.TemplateId);
              if (sideWithTemplate) {
                templateId = parseInt(sideWithTemplate.TemplateId) || 0;
              }
            }
          }

          return {
            ...variant,
            productId: product.productId,
            projectId: variant.projectId || originalData?.ProjectId,
            templateId: templateId,
            jobId: variant.jobId, // Get fresh jobId from variant
            sides: variant.sides || originalData?.Sides
          };
        })
      };
    });
  }

  /**
   * Update variant sides with their respective job_ids from tracking
   */
  updateSidesWithJobIds(): void {
    this.productsToSave.forEach(product => {
      product.selectedColors.forEach((variant: any) => {
        const variantKey = `${product.ProductId}_${variant.styleId}`;
        const tracking = this.pendingExports.get(variantKey);

        if (tracking && variant.sides) {
          variant.sides.forEach((side: any) => {
            const sideName = side.SideName || '';
            // Try to match with tracked job_ids
            tracking.sideJobIds.forEach((jobId, trackedSideName) => {
              const sideNameLower = sideName.toLowerCase().replace(/\s+/g, '_');
              const trackedLower = trackedSideName.toLowerCase().replace(/\s+/g, '_');
              if (sideNameLower === trackedLower ||
                  sideNameLower.includes(trackedLower) ||
                  trackedLower.includes(sideNameLower)) {
                side.JobId = jobId;
              }
            });
          });
        }
      });
    });
  }

  /**
   * Match page name from project details with side data
   * Enhanced matching for sleeve variations
   */
  matchPageWithSide(pageName: string, sides: any[], product: ProductColorVariant, variant: ColorVariant): any {
    const pageNameLower = pageName.toLowerCase().replace(/\s+/g, '_');

    // Determine what type of side we're looking for
    const isLeftSleeve = pageNameLower.includes('left') && pageNameLower.includes('sleeve');
    const isRightSleeve = pageNameLower.includes('right') && pageNameLower.includes('sleeve');
    const isBack = pageNameLower.includes('back') && !pageNameLower.includes('sleeve');
    const isFront = pageNameLower.includes('front') || (!isLeftSleeve && !isRightSleeve && !isBack);

    // Try to find matching side by name with improved matching
    const matchedSide = sides.find((side: any) => {
      const sideName = (side.SideName || side.Name || '').toLowerCase().replace(/\s+/g, '_');

      // Exact match
      if (sideName === pageNameLower) return true;

      // Sleeve matching - handle various naming conventions
      if (isLeftSleeve) {
        return (sideName.includes('left') && sideName.includes('sleeve')) ||
               sideName === 'sleeve_left' || sideName === 'left_sleeve' ||
               sideName === 'sleeveleft' || sideName === 'leftsleeve';
      }
      if (isRightSleeve) {
        return (sideName.includes('right') && sideName.includes('sleeve')) ||
               sideName === 'sleeve_right' || sideName === 'right_sleeve' ||
               sideName === 'sleeveright' || sideName === 'rightsleeve';
      }

      // Back matching
      if (isBack) {
        return sideName === 'back' || sideName.includes('back');
      }

      // Front matching
      if (isFront) {
        return sideName === 'front' || sideName.includes('front');
      }

      // Fallback to partial matching
      return sideName.includes(pageNameLower) || pageNameLower.includes(sideName);
    });

    if (matchedSide) {
    } else {
    }

    return matchedSide;
  }

  /**
   * Create customization object for a specific side/page
   * Uses correct background key names: background_front, background_back, background_sleeve_left, background_sleeve_right
   */
  createSideCustomizations(product: ProductColorVariant, variant: ColorVariant, pageName: string, matchedSide: any): any {
    const pageNameLower = pageName.toLowerCase();
    const sideNameNormalized = pageNameLower.replace(/\s+/g, '_');
    // Get display name (e.g., "Front") so we also check sideLogos stored under display keys
    const sideConfig = this.getSideConfigForCurrentSide(sideNameNormalized);
    const displayName = sideConfig?.displayName || '';

    // In edit mode, use the full Customizations object from DB if available
    // This preserves the original logo URL and any additional customization properties
    if (this.isEditMode && variant.sides && variant.sides.length > 0) {
      const sideData = variant.sides.find((s: any) => {
        const sName = (s.SideName || '').toLowerCase().replace(/\s+/g, '_');
        return sName === sideNameNormalized || sName.includes(sideNameNormalized) || sideNameNormalized.includes(sName);
      });
      if (sideData?.Customizations) {
        const dbCustomizations = typeof sideData.Customizations === 'string'
          ? JSON.parse(sideData.Customizations) : JSON.parse(JSON.stringify(sideData.Customizations));
        // Check if user has set a side-specific logo override (include display name in variations)
        const sideNameVariations = [pageName, pageName.replace(/\s+/g, '_'), pageNameLower, sideNameNormalized];
        if (displayName && !sideNameVariations.includes(displayName)) {
          sideNameVariations.push(displayName);
        }
        let hasCustomLogo = false;
        for (const nameVariation of sideNameVariations) {
          if (variant.sideLogos && variant.sideLogos[nameVariation]) {
            if (dbCustomizations?.classes?.logo) {
              dbCustomizations.classes.logo.url = variant.sideLogos[nameVariation];
            }
            hasCustomLogo = true;
            break;
          }
        }
        // Ensure media_position is present (recalculate from placements if missing)
        if (dbCustomizations?.classes?.logo && !dbCustomizations.classes.logo.media_position) {
          const mediaPosition = this.getMediaPositionForSide(variant, sideNameNormalized);
          if (mediaPosition) {
            dbCustomizations.classes.logo.media_position = mediaPosition;
          }
        }
        // Strip background_* keys — they are not needed in customizations
        if (dbCustomizations?.classes) {
          Object.keys(dbCustomizations.classes).forEach(key => {
            if (key.startsWith('background_')) {
              delete dbCustomizations.classes[key];
            }
          });
        }
        return dbCustomizations;
      }
    }

    // Get logo URL for this side (create flow or fallback)
    const logoUrl = this.resolveExportLogoUrl(product, variant, pageName);

    // Build the customization through the shared top-anchor helper (single source of truth):
    // real user placement wins, else this side's own print-zone top-anchor, else omit (DH default).
    // Dims for logoUrl are warmed by the export caller (exportCurrentSide) so the top-anchor is
    // deterministic on the first pass.
    const mediaPosition = this.getMediaPositionForSide(variant, sideNameNormalized);
    const zoneDims = this.getZoneDimensionsForSide(product.productId, sideNameNormalized);
    const customizations = this.buildTopAnchoredLogoCustomization(logoUrl, zoneDims, mediaPosition);


    return customizations;
  }

  /**
   * Update background URL in customizations to match the variant's actual color style.
   * Prevents new colors from inheriting the locked/default color's product image.
   */
  updateBackgroundUrlForSide(customizations: any, product: ProductColorVariant, variant: ColorVariant, sideNameNormalized: string): void {
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!variantStyle) return;

    let sideImageUrl = '';
    let backgroundKey = '';

    if (sideNameNormalized.includes('front')) {
      sideImageUrl = variantStyle.ImageFilePath_Front;
      backgroundKey = 'background_front';
    } else if (sideNameNormalized.includes('back')) {
      sideImageUrl = variantStyle.ImageFilePath_Back;
      backgroundKey = 'background_back';
    } else if (sideNameNormalized.includes('left') && sideNameNormalized.includes('sleeve')) {
      sideImageUrl = variantStyle.ImageFilePath_SleeveLeft;
      backgroundKey = 'background_sleeve_left';
    } else if (sideNameNormalized.includes('right') && sideNameNormalized.includes('sleeve')) {
      sideImageUrl = variantStyle.ImageFilePath_SleeveRight;
      backgroundKey = 'background_sleeve_right';
    } else {
      sideImageUrl = variantStyle.ImageFilePath_Front;
      backgroundKey = 'background_front';
    }

    if (sideImageUrl && !sideImageUrl.startsWith('http')) {
      sideImageUrl = this.joinUrl(this.imgBaseURL, sideImageUrl);
    }

    if (sideImageUrl && backgroundKey) {
      if (!customizations.classes) customizations.classes = {};
      const oldUrl = customizations.classes[backgroundKey]?.url;
      customizations.classes[backgroundKey] = { url: sideImageUrl };
      if (oldUrl !== sideImageUrl) {
      }
    }
  }

  isColorSelected(product: ProductColorVariant, color: any): boolean {
    return product.selectedColors.some(c => Number(c.styleId) === Number(color.ID));
  }

  isColorLocked(product: ProductColorVariant, color: any): boolean {
    return Number(color.ID) === Number(product.lockedStyleId);
  }

  /**
   * WI-1521 fix #1: true while ANY side of this color is still generating its preview or
   * uploading it to Azure. Drives a small per-swatch spinner so a newly-selected color shows
   * "still working" feedback right where the user clicked, replacing the old full-screen
   * "Preparing color variant…" loader that blocked the whole page for the same wait.
   */
  isColorPreparing(product: ProductColorVariant, color: any): boolean {
    if (!this.isColorSelected(product, color)) return false;
    const prefix = `${product.productId}_${Number(color.ID)}_`;
    for (const key of this.generatingPreviews) {
      if (key.startsWith(prefix)) return true;
    }
    for (const key of this.uploadingBackgrounds) {
      if (key.startsWith(prefix)) return true;
    }
    return false;
  }

  /** Find the locked variant (original color) for the product containing the given variant */
  getLockedVariantForProduct(variant: ColorVariant): ColorVariant | null {
    for (const product of this.selectedProducts) {
      if (product.selectedColors.includes(variant) || product.selectedColors.some(c => Number(c.styleId) === Number(variant.styleId))) {
        return product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId)) || null;
      }
    }
    return null;
  }

  calculateTotalVariants(): void {
    this.totalVariants = this.selectedProducts.reduce(
      (total, product) => total + product.selectedColors.length,
      0
    );
  }

  // Get side images for a color variant - returns all active sides for this variant
  getVariantSideImages(product: ProductColorVariant, variant: ColorVariant): { name: string, locationLabel: string, rawSideName: string, url: string, isLoading: boolean, failed: boolean, printFailed: boolean, printDistorted: boolean, hasImage: boolean }[] {
    const sideImages: { name: string, locationLabel: string, rawSideName: string, url: string, isLoading: boolean, failed: boolean, printFailed: boolean, printDistorted: boolean, hasImage: boolean }[] = [];

    const style = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));
    if (!style) {
      return sideImages;
    }

    const variantKey = `${product.productId}_${variant.styleId}`;
    const currentSide = this.getProductCurrentSide(product.productId);
    const activeSides = this.variantActiveSides.get(variantKey) || [currentSide];

    activeSides.forEach(activeSide => {
      const sideConfig = this.getSideConfigForCurrentSide(activeSide);
      if (!sideConfig) return;
      // Show side if it has the specific image OR any fallback image on the style
      const hasAnyImage = style[sideConfig.imageField]
        || style.ImageFilePath_Front || style.ImageFilePath_Back
        || style.ImageFilePath_SleeveLeft || style.ImageFilePath_SleeveRight;
      if (hasAnyImage) {
        const displayName = sideConfig.displayName;
        const url = this.getVariantSidePreviewUrl(product, variant, displayName);
        const isLoading = this.isPreviewLoading(product, variant, displayName);
        // WI-1524 fix #1: surface preview and print-export failures per side (see hasPreviewFailed
        // / hasPrintExportFailure) so the template can show a visible error + Retry instead of a
        // silently-blank thumbnail or a print file that never actually got generated.
        const failed = this.hasPreviewFailed(product, variant, displayName);
        const printDistorted = this.hasPrintExportDistortion(product, variant, activeSide);
        // A distorted side is reported as distorted, not as a generic "no print file" failure.
        const printFailed = !printDistorted && this.hasPrintExportFailure(product, variant, activeSide);
        // Progressive preview: a fast, lower-res placeholder (fireFastPlaceholderPreview) can land
        // in backgroundUrls before the slow, full-resolution export-job pipeline finishes — so
        // `hasImage` (an actual, non-empty cached URL) is checked separately from `isLoading`
        // (whether the slow pipeline is still working) instead of assuming "loading" means
        // "nothing to show yet". The template uses this to show the placeholder with a small
        // "still refining" badge, instead of hiding the image behind a full spinner until the
        // slow pipeline completes.
        const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;
        const hasImage = !!this.backgroundUrls.get(cacheKey);
        // The card label shows the print LOCATION actually selected on the master products page
        // (e.g. "Shoulder Left"), not the generic side name ("Left") — same source/fallback chain
        // prepareFinalPayloadAndSave already sends as SideName, so the label matches the payload.
        const matchedSide = this.matchSideByName(variant.sides || [], activeSide);
        const locationLabel = matchedSide?.InkSoftLocationName || getDefaultDecorationLocationName(activeSide) || displayName;
        sideImages.push({ name: displayName, locationLabel, rawSideName: activeSide, url, isLoading, failed, printFailed, printDistorted, hasImage });
      }
    });

    return sideImages;
  }

  /**
   * WI-1524 fix #2: enumerate every currently-selected color/side with a failed preview or a
   * print export that never produced a download file, for the pre-save confirmation gate in
   * continueToNextStep(). Reuses getVariantSideImages (the same per-side failure flags the color
   * cards render) rather than re-deriving the failure logic here.
   */
  private collectPreviewAndPrintFailures(): string[] {
    const failures: string[] = [];
    this.selectedProducts.forEach(product => {
      product.selectedColors.forEach(variant => {
        this.getVariantSideImages(product, variant).forEach(side => {
          if (side.failed) {
            failures.push(`${product.productName} — ${variant.colorName} — ${side.name} (preview)`);
          }
          if (side.printFailed) {
            failures.push(`${product.productName} — ${variant.colorName} — ${side.name} (print file)`);
          }
        });
      });
    });
    return failures;
  }

  // Tracks which (projectId_side) placements we've already tried to fetch, so the template
  // getter getSidePrintDimensions() fetches each side's DH metadata at most once.
  private printDimsFetchAttempted = new Set<string>();

  // Print dimensions (inches) for a side, read from its loaded element placement. Used by the
  // template to show "W" x H"" per side. Returns null until the placement is available.
  getSidePrintDimensions(variant: ColorVariant, sideName: string): { width: number; height: number } | null {
    if (!variant?.sides || !sideName) return null;
    const normalized = sideName.toLowerCase().replace(/\s+/g, '_');
    const side = variant.sides.find((s: any) => {
      const sName = (s.SideName || s.Name || '').toLowerCase().replace(/\s+/g, '_');
      return sName === normalized || sName.includes(normalized) || normalized.includes(sName);
    });

    const rawElement = side?.elementPlacement || side?.ElementPlacement;
    if (!rawElement) {
      // Not loaded yet — fetch this side's DH metadata once so unedited sides also show a size.
      this.ensureSidePlacementLoaded(variant, side, sideName);
      return null;
    }

    let element: any;
    try {
      element = typeof rawElement === 'string' ? JSON.parse(rawElement) : rawElement;
    } catch {
      return null;
    }
    if (element == null || element.width == null || element.height == null) return null;

    const width = Number(element.width);
    const height = Number(element.height);
    if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) return null;
    return { width, height };
  }

  // Ensure a side's element placement is loaded so getSidePrintDimensions() can show a size on
  // sides the user hasn't opened yet. Fetches the DH metadata at most once per (projectId_side).
  private ensureSidePlacementLoaded(variant: ColorVariant, side: any, sideName: string): void {
    if (!side) return;
    const projectId = side.ProjectId
      || this.getSideProjectIdFromVariantSides(variant, sideName)
      || variant.projectId;
    if (!projectId) return;
    const key = `${projectId}_${sideName.toLowerCase().replace(/\s+/g, '_')}`;
    if (this.printDimsFetchAttempted.has(key)) return;
    this.printDimsFetchAttempted.add(key);
    this.fetchAndStoreMetadata(projectId, variant, sideName);
  }

  // Get logo URL for a specific side
  getSideLogoUrl(variant: ColorVariant, sideName: string): string {
    // Check if there's a custom logo for this side
    if (variant.sideLogos && variant.sideLogos[sideName]) {
      return variant.sideLogos[sideName];
    }
    // Also check normalized side name variations
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    if (variant.sideLogos && variant.sideLogos[sideNorm]) {
      return variant.sideLogos[sideNorm];
    }
    // In edit mode, extract logo URL from saved Customizations per side
    if (this.isEditMode && variant.sides && variant.sides.length > 0) {
      const sideData = variant.sides.find((s: any) => {
        const sName = (s.SideName || '').toLowerCase().replace(/\s+/g, '_');
        return sName === sideNorm || sName.includes(sideNorm) || sideNorm.includes(sName);
      });
      if (sideData?.Customizations) {
        const cust = typeof sideData.Customizations === 'string'
          ? JSON.parse(sideData.Customizations) : sideData.Customizations;
        const logoUrl = cust?.classes?.logo?.url;
        if (logoUrl) return logoUrl;
      }
    }
    // In edit mode, also check the locked variant's Customizations (for new colors that inherit the logo)
    if (this.isEditMode) {
      const lockedVariant = this.getLockedVariantForProduct(variant);
      if (lockedVariant && lockedVariant !== variant && lockedVariant.sides && lockedVariant.sides.length > 0) {
        const lockedSideData = lockedVariant.sides.find((s: any) => {
          const sName = (s.SideName || '').toLowerCase().replace(/\s+/g, '_');
          return sName === sideNorm || sName.includes(sideNorm) || sideNorm.includes(sName);
        });
        if (lockedSideData?.Customizations) {
          const cust = typeof lockedSideData.Customizations === 'string'
            ? JSON.parse(lockedSideData.Customizations) : lockedSideData.Customizations;
          const logoUrl = cust?.classes?.logo?.url;
          if (logoUrl) return logoUrl;
        }
      }
    }
    // Fall back to variant-level custom logo
    if (variant.variantDesignUrl && !variant.useDefaultDesign) {
      return variant.variantDesignUrl;
    }
    // Fall back to design data — prefer designUrl (actual logo) over previewUrl (product mockup)
    return this.designData?.designUrl || this.designData?.logoUrl || this.designData?.previewUrl || '';
  }

  // Extract logo URL from variant's saved Customizations for a given side
  getLogoUrlFromSideCustomizations(variant: ColorVariant, sideName: string): string | null {
    if (!variant.sides || variant.sides.length === 0) return null;
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const sideData = variant.sides.find((s: any) => {
      const sName = (s.SideName || '').toLowerCase().replace(/\s+/g, '_');
      return sName === sideNorm || sName.includes(sideNorm) || sideNorm.includes(sName);
    });
    if (!sideData?.Customizations) return null;
    const cust = typeof sideData.Customizations === 'string'
      ? JSON.parse(sideData.Customizations) : sideData.Customizations;
    return cust?.classes?.logo?.url || null;
  }

  // Get logo URL for a side from the locked variant's customizations
  getLockedVariantLogoForSide(lockedVariant: ColorVariant | undefined, sideName: string): string {
    if (lockedVariant) {
      // Check sideLogos first (user-changed logo)
      const displayName = this.getDisplaySideName(sideName);
      if (lockedVariant.sideLogos?.[displayName]) return lockedVariant.sideLogos[displayName];
      // Extract from saved Customizations
      const fromCust = this.getLogoUrlFromSideCustomizations(lockedVariant, sideName);
      if (fromCust) return fromCust;
    }
    return this.designData?.designUrl || this.designData?.previewUrl || '';
  }

  // Check if a side has a custom logo
  hasSideCustomLogo(variant: ColorVariant, sideName: string): boolean {
    return !!(variant.sideLogos && variant.sideLogos[sideName]);
  }

  // Remove custom logo for a specific side
  removeSideLogo(variant: ColorVariant, sideName: string): void {
    if (variant.sideLogos && variant.sideLogos[sideName]) {
      delete variant.sideLogos[sideName];
      this.notification.info(`Reverted to default logo for ${sideName}`, 'Info');
    }
  }

  // Trigger file input for a specific side
  triggerSideFileInput(productId: string, variantIdx: number, sideIdx: number): void {
    const elementId = `file-${productId}-${variantIdx}-${sideIdx}`;
    const fileInput = document.getElementById(elementId) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    } else {
      console.error('❌ File input not found:', elementId);
    }
  }

  // Same allow-list as upload-design.component.ts's handleFile() — PNG/JPG/PDF/SVG. UX gate
  // only; the server-side allow-list in UploadHuddleLogoHandler is the real control.
  private isSupportedDesignFile(file: File): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf', '.svg'];
    const fileExtension = '.' + (file.name.split('.').pop() || '').toLowerCase();
    // Deliberately an OR: some browsers report an empty file.type for .svg, so the extension
    // check is the reliable half.
    return allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
  }

  private isDesignFileTooLarge(file: File): boolean {
    const maxSize = 25 * 1024 * 1024; // 25 MB, same cap as upload-design.component.ts
    return file.size > maxSize;
  }

  // Handle file selection for a specific side
  onSideLogoSelected(product: ProductColorVariant, variant: ColorVariant, sideName: string, event: any): void {
    const file = event.target.files[0];
    event.target.value = ''; // reset so re-picking the same file fires change
    if (!file) return;

    if (!this.isSupportedDesignFile(file)) {
      this.notification.error('File type not supported. Please upload PNG, JPG, PDF, or SVG files.', 'Error');
      return;
    }
    if (this.isDesignFileTooLarge(file)) {
      this.notification.error('File size exceeds 25 MB limit.', 'Error');
      return;
    }

    this.loader.show('Updating logo…', 'Applying to selected color variant. This may take a moment.');
    // Same trim pipeline as the in-editor upload-button override and upload-design.component.ts —
    // a PNG/SVG exported with blank padding around the real mark otherwise renders smaller/offset
    // once placed into the print zone.
    this.uploadOverride.processAndUploadFile(file)
      .then(fullLogoUrl => {
        this.loader.hide();
        if (!fullLogoUrl) {
          this.notification.error('Failed to upload logo', 'Error');
          return;
        }
        this.notification.success(`Logo updated for ${sideName}`, 'Success');
        // Apply the uploaded logo to this variant's side. Extracted so the bulk
        // "Imprint Locations" module can reuse the exact same per-variant apply path.
        this.applyLogoToVariantSide(product, variant, sideName, fullLogoUrl);
      })
      .catch(err => {
        console.error(`❌ Error uploading logo for ${sideName}:`, err);
        this.notification.error('Failed to upload logo', 'Error');
        this.loader.hide();
      });
  }

  /**
   * Apply an already-uploaded logo URL to ONE variant's side: store it on sideLogos, create a
   * fresh DesignHuddle project seeded with the logo, refresh the cached preview, and export.
   *
   * Extracted verbatim from onSideLogoSelected's post-upload block so the single per-side upload
   * and the bulk "Imprint Locations" module share one code path (single source of truth).
   *
   * @param propagateReplicas when false, skips propagateLogoToReplicas — set by callers (e.g. the
   *   imprint module) that already iterate EVERY variant themselves, to avoid redundant/duplicate
   *   DesignHuddle project bursts on InkSoft products. Defaults to true to preserve the original
   *   onSideLogoSelected behavior.
   */
  private applyLogoToVariantSide(
    product: ProductColorVariant,
    variant: ColorVariant,
    sideName: string,
    fullLogoUrl: string,
    propagateReplicas: boolean = true,
    // WI-1578: only ever passed by applyImprintGraphicToVariants (propagateReplicas=false), which
    // applies the SAME fullLogoUrl to every selected color in one batch. Without this, every
    // variant unconditionally created its OWN project for an identical graphic — the existing
    // findSideProjectOwner/getSharedSideProjectId reuse helpers can't be reused here as-is because
    // they explicitly SKIP any color with a sideLogos entry for this side (their own invariant for
    // the "generic default design" case), and applyImprintGraphicToVariants deliberately sets that
    // entry on EVERY variant first — so every one of them would fail that check and look like it
    // needs its own project. This is a batch-call-scoped map instead: the first variant processed
    // registers its in-flight creation; every later variant in the SAME batch awaits and reuses it.
    sharedCreations?: Map<string, Promise<string>>
  ): void {
    // Initialize sideLogos if not exists
    if (!variant.sideLogos) {
      variant.sideLogos = {};
    }
    // Store with both display name (for getSideLogoUrl) and normalized name (for createSideCustomizations)
    variant.sideLogos[sideName] = fullLogoUrl;
    const sideNormKey = sideName.toLowerCase().replace(/\s+/g, '_');
    if (sideNormKey !== sideName) {
      variant.sideLogos[sideNormKey] = fullLogoUrl;
    }

    // Logo changed — create new project with new logo, then export
    const variantKey = `${product.productId}_${variant.styleId}`;
    const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
    const sideInfo = this.getSideInfoForSide(product.productId, sideNorm);

    if (sideInfo) {
      const mediaPosition = this.getMediaPositionForSide(variant, sideNorm);

      // Set up export tracking
      const existingExport = this.pendingExports.get(variantKey);
      if (existingExport) {
        existingExport.pending++;
      } else {
        this.pendingExports.set(variantKey, { pending: 1, completed: 0, sideJobIds: new Map() });
      }
      // Artwork swapped: the tracked print file shows the old logo.
      this.invalidatePrintExport(product, variant, sideName);

      // Logo changed — increment DesignId offset for this variant
      this.incrementDesignIdOffset(product.productId, variant.styleId);

      // Mark variant as migrated after logo change (InkSoft migration)
      if (this.isInksoftProduct && !variant.isMigrated) {
        variant.isMigrated = true;
      }

      const applyProjectResult = (projectId: string) => {
        if (projectId) {
          this.variantSideProjects.set(`${variantKey}_${sideNorm}`, projectId);
          // Hit metadata API to get elementPlacement/zonePlacement
          this.fetchAndStoreMetadata(projectId, variant, sideNorm);
          // Regenerate with the new logo via renderSharedProjectPreview (not
          // generatePreviewForCurrentSide/generateSidePreview, whose own guard would silently skip
          // the whole regeneration if backgroundUrls already has a value) — this keeps the existing
          // preview visible, with the fast placeholder + refining badge overlaid, until the new
          // render actually overwrites it, instead of flashing to blank first.
          const displayName = this.getDisplaySideName(sideNorm);
          const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;
          this.generatingPreviews.delete(cacheKey);
          this.fireFastPlaceholderForSharedProject(product, variant, sideNorm, cacheKey, fullLogoUrl, mediaPosition);
          this.renderSharedProjectPreview(product, variant, sideNorm, displayName, projectId, cacheKey);
          this.exportCurrentSide(product, variant, sideNorm);

          // Propagate logo change to customProductImage replicas
          if (propagateReplicas && this.isInksoftProduct && variant.variantSource === 'customizedProduct') {
            this.propagateLogoToReplicas(product, variant, sideNorm, fullLogoUrl, projectId, sideInfo);
          }
        } else {
          this.markExportCompleted(variantKey, sideNorm, '');
        }
      };

      const existingShared = sharedCreations?.get(sideNorm);
      if (existingShared) {
        // A sibling variant earlier in this SAME batch already kicked off (or finished) creating
        // the one project every color in this batch should share — await and reuse it instead of
        // creating our own redundant one.
        existingShared.then(applyProjectResult).catch(err => {
          console.error(`❌ Error awaiting shared project after logo change:`, err);
          this.markExportCompleted(variantKey, sideNorm, '');
        });
      } else {
        // New project seeded top-anchored via helper (a freshly uploaded logo has no
        // user placement yet, so it defaults to chest-print top-anchor).
        const projectTitle = this.generateRandomProjectTitle();
        const creation: Promise<string> = this.buildSeedCustomization(fullLogoUrl, product.productId, sideNorm, mediaPosition)
          .then(customizationObject => this.createProjectForSide(sideInfo.templateId, projectTitle, customizationObject));
        if (sharedCreations) {
          // Register synchronously (before any await resolves) so a sibling variant processed
          // later in the SAME batch sees this in-flight creation and borrows it instead of racing
          // to create its own — same pattern pendingSideProjectCreations uses elsewhere.
          sharedCreations.set(sideNorm, creation);
        }
        creation.then(applyProjectResult).catch(err => {
          console.error(`❌ Error creating project after logo change:`, err);
          this.markExportCompleted(variantKey, sideNorm, '');
        });
      }
    }
  }

  // ==================== Bulk Imprint-Locations Module ====================
  // A per-product control that lets a user add / replace / remove a secondary imprint location
  // (a print side + artwork graphic) across ALL color variants of the product at once, instead of
  // repeating the per-variant "+ Add Side" + logo upload for every color. It is a thin batch
  // wrapper over the existing single-variant primitives (addSideToSingleVariant +
  // applyLogoToVariantSide); the source of truth for what is saved stays on variant.sides[] /
  // variant.sideLogos / variantActiveSides, so imprints round-trip through the pricing page with
  // no new persistence.

  /**
   * Derive the imprint entries (secondary / non-front print locations) shown in the module for a
   * product, from the current state of its variants. Each distinct non-front active side becomes
   * one entry; the artwork thumbnail is taken from the first variant that has a logo for that side.
   * Deriving from the saved source of truth means entries reflect sides added via the per-variant
   * "+ Add Side" control and survive the pricing round-trip without extra persistence.
   */
  getProductImprints(product: ProductColorVariant): { side: string; displayName: string; artworkUrl: string; removable: boolean }[] {
    const entries: { side: string; displayName: string; artworkUrl: string; removable: boolean }[] = [];
    if (!product?.selectedColors) return entries;
    const seen = new Set<string>();

    // The product's actual base side — shown first so "Edit Design"/"Replace Artwork" are
    // available for it too (WI-1578 follow-up), but it's never removable like an added module
    // side. This was hardcoded to 'front', so a product whose design was actually started on a
    // different side (Back, a sleeve — getProductCurrentSide / currentSideNameMap, carried over
    // from master-product-listing's own currently-viewed side) still showed a "Front" row here
    // with no artwork, while the side the user actually customized appeared as a removable
    // secondary entry instead of the protected primary one.
    const baseSide = this.canonicalSide(this.getProductCurrentSide(product.productId));
    seen.add(baseSide);
    entries.push({
      side: baseSide,
      displayName: this.getDisplaySideName(baseSide),
      artworkUrl: this.findImprintArtworkUrl(product, baseSide),
      removable: false
    });

    for (const variant of product.selectedColors) {
      const variantKey = `${product.productId}_${variant.styleId}`;
      const activeSides = this.variantActiveSides.get(variantKey) || [];
      for (const sideNorm of activeSides) {
        const canon = this.canonicalSide(sideNorm);
        if (seen.has(canon)) continue;
        seen.add(canon);
        entries.push({
          side: sideNorm,
          displayName: this.getDisplaySideName(sideNorm),
          artworkUrl: this.findImprintArtworkUrl(product, sideNorm),
          removable: true
        });
      }
    }
    return entries;
  }

  /**
   * Product's available sides eligible to be added as a new imprint location: everything except the
   * base "front" side and sides already present as an imprint entry. Canonical comparison keeps
   * "left_sleeve" vs "sleeve_left" naming variants from drifting.
   */
  getAvailableImprintSides(product: ProductColorVariant): { name: string; displayName: string }[] {
    if (!product) return [];
    // Excludes whatever getProductImprints already listed (the base side + every added imprint)
    // via `existing` alone — no separate hardcoded 'front' exclusion. getProductImprints' base
    // side is the product's ACTUAL current side (getProductCurrentSide), not always Front, so a
    // product started on e.g. Right Sleeve must still offer Front here as addable; a leftover
    // `!== 'front'` filter would wrongly exclude it even though it's no longer the base side and
    // isn't in `existing`.
    const existing = new Set(this.getProductImprints(product).map(e => this.canonicalSide(e.side)));
    return this.getProductAvailableSides(product.productId)
      .filter(s => !existing.has(this.canonicalSide(s.name)))
      .map(s => ({ name: s.name, displayName: s.displayName }));
  }

  /** Record the imprint location chosen in the add-form dropdown for a product. */
  onImprintSideSelected(productId: string, side: string): void {
    const draft = this.imprintDraft.get(productId) || { side: '', fileName: '', logoUrl: null, uploading: false };
    draft.side = side || '';
    this.imprintDraft.set(productId, draft);
  }

  /**
   * Currently-chosen side for the add-form dropdown — bound back onto the <select>'s own [value]
   * in the template. Some browsers silently display the single remaining enabled option as
   * selected by default (the placeholder is `disabled`, so it can't hold the visible selection)
   * WITHOUT ever firing a native `change` event, which previously left the dropdown showing e.g.
   * "Front" selected while the component had no idea a side was chosen at all. Forcing [value] back
   * to this getter on every change-detection cycle keeps the DOM in sync with what the component
   * actually knows, so the dropdown can never visually show a selection the user didn't explicitly
   * make — it always reverts to the placeholder until onImprintSideSelected() genuinely fires.
   */
  getImprintDraftSide(productId: string): string {
    return this.imprintDraft.get(productId)?.side || '';
  }

  /**
   * Graphic picked in the add-form: uploads it IMMEDIATELY, same flow as upload-design.component's
   * auto-upload-on-pick (validate → trim/process → upload → feedback), instead of staging a raw
   * File and only uploading later when "Add to All Colors" is clicked. "Add to All Colors" then
   * just applies the already-uploaded URL — no second upload, no wait at that point.
   */
  async onImprintFileSelected(productId: string, event: any): Promise<void> {
    const file: File | null = event?.target?.files?.[0] || null;
    if (event?.target) event.target.value = ''; // reset so re-picking the same file fires change
    if (!file) return;

    const draft = this.imprintDraft.get(productId) || { side: '', fileName: '', logoUrl: null, uploading: false };

    if (!this.isSupportedDesignFile(file)) {
      this.notification.error('File type not supported. Please upload PNG, JPG, PDF, or SVG files.', 'Error');
      return;
    }
    if (this.isDesignFileTooLarge(file)) {
      this.notification.error('File size exceeds 25 MB limit.', 'Error');
      return;
    }

    draft.fileName = file.name;
    draft.logoUrl = null;
    draft.uploading = true;
    this.imprintDraft.set(productId, draft);

    try {
      const logoUrl = await this.uploadOverride.processAndUploadFile(file);
      if (!logoUrl) {
        this.notification.error('Failed to upload graphic.', 'Upload failed');
        draft.fileName = '';
        return;
      }
      draft.logoUrl = logoUrl;
    } catch (err) {
      console.error('❌ Imprint graphic upload error:', err);
      this.notification.error('Failed to upload graphic.', 'Upload failed');
      draft.fileName = '';
    } finally {
      draft.uploading = false;
      this.imprintDraft.set(productId, draft);
    }
  }

  /** True while the add-form's graphic is uploading for this product (disables Add to All Colors). */
  isImprintUploading(productId: string): boolean {
    return !!this.imprintDraft.get(productId)?.uploading;
  }

  /** File name currently staged in the add-form (for the template's "chosen: …" label). */
  getImprintDraftFileName(productId: string): string {
    return this.imprintDraft.get(productId)?.fileName || '';
  }

  /**
   * WI-1578: per-entry "Edit Design" action — opens the DH editor once, on the locked/base color's
   * project for this side (the existing single-variant flow, openEditorForSide), and arranges for
   * Save & Close to share the edited design with every other selected color (size/position/
   * rotation/text, not just the graphic file — see propagateEditedDesignToAllColors).
   */
  async editImprintDesignForAllColors(product: ProductColorVariant, entry: { side: string; displayName: string }): Promise<void> {
    const lockedVariant = (product.selectedColors || [])
      .find(v => Number(v.styleId) === Number(product.lockedStyleId));
    if (!lockedVariant) {
      this.notification.warning('No base color found for this product.', 'Cannot edit design');
      return;
    }

    const sideNorm = entry.side.toLowerCase().replace(/\s+/g, '_');
    const lockedProjectId = this.variantSideProjects.get(`${product.productId}_${lockedVariant.styleId}_${sideNorm}`)
      || this.getSideProjectIdFromVariantSides(lockedVariant, sideNorm);

    // Any OTHER selected color currently pointing at a DIFFERENT project for this side already has
    // its own design here (a per-color "Change design", its own Replace Artwork, etc.) — Save &
    // Close below would otherwise replace it with the locked color's edited design too.
    const others = (product.selectedColors || []).filter(v => v !== lockedVariant);
    const willOverwrite = others.filter(v => {
      const otherProjectId = this.variantSideProjects.get(`${product.productId}_${v.styleId}_${sideNorm}`)
        || this.getSideProjectIdFromVariantSides(v, sideNorm);
      return otherProjectId && otherProjectId !== lockedProjectId;
    });

    const totalColors = (product.selectedColors || []).length;
    let excludeStyleIds = new Set<number>();

    if (willOverwrite.length > 0) {
      // Declining here does NOT abort the edit — it only protects these specific colors from being
      // overwritten. The locked color and every OTHER (not already customized) color still get the
      // edited design on Save & Close either way.
      const names = willOverwrite.map(v => v.colorName).join(', ');
      const plural = willOverwrite.length !== 1;
      const confirmed = await this.showConfirm(
        `${names} already ${plural ? 'have' : 'has'} ${plural ? 'their own' : 'its own'} ${entry.displayName} design.\n\n` +
        `Every other color will get this edited design either way. Choose whether to replace ${plural ? 'these too' : 'this one too'}, ` +
        `or keep ${plural ? 'their' : 'its'} current design.`,
        {
          title: 'Design already customized',
          confirmLabel: `Replace ${plural ? 'all, including these' : 'all, including this one'}`,
          cancelLabel: `Keep ${plural ? 'their' : 'its'} design`,
          allowOverlayDismiss: false,
          warn: true
        }
      );
      // X (null) aborts entirely — nothing happens, not even the locked color's own edit. Only the
      // Cancel button (false) means "proceed, but keep these colors' own design" — see showConfirm.
      if (confirmed === null) return;
      if (confirmed === false) {
        excludeStyleIds = new Set(willOverwrite.map(v => Number(v.styleId)));
      }
    } else {
      // No existing per-color customization to protect yet — still confirm up front (even the
      // very first time this is used for a product/side) so it's never a surprise that editing
      // one color's design here replaces it on every selected color.
      const confirmed = await this.showConfirm(
        `Editing ${entry.displayName} here opens the design editor for the base color. ` +
        `Once you Save & Close, this exact design — size, position, rotation, text and all — ` +
        `will be applied to all ${totalColors} selected color${totalColors !== 1 ? 's' : ''} of ${product.productName}.`,
        { title: 'Edit design for all colors', confirmLabel: 'Continue', cancelLabel: 'Cancel', allowOverlayDismiss: false, compactText: true }
      );
      if (!confirmed) return;
    }

    this.editorApplyToAllColors = true;
    this.editorApplyToAllColorsExcludeStyleIds = excludeStyleIds;
    this.openEditorForSide(product, lockedVariant, entry.displayName);
  }

  /**
   * Add-form action: the chosen side + already-uploaded graphic (uploaded on file selection, see
   * onImprintFileSelected) are placed on every color variant of the product. Project creation is
   * done sequentially (one variant at a time) to avoid an unbounded burst of createProject calls
   * for a many-color product; a single shared loader wraps the batch and one summary toast is shown.
   */
  async applyImprintToAllVariants(product: ProductColorVariant): Promise<void> {
    const productId = product.productId;
    const draft = this.imprintDraft.get(productId) || { side: '', fileName: '', logoUrl: null, uploading: false };

    if (!draft.side) {
      this.notification.warning('Please select an imprint location.', 'Missing location');
      return;
    }
    if (draft.uploading) {
      this.notification.warning('Still uploading the graphic — please wait a moment.', 'Upload in progress');
      return;
    }
    if (!draft.logoUrl) {
      this.notification.warning('Please upload a graphic.', 'Missing artwork');
      return;
    }
    const variants = product.selectedColors || [];
    if (variants.length === 0) {
      this.notification.warning('No colors selected for this product.', 'No colors');
      return;
    }

    // Snapshot into local consts and clear the shared draft IMMEDIATELY, before the first
    // `await` — this whole method is async (per-color export/preview), and nothing previously
    // stopped the user from picking the NEXT location/file and clicking "Add to All Colors" again
    // while the FIRST call was still in flight. Both calls shared and mutated the SAME
    // `imprintDraft` object, so the first (still-running) call re-read `draft.side`/`draft.logoUrl`
    // AFTER the second click had already overwritten them — applying the second location's graphic
    // to the first location, or vice versa ("the last one selected applies to the previous one").
    // Clearing the map entry now means a second click starts from a fresh, independent draft
    // object instead of reaching into this call's in-flight one.
    const side = draft.side;
    const logoUrl = draft.logoUrl;
    this.imprintDraft.delete(productId);

    const colorLabel = `${variants.length} color${variants.length !== 1 ? 's' : ''}`;
    // No full-page blocking loader — same reasoning as modifyImprintArtwork (Replace Artwork) and
    // the WI-1521 single-color-add fix: each affected color's own Design Variants card already
    // shows progressive feedback (the fast thumbnailTemplateUrl placeholder lands almost
    // immediately, then the "Loading exported image" refining badge while the slow export
    // finishes), so a page-wide overlay sitting there until the SLOW export completes for every
    // color is redundant and just blocks the user from the rest of the page for no reason.
    try {
      await this.applyImprintGraphicToVariants(product, side, logoUrl);
      this.notification.success(`Imprint applied to ${colorLabel}.`, 'Success');
    } catch (err) {
      console.error('❌ applyImprintToAllVariants failed:', err);
      this.notification.error('Failed to apply imprint.', 'Error');
    }
  }

  /** Per-entry action: replace the artwork on an existing imprint across all color variants. */
  async modifyImprintArtwork(product: ProductColorVariant, entry: { side: string }, event: any): Promise<void> {
    const file: File | null = event?.target?.files?.[0] || null;
    if (event?.target) event.target.value = ''; // reset so re-picking the same file fires change
    if (!file) return;

    const variants = product.selectedColors || [];
    const colorLabel = `${variants.length} color${variants.length !== 1 ? 's' : ''}`;
    // No full-page blocking loader — same reasoning as the WI-1521 single-color-add fix: each
    // affected color's own Design Variants card already shows progressive feedback (fast
    // placeholder, then the "Loading exported image" refining badge) as applyImprintGraphicToVariants
    // processes it, so a page-wide overlay on top of that is redundant and hides those cards from
    // the user for no reason.
    try {
      const logoUrl = await this.uploadImprintGraphic(file);
      if (!logoUrl) {
        this.notification.error('Failed to upload graphic.', 'Upload failed');
        return;
      }
      // WI-1522 fix #3: same as applyImprintToAllVariants — wait for the actual per-color work.
      await this.applyImprintGraphicToVariants(product, entry.side, logoUrl);
      this.notification.success(`Imprint artwork updated for ${colorLabel}.`, 'Success');
    } catch (err) {
      console.error('❌ modifyImprintArtwork failed:', err);
      this.notification.error('Failed to update artwork.', 'Error');
    }
  }

  /**
   * Per-entry action: remove an imprint location (and its artwork) from ALL color variants. Cleans
   * only in-memory state for that single side on each variant — active sides, variant.sides[],
   * sideLogos, per-side project id, and the shared preview caches. It does NOT delete Azure blobs
   * (a blob may be shared across colors/sides) and does NOT touch the pendingExports pending/
   * completed counters (they self-balance as any in-flight export for the side completes), only the
   * side's stale job/download sub-entries.
   */
  async removeImprintFromAllVariants(product: ProductColorVariant, entry: { side: string; displayName: string }): Promise<void> {
    const confirmed = await this.showConfirm(
      `Remove the ${entry.displayName} imprint from all colors of ${product.productName}?`,
      { title: 'Remove imprint location', confirmLabel: 'Remove', destructive: true }
    );
    if (!confirmed) return;

    const sideNorm = entry.side.toLowerCase().replace(/\s+/g, '_');
    const canon = this.canonicalSide(entry.side);
    const displaySide = this.getDisplaySideName(sideNorm);

    for (const variant of product.selectedColors || []) {
      const variantKey = `${product.productId}_${variant.styleId}`;

      // Active sides — drop just this side.
      const active = this.variantActiveSides.get(variantKey);
      if (active) {
        this.variantActiveSides.set(variantKey, active.filter(s => this.canonicalSide(s) !== canon));
      }

      // variant.sides[] — splice the matching side out.
      if (variant.sides) {
        variant.sides = variant.sides.filter(
          (s: any) => this.canonicalSide(s.SideName || s.Name || '') !== canon
        );
      }

      // sideLogos — delete every key (display + normalized variants) that maps to this side.
      if (variant.sideLogos) {
        for (const key of Object.keys(variant.sideLogos)) {
          if (this.canonicalSide(key) === canon) delete variant.sideLogos[key];
        }
      }

      // Per-side project id.
      this.variantSideProjects.forEach((_, key) => {
        if (key.startsWith(`${variantKey}_`) &&
            this.canonicalSide(key.substring(variantKey.length + 1)) === canon) {
          this.variantSideProjects.delete(key);
        }
      });

      // backgroundUrls / generatingPreviews / uploadingBackgrounds share the display-name key.
      const cacheKey = `${variantKey}_${displaySide}`;
      this.backgroundUrls.delete(cacheKey);
      this.generatingPreviews.delete(cacheKey);
      this.uploadingBackgrounds.delete(cacheKey);

      // pendingExports is a per-variant aggregate across ALL sides — never delete the whole entry.
      // Only clear this side's stale job/download sub-entries; leave pending/completed alone so an
      // in-flight export for the side still balances the counters when it resolves.
      const tracking = this.pendingExports.get(variantKey);
      if (tracking) {
        for (const k of [sideNorm, displaySide, entry.side]) {
          tracking.sideJobIds?.delete(k);
          tracking.sideDownloadUrls?.delete(k);
        }
      }
    }

    this.notification.success(`${entry.displayName} imprint removed from all colors.`, 'Removed');
  }

  /** trackBy for the imprint *ngFor so replacing/removing an entry doesn't rebuild the whole list. */
  trackByImprintSide(_index: number, entry: { side: string }): string {
    return entry.side;
  }

  // getAvailableImprintSides() returns a brand-new array of brand-new {name,displayName} objects
  // on every call (it's a .filter().map() over a fresh getProductImprints() result). The "Add
  // imprint location" <select>'s *ngFor had no trackBy, so without one Angular's default identity
  // diffing treats every option as newly added on EVERY change-detection cycle — not just when
  // the available sides actually change — and destroys+recreates the <option> elements each time.
  // That silently resets the native <select>'s DOM selection (no new `change` event fires, since
  // the reset is Angular's own DOM write, not user interaction), so a user could pick e.g. "Front"
  // and have it still look selected while the component's imprintDraft state already lost it —
  // "Add to All Colors" then reports "Please select an imprint location" even though a location
  // was just visibly chosen. Matches the same trackBy already used for the sibling imprint-entries
  // list just above this dropdown in the template.
  trackByAvailableSide(_index: number, s: { name: string }): string {
    return s.name;
  }

  /**
   * Upload a graphic via the existing upload-huddle-logo endpoint and resolve to its full Azure URL
   * (or '' on failure). Mirrors the upload in onSideLogoSelected, wrapped as a Promise for the batch.
   */
  // Resolves to '' on any failure (unsupported type, oversized, or upload error) — both callers
  // (applyImprintToAllVariants, modifyImprintArtwork) already show their own failure toast on an
  // empty result, so this stays silent to avoid a double message.
  private uploadImprintGraphic(file: File): Promise<string> {
    if (!this.isSupportedDesignFile(file) || this.isDesignFileTooLarge(file)) {
      return Promise.resolve('');
    }

    // Same trim pipeline as the per-side logo upload / in-editor upload-button override — see
    // onSideLogoSelected for why.
    return this.uploadOverride.processAndUploadFile(file)
      .then(url => url || '')
      .catch(err => {
        console.error('❌ Imprint graphic upload error:', err);
        return '';
      });
  }

  /**
   * Poll `predicate` until it returns true or `timeoutMs` elapses, then resolve either way — never
   * rejects, so a caller can always safely await it without its own try/catch just for this. Used
   * by applyImprintGraphicToVariants (WI-1522 fix #3) to await the tail of a fire-and-forget DH
   * chain (createProject/export-job/upload) that has no Promise-returning API of its own.
   */
  private waitUntil(predicate: () => boolean, timeoutMs: number = 60000, intervalMs: number = 250): Promise<void> {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (predicate() || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  /**
   * Processes `items` with at most `limit` in flight at once — a slot refills as soon as one item
   * finishes, instead of either firing everyone at once (the original "all colors sharing one DH
   * project hit export-job simultaneously" bug) or making every color wait for the FULL previous
   * color's settlement before even starting (correct, but needlessly slow — a borrowing color then
   * takes as long as owner+borrower combined instead of loading close to as fast as the owner).
   * Same MAX_CONCURRENT_VARIANT_PREVIEWS tradeoff this file already strikes elsewhere, reused here
   * for propagateEditedDesignToAllColors / applyImprintGraphicToVariants's per-color loops.
   */
  private async processWithConcurrencyLimit<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
    let index = 0;
    const runNext = async (): Promise<void> => {
      while (index < items.length) {
        const item = items[index++];
        await worker(item);
      }
    };
    const workerCount = Math.min(limit, items.length);
    await Promise.all(Array.from({ length: workerCount }, () => runNext()));
  }

  /**
   * Apply an uploaded graphic to a given side across every color variant of a product. For a side
   * not yet on a variant it registers the side (addSideToSingleVariant, which creates the project
   * using the sideLogo we pre-set); for a side already present it regenerates via
   * applyLogoToVariantSide. Replica propagation is skipped because this loop already touches every
   * variant.
   *
   * WI-1522 fix #3: addSideToSingleVariant/applyLogoToVariantSide are fire-and-forget (they kick
   * off a DH createProject/export-job/upload chain and return immediately) — the caller used to
   * hide its loader right after this loop returned, while every variant's export/preview was still
   * running in the background, letting a Builder save mid-flight. Since neither primitive has a
   * Promise-returning form (and both are shared with several other call sites, so converting them
   * is out of scope here), this snapshots each touched variant's export-completion count and
   * preview cache key BEFORE dispatching its work, then returns a Promise that only resolves once
   * every variant's export has advanced by the expected +1 AND its preview is no longer in flight —
   * bounded by waitUntil's timeout so a stuck variant can't hang the caller forever.
   */
  private async applyImprintGraphicToVariants(product: ProductColorVariant, side: string, logoUrl: string): Promise<void> {
    const sideNorm = side.toLowerCase().replace(/\s+/g, '_');
    const displayName = this.getDisplaySideName(sideNorm);

    // Pass 1: set the SAME logoUrl on every variant's sideLogos first, before any project
    // creation/reuse decision runs for ANY of them. addSideToSingleVariant/applyLogoToVariantSide
    // decide whether to share a project by comparing a variant's resolved logo against the locked
    // color's CURRENT sideLogos value (getLockedVariantLogoForSide) — if that decision ran inline,
    // per-variant, in a single pass (as this used to), a variant processed BEFORE the locked color
    // in product.selectedColors would compare against the locked color's NOT-YET-UPDATED logo,
    // see a mismatch, and wrongly create its own redundant project instead of sharing. Splitting
    // into two passes guarantees every variant (locked included) already has its final value before
    // any reuse decision is made, regardless of array order.
    for (const variant of product.selectedColors || []) {
      if (!variant.sideLogos) variant.sideLogos = {};
      variant.sideLogos[sideNorm] = logoUrl;
      variant.sideLogos[displayName] = logoUrl;
    }

    // Batch-scoped reuse map for the "existing side, replace artwork" branch below — every variant
    // in THIS call gets the identical logoUrl, so only the first one should actually create a DH
    // project; every other one awaits and shares it. See applyLogoToVariantSide's sharedCreations
    // param doc for why the usual findSideProjectOwner/getSharedSideProjectId helpers can't be used
    // here as-is (they skip any color with a sideLogos entry, which pass 1 above just set on all of
    // them). Scoped to this one call — never shared across separate Replace Artwork invocations.
    const sharedCreations = new Map<string, Promise<string>>();

    // Pass 2: decide project creation/reuse per variant, with every variant's sideLogos (including
    // the locked color's) already settled — and process variants with BOUNDED concurrency, not
    // fully sequential and not all-at-once. Every variant in this batch ends up sharing ONE DH
    // project for this side (either via sharedCreations here, or addSideToSingleVariant's own
    // owner/borrower sharing, both of which already handle a borrower starting either in the same
    // tick as the owner — via the in-flight promise — or after the owner has already finished — via
    // the registered variantSideProjects lookup) — firing ALL of their preview + print-export
    // requests against that one project at once means multiple concurrent export-job calls hit it
    // simultaneously, which DH does not reliably handle (confirmed live: some colors come back with
    // no print file even though nothing on our side actually errored). Fully sequential processing
    // fixed that but made later colors in the batch wait for everyone ahead of them too.
    // MAX_CONCURRENT_VARIANT_PREVIEWS in flight at once keeps contention low while letting most
    // colors load close to as fast as the first one.
    await this.processWithConcurrencyLimit(product.selectedColors || [], this.MAX_CONCURRENT_VARIANT_PREVIEWS, async (variant) => {
      const variantKey = `${product.productId}_${variant.styleId}`;
      const cacheKey = `${product.productId}_${variant.styleId}_${displayName}`;
      const startCompleted = this.pendingExports.get(variantKey)?.completed || 0;

      // NOT `variantActiveSides.some(...)` — in the create flow, loadSelectedProducts() seeds
      // variant.sides directly from product.Sides (the MASTER PRODUCT'S full list of available
      // print locations), and restoreFromSavedData() then treats variant.sides being non-empty as
      // "every one of these is already an active side for this variant" — so a side this variant
      // has never actually been customized for (e.g. Front, when the product was started on Right
      // Sleeve) can already appear "active" on the very first load. That wrongly routed a brand
      // new side through applyLogoToVariantSide (Replace Artwork, which assumes a real existing
      // project to regenerate) instead of addSideToSingleVariant (new side), producing a false
      // "Logo is stretched" / "No print file" flag for a side that was never actually distorted or
      // failed to export. Checking for a REAL project on THIS variant is immune to that stale
      // metadata, regardless of what variantActiveSides/variant.sides claims. (variant.sideLogos is
      // NOT a usable signal here — Pass 1 above already overwrote it with the new logo for every
      // variant, before this check runs, so it would always read "already has a logo" regardless
      // of history.)
      const alreadyHasSide = !!this.variantSideProjects.get(`${variantKey}_${sideNorm}`);

      if (alreadyHasSide) {
        // Existing side (e.g. Replace Artwork) — regenerate the project with the new graphic,
        // sharing ONE project across every variant in this batch (sharedCreations).
        this.applyLogoToVariantSide(product, variant, displayName, logoUrl, false, sharedCreations);
      } else {
        // New side — register it; the project is created with the pre-set graphic.
        this.addSideToSingleVariant(product, variant, side);
      }

      await this.waitUntil(() => {
        const tracking = this.pendingExports.get(variantKey);
        const wanted = startCompleted + 1;
        if (!tracking || tracking.completed < wanted) return false;
        return !this.generatingPreviews.has(cacheKey) && !this.uploadingBackgrounds.has(cacheKey);
      });
    });
  }

  /**
   * A freshly-uploaded imprint graphic's blob URL can transiently fail to load the very first time
   * (Azure blob read-after-write propagation delay right after upload) — the broken image was
   * reported as "thumbnail not showing the first time, fine after a subsequent change" (a later
   * change happens to reuse/revisit a URL that's had time to settle). Retries the SAME url once
   * after a short delay instead of leaving a permanently broken <img>; a dataset flag caps it to one
   * retry so a genuinely missing/invalid URL doesn't loop forever.
   */
  onImprintThumbError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img || img.dataset['retried']) return;
    img.dataset['retried'] = 'true';
    const src = img.src;
    setTimeout(() => {
      img.src = '';
      img.src = src;
    }, 1000);
  }

  /** First non-empty artwork URL for a side across the product's variants (for the entry thumbnail). */
  private findImprintArtworkUrl(product: ProductColorVariant, sideNorm: string): string {
    const displayName = this.getDisplaySideName(sideNorm);

    // Check the locked/base color FIRST — it's the color "Edit Design" always edits (see
    // editImprintDesignForAllColors), so the row's thumbnail must represent what editing it will
    // actually open, not whichever OTHER color happens to come first in product.selectedColors.
    const lockedVariant = (product.selectedColors || [])
      .find(v => Number(v.styleId) === Number(product.lockedStyleId));
    if (lockedVariant) {
      const lockedLogos = lockedVariant.sideLogos || {};
      const lockedUrl = lockedLogos[sideNorm] || lockedLogos[displayName];
      if (lockedUrl) return lockedUrl;
      const lockedFromCust = this.getLogoUrlFromSideCustomizations(lockedVariant, sideNorm);
      if (lockedFromCust) return lockedFromCust;
    }

    // Fall back to any OTHER color only when the locked color itself has nothing for this side yet.
    for (const variant of product.selectedColors || []) {
      if (variant === lockedVariant) continue;
      const logos = variant.sideLogos || {};
      const url = logos[sideNorm] || logos[displayName];
      if (url) return url;
      const fromCust = this.getLogoUrlFromSideCustomizations(variant, sideNorm);
      if (fromCust) return fromCust;
    }
    // Front's design very often comes from the originally-uploaded design file (this.designData),
    // never written into any variant's sideLogos/Customizations at all — those are only populated
    // by a per-side override (editor edit, Replace Artwork). Without this fallback, the always-
    // present Front row showed the placeholder icon even though every color visibly has a front
    // design, simply because this function never looked at designData.
    return this.designData?.designUrl || this.designData?.previewUrl || '';
  }
  // ==================== End Bulk Imprint-Locations Module ====================

  /**
   * Export a single side of a variant by calling export-job API
   */
  exportSingleSide(product: ProductColorVariant, variant: ColorVariant, sideName: string): void {
    if (!variant.projectId) {
      return;
    }

    // Get project details to find page_id for this side
    const projectDetails = this.projectDetailsMap.get(variant.projectId);
    if (!projectDetails) {
      this.http.get<any>(`${environment.baseUrl}/api/designhuddle/projects/${variant.projectId}/details`).subscribe({
        next: (details: any) => {
          this.projectDetailsMap.set(variant.projectId!, details);
          this.doExportSingleSide(product, variant, sideName, details);
        },
        error: (error) => {
          console.error(`❌ Error fetching project details for ${variant.projectId}:`, error);
        }
      });
      return;
    }

    this.doExportSingleSide(product, variant, sideName, projectDetails);
  }

  /**
   * Actually perform export for a single side using project details
   */
  doExportSingleSide(product: ProductColorVariant, variant: ColorVariant, sideName: string, projectDetails: any): void {
    // Get pages from project details
    const pages = projectDetails?.data?.pages || projectDetails?.pages || [];
    const sideNameNormalized = sideName.toLowerCase().replace(/\s+/g, '_');

    // Find matching page by name
    const matchedPage = pages.find((page: any) => {
      const pageName = (page.name || page.page_name || '').toLowerCase().replace(/\s+/g, '_');
      return pageName === sideNameNormalized ||
             pageName.includes(sideNameNormalized) ||
             sideNameNormalized.includes(pageName);
    });

    if (!matchedPage) {
      return;
    }

    const pageId = matchedPage.page_id || matchedPage.id || '';
    const pageName = matchedPage.name || matchedPage.page_name || sideName;

    if (!pageId) {
      return;
    }

    // Frame/asset aspect guard (see exportCurrentSide / frame-fit.ts): a stretched logo frame must
    // never reach a print file. Surface it immediately — this path is user-initiated.
    this.isSideFrameDistorted(product, variant, pageName).then(distorted => {
    if (distorted) {
      this.flagPrintExportDistorted(product, variant, pageName);
      this.notification.error(`The logo on ${variant.colorName} - ${sideName} is stretched. Open the editor and re-add the logo, then save again.`, 'Logo is stretched');
      return;
    }

    // Create customization for this specific side
    const sideCustomizations = this.createSideCustomizations(product, variant, pageName, null);

    // fileName = pageId + sideName
    const fileName = `${pageId}_${sideNameNormalized}`;

    const apiUrl = `${environment.baseUrl}/api/designhuddle/projects/${variant.projectId}/export-job`;

    // Resolve the side's physical zone inches BEFORE building the body (awaits a DH /data
    // fetch only when not stored). width = round(zoneInches × 300) → 300 DPI at true size.
    this.resolveZoneInchesForSide(variant.projectId || '', variant, pageName).subscribe((zoneInches) => {
    const widthPx = this.computeExportWidthPx(zoneInches);
    const body: any = {
      format: 'png',
      fileName: fileName,
      page_id: pageId,
    };
    if (widthPx != null) body.width = widthPx;


    this.loader.show('Updating variant design…', 'This may take a moment.');
    this.http.post<any>(apiUrl, body).pipe(
      catchError(error => {
        console.error(`❌ Export error for ${variant.colorName} - ${sideName}:`, error);
        this.notification.error(`Failed to export ${sideName} for ${variant.colorName}`, 'Error');
        this.loader.hide();
        return of(null);
      })
    ).subscribe({
      next: (response: any) => {
        if (response) {
          const jobId = response?.data?.job_id || '';
          if (jobId) {
            variant.jobId = String(jobId);

            // Update the side's JobId in variant.sides
            if (variant.sides) {
              const matchedSide = variant.sides.find((side: any) => {
                const sName = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
                return sName === sideNameNormalized ||
                       sName.includes(sideNameNormalized) ||
                       sideNameNormalized.includes(sName);
              });
              if (matchedSide) {
                matchedSide.JobId = String(jobId);
                matchedSide.PageId = String(pageId);
              }
            }

            this.notification.success(`Export completed for ${variant.colorName} - ${sideName}`, 'Success');
            // Start polling for download URL
            const variantKeyForPoll = `${product.productId}_${variant.styleId}`;
            this.pollExportJobProgress(variant.projectId, jobId, variantKeyForPoll, sideNameNormalized);
            this.loader.hide();
          } else {
            console.error(`❌ No job_id in response for ${variant.colorName} - ${sideName}:`, response);
            this.loader.hide();
          }
        } else {
          this.loader.hide();
        }
      }
    });
    }); // end resolveZoneInchesForSide subscribe
    }); // end isSideFrameDistorted
  }


  // Get all sides for a product (used in header strip)
  getProductSides(product: ProductColorVariant): { name: string, shortName: string }[] {
    const sides: { name: string, shortName: string }[] = [];
    const firstColor = product.availableColors?.[0];
    if (!firstColor) return sides;

    if (firstColor.ImageFilePath_Front) {
      sides.push({ name: 'Front', shortName: 'F' });
    }
    if (firstColor.ImageFilePath_Back) {
      sides.push({ name: 'Back', shortName: 'B' });
    }
    if (firstColor.ImageFilePath_SleeveLeft) {
      sides.push({ name: 'Left', shortName: 'L' });
    }
    if (firstColor.ImageFilePath_SleeveRight) {
      sides.push({ name: 'Right', shortName: 'R' });
    }
    return sides;
  }

  // Get product-level logo URL for a side
  getProductSideLogoUrl(product: ProductColorVariant, sideName: string): string {
    const productLogos = this.productSideLogos.get(product.productId);
    if (productLogos && productLogos[sideName]) {
      return productLogos[sideName];
    }
    return this.designData?.previewUrl || this.designData?.designUrl || '';
  }

  // Trigger file input for product-level logo
  triggerProductSideLogoInput(productId: string, idx: number): void {
    const fileInput = document.getElementById(`prod-logo-${productId}-${idx}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle product-level logo change - updates ALL variants
  onProductSideLogoChange(product: ProductColorVariant, sideName: string, event: any): void {

    const file = event.target.files[0];
    if (!file) {
      return;
    }

    this.loader.show('Updating logo…', 'Applying to selected color variant. This may take a moment.');
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const base64 = (e.target.result as string).split(',')[1];
      const extension = file.name.split('.').pop() || 'png';

      this.http.post(`${environment.baseUrl}/Common/upload-huddle-logo`, {
        File: base64,
        Extension: extension
      }).subscribe({
        next: (response: any) => {
          if (response.StatusCode === 200) {
            const fileName = response.Data.ResponseData;
            if (fileName) {
              const fullLogoUrl = `${environment.huddleLogoFolder}${fileName}`;

              // Store at product level
              if (!this.productSideLogos.has(product.productId)) {
                this.productSideLogos.set(product.productId, {});
              }
              this.productSideLogos.get(product.productId)![sideName] = fullLogoUrl;

              // Update ALL selected variants for this product and export each
              product.selectedColors.forEach(variant => {
                if (!variant.sideLogos) {
                  variant.sideLogos = {};
                }
                variant.sideLogos[sideName] = fullLogoUrl;

                // Call export-job for this side of each variant
                this.exportSingleSide(product, variant, sideName);
              });

              this.notification.success(`${sideName} logo updated for all colors`, 'Success');
            }
          } else {
            this.notification.error(response.Message || 'Upload failed', 'Error');
          }
          this.loader.hide();
        },
        error: (error: any) => {
          console.error(`❌ Error uploading logo:`, error);
          this.notification.error('Failed to upload logo', 'Error');
          this.loader.hide();
        }
      });
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  onVariantFileSelected(product: ProductColorVariant, variant: ColorVariant, sideName: string, event: any): void {
    const file = event.target.files[0];
    if (file) {
      variant.variantDesignFile = file;
      variant.useDefaultDesign = false;

      // Read file as base64 and upload to Azure
      this.loader.show('Uploading design…', 'This may take a moment.');
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = (e.target.result as string).split(',')[1]; // Remove data:image/xxx;base64, prefix
        const extension = file.name.split('.').pop() || 'png';

        // Upload to Azure blob storage using the correct payload format
        this.http.post(`${environment.baseUrl}/Common/upload-huddle-logo`, {
          File: base64,
          Extension: extension
        }).subscribe({
          next: (response: any) => {

            if (response.StatusCode === 200) {
              const fileName = response.Data.ResponseData;

              if (fileName) {
                // Add the blob storage prefix to create full URL
                const fullLogoUrl = `${environment.huddleLogoFolder}${fileName}`;

                // Replace previous image URL with the new/existing URL
                variant.variantPreviewUrl = fullLogoUrl;
                variant.variantDesignUrl = fullLogoUrl;

                // Check if file already existed
                const isExisting = response.Message === 'File already exists';
                this.notification.success(
                  isExisting
                    ? `Logo already exists, using existing file for ${variant.colorName}`
                    : `Logo updated for ${variant.colorName}`,
                  'Success'
                );

                // Call export-job for only this side after logo update
                this.exportSingleSide(product, variant, sideName);
              } else {
                this.notification.error('Failed to get logo URL from response', 'Error');
              }
            } else {
              this.notification.error(response.Message || 'Upload failed', 'Error');
            }
            this.loader.hide();
          },
          error: (error: any) => {
            console.error('❌ Error uploading logo:', error);
            this.notification.error('Failed to upload logo', 'Error');
            this.loader.hide();
          }
        });
      };
      reader.readAsDataURL(file);
    }
  }

  removeVariantDesign(variant: ColorVariant): void {
    // Clear variant logo and revert to default
    variant.variantDesignFile = undefined;
    variant.variantDesignUrl = undefined;
    variant.variantPreviewUrl = undefined;
    variant.useDefaultDesign = true;
    this.notification.info(`Reverted to default design for ${variant.colorName}`, 'Info');
  }

  createVariantCustomizations(product: any, variant: any, originalCustomizations: any): any {

    if (product.availableColors) {
    }

    // Clone the original customizations
    const variantCustomizations = JSON.parse(JSON.stringify(originalCustomizations || { classes: {} }));

    if (!variantCustomizations.classes) {
      variantCustomizations.classes = {};
    }


    // Find the style for this variant's color
    const variantStyle = product.availableColors?.find((s: any) => Number(s.ID) === Number(variant.styleId));

    if (!variantStyle) {
      console.error(`❌ Style NOT found for variant ${variant.colorName} (styleId: ${variant.styleId})`);
      console.error(`   - Available style IDs:`, product.availableColors?.map((s: any) => s.ID));
      return variantCustomizations;
    }


    // Update logo if variant has custom uploaded logo (preserve media_position)
    if (variant.variantDesignUrl && !variant.useDefaultDesign) {
      const oldLogoUrl = variantCustomizations.classes.logo?.url;
      const existingMediaPosition = variantCustomizations.classes.logo?.media_position;
      variantCustomizations.classes.logo = {
        url: variant.variantDesignUrl
      };
      if (existingMediaPosition) {
        variantCustomizations.classes.logo.media_position = existingMediaPosition;
      }
    }


    return variantCustomizations;
  }

  goBack(): void {
    this.location.back();
  }

  /** Normalized, sorted imprint-location set for a variant (live variantActiveSides, fallback variant.sides). */
  private getVariantSideSet(product: ProductColorVariant, variant: ColorVariant): string[] {
    const key = `${product.productId}_${variant.styleId}`;
    const active = this.variantActiveSides.get(key);
    const raw: string[] = (active && active.length > 0)
      ? active
      : (variant.sides || []).map((s: any) => s.SideName || s.Name || '');
    const normalized = raw.map(s => (s || '').toLowerCase().replace(/\s+/g, '_')).filter(Boolean);
    return [...new Set(normalized)].sort();
  }

  /**
   * Per-product hard gate: every color variant of a product must carry the SAME imprint-location set.
   * Auto-propagation keeps this true; this is the backstop (also covers edit/migration paths that
   * build variants by other routes). Cross-product differences are handled separately as a soft
   * warning, since different products can legitimately offer different sides.
   */
  private validateImprintParity(): { ok: boolean; message: string } {
    const fmt = (arr: string[]) => arr.map(s => this.getDisplaySideName(s)).join(', ') || 'none';
    for (const product of this.selectedProducts) {
      const variants = product.selectedColors || [];
      if (variants.length <= 1) continue;
      const refSet = this.getVariantSideSet(product, variants[0]);
      const ref = refSet.join('|');
      for (const variant of variants) {
        const set = this.getVariantSideSet(product, variant);
        if (set.join('|') !== ref) {
          return {
            ok: false,
            message: `All colors of "${product.productName}" must have the same imprint locations. `
              + `"${variant.colorName}" has [${fmt(set)}] but "${variants[0].colorName}" has [${fmt(refSet)}]. `
              + `Add the missing location(s) to every color before continuing.`
          };
        }
      }
    }
    return { ok: true, message: '' };
  }

  /**
   * Cross-product soft check: if selected products end up with different imprint-location sets,
   * return a human-readable warning (never blocks — a product may not support a side another has,
   * e.g. a tank has no sleeves). Assumes per-product parity already holds, so a product's first
   * variant represents the whole product.
   */
  private getImprintCrossProductWarning(): string | null {
    const fmt = (arr: string[]) => arr.map(s => this.getDisplaySideName(s)).join(', ') || 'none';
    const perProduct: { key: string; label: string }[] = [];
    this.selectedProducts.forEach(product => {
      const variants = product.selectedColors || [];
      if (variants.length === 0) return;
      const set = this.getVariantSideSet(product, variants[0]);
      perProduct.push({ key: set.join('|'), label: `"${product.productName}": [${fmt(set)}]` });
    });
    if (perProduct.length <= 1) return null;
    const ref = perProduct[0].key;
    if (perProduct.every(p => p.key === ref)) return null;
    return `Products have different imprint locations — ${perProduct.map(p => p.label).join('; ')}. `
      + `You can continue, but please confirm this is intended.`;
  }

  /**
   * WI-1521-followup (Issue 1 fix): toggleColorSelection() now defers DH project creation for a
   * newly-added color's non-current-side imprints instead of eagerly creating one project per side
   * for every added color (the reported bug — "why creating new project for every color, it should
   * be created when we update design"). A deferred side is normally picked up lazily the moment the
   * user actually switches the viewed side into it (onSideChange() already creates-or-reuses a
   * project per variant per side whenever the view changes — unchanged by this fix). This is the
   * safety net for the case where the user clicks Continue without ever viewing that side: it scans
   * every variant's active sides for one that has NEITHER a DH project (variantSideProjects) NOR any
   * preview attempt at all (backgroundUrls has no entry — not even a recorded failure, which DOES
   * count as "attempted") and kicks off the exact same reuse-or-create logic toggleColorSelection
   * uses, registering the side in pendingExports so the existing hasPendingExports gate further down
   * in continueToNextStep() waits for it (shows the loader, same as any other in-flight export)
   * before saving/navigating. Nothing selected is ever silently left unexported.
   */
  private ensurePendingSideProjectsBeforeSave(): void {
    this.selectedProducts.forEach(product => {
      const lockedVariantRef = product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId));

      product.selectedColors.forEach(variant => {
        const variantKey = `${product.productId}_${variant.styleId}`;
        const activeSides = this.variantActiveSides.get(variantKey) || [];

        activeSides.forEach(sideName => {
          // Already has a project (created eagerly, via onSideChange, or via the editor) — nothing to do.
          if (this.variantSideProjects.get(`${variantKey}_${sideName}`)) return;

          const sideConfig = this.getSideConfigForCurrentSide(sideName);
          const cacheKey = sideConfig ? `${product.productId}_${variant.styleId}_${sideConfig.displayName}` : '';
          const alreadyAttempted = this.isPreviewLoading(product, variant, sideConfig?.displayName || sideName)
            || (!!cacheKey && this.backgroundUrls.has(cacheKey));
          if (alreadyAttempted) return; // in flight, already resolved, or a recorded failure

          const sideInfo = this.getSideInfoForSide(product.productId, sideName);
          if (!sideInfo) return;


          const tracking = this.pendingExports.get(variantKey) || { pending: 0, completed: 0, sideJobIds: new Map<string, string>() };
          tracking.pending++;
          this.pendingExports.set(variantKey, tracking);

          const newLogoUrl = variant.sideLogos?.[sideName] || '';
          const sameLogo = !newLogoUrl;

          // Cheap reuse — mirrors toggleColorSelection's carry-over branch exactly. Checks BOTH an
          // already-registered shared project AND one another variant is still in the middle of
          // creating (pendingSideProjectCreations) — this loop processes every variant's every
          // active side in one synchronous pass with no await between them, so without this
          // in-flight check two colors that both need the same never-viewed side would each race
          // to create their own duplicate project, exactly like onSideChange's same race (see
          // pendingSideProjectCreations' doc comment) — just triggered from Continue instead of a
          // side-switch click.
          const sideKey = `${product.productId}_${sideName}`;
          const inFlight = sameLogo ? this.pendingSideProjectCreations.get(sideKey) : undefined;
          const already = sameLogo ? this.getSharedSideProjectId(product, sideName) : null;

          const reuseFrom = () => {
            // The owner may be the locked color OR any other already-selected color that created
            // this side's project first — resolve it rather than assuming locked.
            const ownerKey = this.findSideProjectOwnerKey(product, sideName);
            const ownerExportTracking = this.pendingExports.get(ownerKey);
            const ownerJobId = ownerExportTracking?.sideJobIds?.get(sideName) || '';
            const ownerDownloadUrl = ownerExportTracking?.sideDownloadUrls?.get(sideName) || '';
            this.generatePreviewForCurrentSide(product, variant, sideName);
            if (ownerDownloadUrl) {
              if (!tracking.sideDownloadUrls) {
                tracking.sideDownloadUrls = new Map<string, string>();
              }
              tracking.sideDownloadUrls.set(sideName, ownerDownloadUrl);
            }
            this.markExportCompleted(variantKey, sideName, ownerJobId);
          };

          const createOwnProject = () => {
            // No shared/in-flight project to borrow (or this variant has its own custom logo for
            // this side) — create a new one now (this is the "before Continue" backfill; blocking
            // here is acceptable since it's the exception, not the common case). This color becomes
            // the OWNER of this side's project — findSideProjectOwner's scan picks it up
            // automatically via variantSideProjects, skipping it if !sameLogo — so a later color
            // needing the same side can reuse it. Mark in-flight SYNCHRONOUSLY
            // (before the first await) so a sibling variant/side later in this same forEach pass
            // sees it and borrows instead of racing to create its own — same pattern as
            // createAndOwnSideProject uses for onSideChange.
            const lockedLogoUrl = this.getLockedVariantLogoForSide(lockedVariantRef, sideName);
            const mediaPosition = this.getMediaPositionForSide(lockedVariantRef || variant, sideName);
            this.incrementDesignIdOffset(product.productId, variant.styleId);
            const projectTitle = this.generateRandomProjectTitle();
            const creation: Promise<string> = this.buildSeedCustomization(lockedLogoUrl, product.productId, sideName, mediaPosition)
              .then(customizationObject => this.createProjectForSide(sideInfo.templateId, projectTitle, customizationObject));
            if (sameLogo) {
              this.pendingSideProjectCreations.set(sideKey, creation);
            }
            creation.then(projectId => {
              if (sameLogo) this.pendingSideProjectCreations.delete(sideKey);
              if (projectId) {
                this.variantSideProjects.set(`${variantKey}_${sideName}`, projectId);
                this.fetchAndStoreMetadata(projectId, variant, sideName);
                this.generatePreviewForCurrentSide(product, variant, sideName);
                this.exportCurrentSide(product, variant, sideName);
              } else {
                this.markExportCompleted(variantKey, sideName, '');
              }
            }).catch(err => {
              if (sameLogo) this.pendingSideProjectCreations.delete(sideKey);
              console.error(`❌ [Continue safety net] Error creating project for ${variant.colorName} - ${sideName}:`, err);
              this.markExportCompleted(variantKey, sideName, '');
            });
          };

          if (inFlight || already) {
            const waitFor = inFlight || Promise.resolve(already as string);
            waitFor.then(sharedProjectId => {
              if (!sharedProjectId) {
                // The owner's creation failed — nothing to borrow; become the new owner instead.
                createOwnProject();
                return;
              }
              reuseFrom();
            });
          } else {
            createOwnProject();
          }
        });
      });
    });
  }

  continueToNextStep(): void {
    if (this.totalVariants === 0) {
      this.notification.warning('Please select at least one color variant.', 'Warning');
      return;
    }

    // Imprint-location parity: hard-block when a single product's color variants disagree
    // (always fixable via the "+ Add Side" control), and soft-warn when different products differ
    // (may be legitimate — a product can't offer a side it doesn't support, e.g. a tank has no sleeves).
    const parity = this.validateImprintParity();
    if (!parity.ok) {
      this.notification.warning(parity.message, 'Imprint locations mismatch');
      return;
    }
    const crossProductWarning = this.getImprintCrossProductWarning();
    if (crossProductWarning) {
      this.notification.warning(crossProductWarning, 'Imprint locations differ');
    }

    // WI-1521-followup (Issue 1): backfill any side toggleColorSelection deferred (see its
    // isCurrentSide branch) that the user never got around to viewing before hitting Continue.
    // Safe/no-op for every side that already has a project or an attempted preview — see the
    // function doc for exactly what it checks.
    this.ensurePendingSideProjectsBeforeSave();

    // WI-1524 fix #2: require explicit confirmation before saving if any color/side has a failed
    // preview or a print export that never got a download file — otherwise the product silently
    // saves with a blank thumbnail / missing print file (the reported bug). Uses this codebase's
    // established confirm() pattern (migration-modal, product-edit-modal, other guards here) rather
    // than a hard block: a full block would trap a Builder with no way to ship at all during a
    // genuine DH outage — exactly the kind of day this ticket's email thread is about — whereas
    // confirm() still makes the failure impossible to miss (named per color/side) while leaving an
    // explicit, informed escape hatch ("retry later / fix manually") in the Builder's hands.
    // Hard block (no escape hatch): a side whose logo frame disagrees with the artwork would save
    // a print file that is KNOWN to be stretched — production received 468 of those before this
    // guard existed. Unlike a transient export failure there is nothing to retry; the designer
    // must re-add the logo in the editor.
    const distorted = this.collectDistortedSides();
    if (distorted.length > 0) {
      const shownDistorted = distorted.slice(0, 10).join(', ');
      const moreDistorted = distorted.length > 10 ? ` …and ${distorted.length - 10} more` : '';
      this.notification.error(
        `Logo is stretched on ${distorted.length} color/side${distorted.length !== 1 ? 's' : ''}: ${shownDistorted}${moreDistorted}. Open the editor for each, re-add the logo, and save again.`,
        'Cannot continue'
      );
      return;
    }

    const failures = this.collectPreviewAndPrintFailures();
    if (failures.length > 0) {
      const shown = failures.slice(0, 10).join('\n');
      const more = failures.length > 10 ? `\n…and ${failures.length - 10} more` : '';
      const confirmed = window.confirm(
        `${failures.length} color/side${failures.length !== 1 ? 's' : ''} failed to generate a preview or print file and would save blank:\n\n${shown}${more}\n\nYou can retry them from the color cards above (Retry button on the failed side). Continue anyway?`
      );
      if (!confirmed) return;
    }


    // Merge original product data with selected variants
    const productsWithVariants = this.selectedProducts.map(product => {
      const originalData = this.productsDataFromPreviousPage.find(
        p => String(p.ProductId) === String(product.productId)
      );


      // Use latest generated thumbnail as product image (locked variant's front side)
      let latestProductImage = product.productImage;
      const lockedVariant = product.selectedColors.find(c => Number(c.styleId) === Number(product.lockedStyleId));
      if (lockedVariant) {
        const frontDisplayName = this.getDisplaySideName('front');
        const frontCacheKey = `${product.productId}_${lockedVariant.styleId}_${frontDisplayName}`;
        const latestThumbnail = this.backgroundUrls.get(frontCacheKey);
        if (latestThumbnail) {
          latestProductImage = latestThumbnail;
        }
      }

      // Resolve the selected decoration type's per-side rate (embroidery vs digital, etc.) so the
      // Pricing & Tags page applies the correct additional-side cost. The rate is resolved
      // server-side (GetProductDecorations → DecorationPricingService.ResolveRateAsync) and attached
      // to the product by loadProductDecorations(); null when unresolved, in which case the pricing
      // page falls back to the store's flat PrintingCost (unchanged behavior for digital/unmapped).
      const selectedDecorationTypeId = (product as any).selectedDecorationTypeId ?? null;
      const selectedDecoration = (((product as any).decorations) || [])
        .find((d: any) => Number(d.DecorationTypeId) === Number(selectedDecorationTypeId));
      const decorationRate = selectedDecoration?.Rate != null ? Number(selectedDecoration.Rate) : null;

      return {
        ...originalData,
        productName: product.productName,
        productCategory: product.productCategory,
        productImage: latestProductImage,
        availableColors: product.availableColors, // IMPORTANT: Include availableColors for variant customizations
        selectedDecorationTypeId: selectedDecorationTypeId,
        decorationRate: decorationRate,
        selectedColors: product.selectedColors.map(variant => {
          // If variant has its own projectId (alternate design), use it
          // Otherwise, use the default product projectId
          const finalProjectId = variant.projectId || originalData?.ProjectId;
          const finalCustomizations = variant.customizations || originalData?.Customizations;
          const finalJobId = variant.jobId || originalData?.JobId;

          // Get templateId with fallbacks (check Sides if not at product level)
          let finalTemplateId = variant.templateId || originalData?.TemplateId;
          if (!finalTemplateId) {
            const sides = variant.sides || originalData?.Sides;
            if (sides && sides.length > 0) {
              const sideWithTemplate = sides.find((s: any) => s.TemplateId);
              if (sideWithTemplate) {
                finalTemplateId = parseInt(sideWithTemplate.TemplateId) || 0;
              }
            }
          }

          // Persist ONLY this variant's ACTIVE sides. variant.sides is seeded from product.Sides
          // (every side the template supports) while variantActiveSides is the real user selection
          // — and variantActiveSides is not itself persisted. Saving the full list made
          // restoreFromSavedData rebuild variantActiveSides from every product side, so coming back
          // from pricing showed all sides regardless of what was picked (and fired exports for
          // sides the user never selected). Mirrors the filter already applied to the API payload
          // in prepareFinalPayloadAndSave. NOTE: product-level Sides (via ...originalData) is left
          // untouched — buildAvailableSides needs the full list for the "+ Add Side" dropdown.
          const variantKeyForSides = `${product.productId}_${variant.styleId}`;
          const activeSidesNorm = (this.variantActiveSides.get(variantKeyForSides) || [])
            .map((s: string) => this.canonicalSide(s));
          const sourceSides = (variant.sides || originalData?.Sides || []).filter((side: any) => {
            // No active-side info for this variant → preserve prior behavior rather than drop sides.
            if (activeSidesNorm.length === 0) return true;
            return activeSidesNorm.includes(this.canonicalSide(side.SideName || side.Name || ''));
          });

          // Update sides with latest thumbnail URLs from backgroundUrls cache
          const updatedSides = sourceSides.map((side: any) => {
            const sName = (side.SideName || side.Name || '').toLowerCase().replace(/\s+/g, '_');
            const sideDisplayName = this.getDisplaySideName(sName);
            const cacheKey = `${product.productId}_${variant.styleId}_${sideDisplayName}`;
            const latestUrl = this.backgroundUrls.get(cacheKey);
            if (latestUrl) {
              return { ...side, ThumbnailUrl: latestUrl, ThumbnailRelativeUrl: latestUrl };
            }
            return side;
          });

          // Update variant's projectId per side from variantSideProjects
          updatedSides.forEach((side: any) => {
            const sName = (side.SideName || '').toLowerCase().replace(/\s+/g, '_');
            const sideProjectId = this.variantSideProjects.get(`${product.productId}_${variant.styleId}_${sName}`);
            if (sideProjectId) {
              side.ProjectId = sideProjectId;
            }
          });

          return {
            ...variant,
            productId: product.productId,
            projectId: finalProjectId,
            customizations: finalCustomizations,
            templateId: finalTemplateId,
            jobId: finalJobId,
            sides: updatedSides,
            projectName: variant.projectName || originalData?.ProjectName || originalData?.ProjectTitle || originalData?.Description
          };
        })
      };
    });


    // Save color variants data to state
    const colorVariantsData = {
      designData: this.designData,
      products: productsWithVariants
    };
    this.stateService.setColorVariantsData(colorVariantsData);


    // A colour whose artwork differs from the locked colour's must be saved with a print file
    // exported from its own design. Start any export that is missing (the wait below then holds the
    // save); refuse to continue when a colour has no design of its own to export from.
    const ownExports = this.ensureOwnPrintExports(productsWithVariants);
    if (ownExports.blocked.length > 0) {
      this.loader.hide();
      this.notification.error(
        `These colours have their own design but no print file of their own: ${ownExports.blocked.join(', ')}. ` +
        'Open each colour in the editor and close it to export its print file, then continue.',
        'Error'
      );
      return;
    }

    // Check if there are pending exports for newly added colors
    let hasPendingExports = false;
    this.pendingExports.forEach((tracking) => {
      if (tracking.completed < tracking.pending) {
        hasPendingExports = true;
      }
    });

    if (hasPendingExports) {
      // Wait for pending exports to complete, then save and navigate
      this.productsToSave = productsWithVariants;
      this.waitingForExportsToSave = true;
      this.shouldNavigateAfterSave = true; // Navigate after save when Continue clicked
      this.loader.show('Preparing your product…', 'Waiting for exports to complete. This may take a moment.');
    } else {
      // No pending exports, prepare final payload and save, then navigate
      this.shouldNavigateAfterSave = true;
      this.loader.show('Preparing your product…', 'This may take a moment.');
      this.prepareFinalPayloadAndSave(productsWithVariants);
    }
  }

  /**
   * The stand-in product createSideCustomizations needs for a payload product. It must carry
   * selectedColors/lockedStyleId (WI-1522 hotfix: resolveExportLogoUrl's locked-colour fallback
   * reads them), which the payload copy of the product does not.
   */
  private customizationProductFor(product: any, originalProduct: ProductColorVariant | undefined): ProductColorVariant {
    return {
      availableColors: product.availableColors,
      productId: product.ProductId,
      selectedColors: originalProduct?.selectedColors || [],
      lockedStyleId: originalProduct?.lockedStyleId
    } as ProductColorVariant;
  }

  /** The active, saved sides of a colour — the same selection prepareFinalPayloadAndSave sends. */
  private activeSidesOf(product: any, variant: any): any[] {
    const variantKey = `${product.ProductId}_${variant.styleId}`;
    const active = (this.variantActiveSides.get(variantKey) || [this.getProductCurrentSide(product.ProductId)])
      .map((s: string) => s.toLowerCase().replace(/\s+/g, '_'));
    return (variant.sides || []).filter((side: any) => {
      const sName = (side.SideName || side.Name || '').toLowerCase().replace(/\s+/g, '_');
      return active.some(a => sName === a || sName.includes(a) || a.includes(sName));
    });
  }

  /**
   * True when this colour prints different artwork on this side from the locked colour (see
   * alternate-print-file.ts). The locked colour itself never differs.
   */
  private sideArtDiffersFromLocked(product: any, originalProduct: ProductColorVariant | undefined, variant: any, sideName: string, side: any): boolean {
    if (!originalProduct || Number(variant.styleId) === Number(originalProduct.lockedStyleId)) {
      return false;
    }
    const lockedVariant: any = (product.selectedColors || []).find((c: any) => Number(c.styleId) === Number(originalProduct.lockedStyleId))
      || originalProduct.selectedColors.find(c => Number(c.styleId) === Number(originalProduct.lockedStyleId));
    if (!lockedVariant) {
      return false;
    }
    const lockedSide = lockedVariant.sides?.length ? this.matchSideByName(lockedVariant.sides, sideName) : null;
    const stand = this.customizationProductFor(product, originalProduct);
    const key = sideArtKey(this.createSideCustomizations(stand, variant, sideName, side), variant.templateId);
    const lockedKey = sideArtKey(this.createSideCustomizations(stand, lockedVariant, sideName, lockedSide), lockedVariant.templateId);
    return artDiffersFromLocked(variant.variantSource, key, lockedKey);
  }

  private printExportOf(variantKey: string, sideNorm: string): PrintExport {
    const tracking = this.pendingExports.get(variantKey);
    return {
      url: tracking?.sideDownloadUrls?.get(sideNorm) || '',
      jobId: tracking?.sideJobIds?.get(sideNorm) || ''
    };
  }

  /** Print exports of the colours this side's file could have been borrowed from (locked + side owner). */
  private borrowablePrintExports(originalProduct: ProductColorVariant, variantKey: string, sideNorm: string): PrintExport[] {
    const keys = new Set<string>([
      `${originalProduct.productId}_${originalProduct.lockedStyleId}`,
      this.findSideProjectOwnerKey(originalProduct, sideNorm)
    ]);
    keys.delete(variantKey);
    return [...keys].map(k => this.printExportOf(k, sideNorm));
  }

  /**
   * Before saving: every colour whose artwork differs from the locked colour's and holds no print
   * file of its own (or one borrowed from another colour) gets its own design exported. Returns
   * how many exports were started (the caller's pending-export wait holds the save) and the colours
   * that have no design of their own to export from.
   */
  private ensureOwnPrintExports(productsWithVariants: any[]): { started: number; blocked: string[] } {
    let started = 0;
    const blocked: string[] = [];

    productsWithVariants.forEach(product => {
      const originalProduct = this.selectedProducts.find(p => String(p.productId) === String(product.ProductId));
      if (!originalProduct) {
        return;
      }
      // Any colour (locked included) whose side was edited but never had an export started for its
      // current design (e.g. the editor-close re-export bailed early): start it now so the pending
      // gate holds the save for it — a pre-edit print file is never used instead.
      (product.selectedColors || []).forEach((variant: any) => {
        const variantKey = `${product.ProductId}_${variant.styleId}`;
        const liveVariant = originalProduct.selectedColors.find(c => Number(c.styleId) === Number(variant.styleId));
        if (!liveVariant) {
          return;
        }
        this.activeSidesOf(product, variant).forEach((side: any) => {
          const sideNorm = (side.SideName || 'Front').toLowerCase().replace(/\s+/g, '_');
          if (!this.printGens.needsNewExport(this.printGenKey(variantKey, sideNorm))) {
            return;
          }
          let tracking = this.pendingExports.get(variantKey);
          if (!tracking) {
            tracking = { pending: 0, completed: 0, sideJobIds: new Map() };
            this.pendingExports.set(variantKey, tracking);
          }
          tracking.pending++;
          this.exportCurrentSide(originalProduct, liveVariant, sideNorm);
          started++;
        });
      });
      (product.selectedColors || []).forEach((variant: any) => {
        if (Number(variant.styleId) === Number(originalProduct.lockedStyleId)) {
          return;
        }
        const variantKey = `${product.ProductId}_${variant.styleId}`;
        this.activeSidesOf(product, variant).forEach((side: any) => {
          const sideName = side.SideName || 'Front';
          const sideNorm = sideName.toLowerCase().replace(/\s+/g, '_');
          const differs = this.sideArtDiffersFromLocked(product, originalProduct, variant, sideName, side);
          const own = this.printExportOf(variantKey, sideNorm);
          if (!needsOwnPrintExport(differs, own, this.borrowablePrintExports(originalProduct, variantKey, sideNorm))) {
            return;
          }

          const ownProject = this.variantSideProjects.get(`${variantKey}_${sideNorm}`) || '';
          const ownerKey = this.findSideProjectOwnerKey(originalProduct, sideNorm);
          const borrowedProject = [
            this.variantSideProjects.get(`${product.ProductId}_${originalProduct.lockedStyleId}_${sideNorm}`),
            ownerKey !== variantKey ? this.variantSideProjects.get(`${ownerKey}_${sideNorm}`) : undefined
          ].some(p => !!p && p === ownProject);
          const liveVariant = originalProduct.selectedColors.find(c => Number(c.styleId) === Number(variant.styleId));

          if (!ownProject || borrowedProject || !liveVariant) {
            blocked.push(`${variant.colorName || variant.styleId} – ${this.getDisplaySideName(sideName)}`);
            return;
          }

          let tracking = this.pendingExports.get(variantKey);
          if (!tracking) {
            tracking = { pending: 0, completed: 0, sideJobIds: new Map() };
            this.pendingExports.set(variantKey, tracking);
          }
          tracking.pending++;
          tracking.sideJobIds.delete(sideNorm);
          tracking.sideDownloadUrls?.delete(sideNorm);
          this.exportCurrentSide(originalProduct, liveVariant, sideNorm);
          started++;
        });
      });
    });

    return { started, blocked };
  }

  /**
   * Prepare final payload with side-specific customizations and JobIds, then save
   */
  prepareFinalPayloadAndSave(productsWithVariants: any[]): void {

    const allProductPayloads: any[] = [];
    const printFileErrors: string[] = [];
    // WI 1819: the default/locked style MUST have at least one side, or the backend never
    // assigns a CustomizedProductId and the whole publish is cancelled with an opaque "save
    // completed without creating a product record" error — see the root-cause fix in
    // canonicalSide() above. This is a safety net for any other way a style could still end up
    // here with zero sides: catch it before the API call, not after a silent publish failure.
    const emptyDefaultSidesErrors: string[] = [];

    productsWithVariants.forEach((product, productIndex) => {
      const stylesArray: any[] = [];

      const originalProduct = this.selectedProducts.find(p => String(p.productId) === String(product.ProductId));

      product.selectedColors.forEach((variant: any, variantIndex: number) => {
        const templateId = variant.templateId || 0;
        const variantKey = `${product.ProductId}_${variant.styleId}`;
        const productCurrentSide = this.getProductCurrentSide(product.ProductId);
        const activeSidesForVariant = this.variantActiveSides.get(variantKey) || [productCurrentSide];
        const activeSideNormalized = activeSidesForVariant.map((s: string) => s.toLowerCase().replace(/\s+/g, '_'));
        let anySideArtDiffers = false;

        const sidesWithCustomizations = variant.sides && variant.sides.length > 0
          ? variant.sides
            // Match each master-product side against the active technical tokens. The DB Side.Name
            // for a sleeve is separator-less ("sleeveright"/"sleeveleft") or word-reversed
            // ("Right Sleeve"), neither of which naively matches the active token ("right_sleeve")
            // — so a single sleeve side used to produce an EMPTY Sides array and the publish aborted
            // with "save completed without creating a product record" (front worked only because
            // "cap_front" contains "front"). sideNameMatchesActiveToken() folds the spellings via
            // canonicalSide() while refusing to collapse "Left/Right Chest" into a sleeve.
            .filter((side: any) =>
              activeSideNormalized.some(active =>
                this.sideNameMatchesActiveToken(side.SideName || side.Name || '', active)
              )
            )
            .map((side: any) => {
              // The raw Side.Name ("sleeveright") never aligns with the active/technical token
              // ("right_sleeve") that every tracking map, per-side project key, preview cache and
              // export is keyed by. Resolve this entry to the active side it represents and key
              // everything below off that token — otherwise the projectId/JobId/DownloadUrl lookups
              // all miss and the side would be saved with no print file even after the filter matched.
              const rawSideName = side.SideName || side.Name || 'Front';
              const rawNormalized = rawSideName.toLowerCase().replace(/\s+/g, '_');
              const sideName = activeSideNormalized.find(active =>
                this.sideNameMatchesActiveToken(rawSideName, active)
              ) || rawNormalized;
              const sideNameNormalized = sideName;

              // Get thumbnail URL from backgroundUrls map (newly generated previews)
              let thumbnailUrl = '';
              const displaySideName = this.getDisplaySideName(sideName);
              const cacheKey = `${product.ProductId}_${variant.styleId}_${displaySideName}`;
              thumbnailUrl = this.backgroundUrls.get(cacheKey) || '';

              // Fallback: in edit mode, use existing ThumbnailUrl from variant's side data (from DB)
              if (!thumbnailUrl && side.ThumbnailUrl) {
                thumbnailUrl = side.ThumbnailUrl;
              }

              // Extract relative URL
              let thumbnailRelativeUrl = thumbnailUrl;
              if (thumbnailUrl && environment.huddleBackgroundFolder && thumbnailUrl.startsWith(environment.huddleBackgroundFolder)) {
                thumbnailRelativeUrl = thumbnailUrl.replace(environment.huddleBackgroundFolder, '');
              }
              // Fallback: use existing ThumbnailRelativeUrl from side data
              if (!thumbnailRelativeUrl && side.ThumbnailRelativeUrl) {
                thumbnailRelativeUrl = side.ThumbnailRelativeUrl;
              }

              // Create customizations specific to this side.
              // WI-1522 hotfix: this synthetic stand-in product object only carried
              // availableColors/productId — resolveExportLogoUrl's new locked-variant fallback
              // (added for WI-1522) reads product.selectedColors/lockedStyleId, which were
              // undefined here, crashing with "Cannot read properties of undefined (reading
              // 'find')" and getting the whole save stuck at "Preparing color variant...".
              // originalProduct (resolved above, the real live ProductColorVariant) has both.
              const sideCustomizations = this.createSideCustomizations(
                {
                  availableColors: product.availableColors,
                  productId: product.ProductId,
                  selectedColors: originalProduct?.selectedColors || [],
                  lockedStyleId: originalProduct?.lockedStyleId
                } as ProductColorVariant,
                variant,
                sideName,
                side
              );

              // Get per-side projectId
              const sideProjectKey = `${product.ProductId}_${variant.styleId}_${sideNameNormalized}`;
              const sideProjectId = this.variantSideProjects.get(sideProjectKey) || variant.projectId || '';

              // Get JobId and DownloadUrl from completed export tracking
              const ownExport = this.printExportOf(variantKey, sideNameNormalized);
              const sideJobId = ownExport.jobId;
              let sideDownloadUrl = ownExport.url;

              // Same artwork as the locked colour: if this side shares a JobId with whichever color
              // OWNS the side-level project (not necessarily the default/locked style — see
              // findSideProjectOwner / findSideProjectOwnerKey), use its DownloadUrl. Covers the race
              // where this variant's reuse branch carried over the owner's jobId/downloadUrl
              // synchronously at add-time, before the owner's own export had actually finished
              // uploading — by save time (checkAllExportsComplete gates on every tracking reaching
              // completed>=pending) the owner's tracking is guaranteed to hold the real downloadUrl.
              // Different artwork: only this colour's own print file — never another colour's, or
              // production prints the wrong art (see alternate-print-file.ts).
              if (originalProduct) {
                const artDiffers = this.sideArtDiffersFromLocked(product, originalProduct, variant, sideName, side);
                anySideArtDiffers = anySideArtDiffers || artDiffers;
                const ownerKey = this.findSideProjectOwnerKey(originalProduct, sideNameNormalized);
                const resolved = resolveSideDownloadUrl(
                  artDiffers,
                  ownExport,
                  ownerKey !== variantKey ? this.printExportOf(ownerKey, sideNameNormalized) : { url: '', jobId: '' },
                  this.borrowablePrintExports(originalProduct, variantKey, sideNameNormalized));
                sideDownloadUrl = resolved.url;
                if (resolved.error) {
                  printFileErrors.push(`${variant.colorName || variant.styleId} – ${this.getDisplaySideName(sideName)}`);
                  console.error(`❌ ${variant.colorName} – ${sideName}: own artwork but ${resolved.error} print file`, {
                    productId: product.ProductId, styleId: variant.styleId, side: sideName
                  });
                }
              }

              // Edited since its last accepted print export (the post-edit export failed or never
              // ran): never fall back to a pre-edit file — surface the same visible error below.
              if (this.printGens.isStale(this.printGenKey(variantKey, sideNameNormalized))) {
                sideDownloadUrl = '';
                const staleLabel = `${variant.colorName || variant.styleId} – ${this.getDisplaySideName(sideName)}`;
                if (!printFileErrors.includes(staleLabel)) printFileErrors.push(staleLabel);
              }

              // Get PrintPlacement directly from the side object (comes from original Sides data)
              const printPlacement = side.PrintPlacement || '';

              // Support both camelCase (from parseProjectMetadata) and PascalCase (from DB)
              const rawElement = side.elementPlacement || side.ElementPlacement;
              const rawZone = side.zonePlacement || side.ZonePlacement;

              // SideName sent to the backend is now the decoration-location NAME ("Full Front"),
              // not the technical identifier ("front") — prefer whatever's already saved/resolved
              // for this side (side.InkSoftLocationName, passed through from the backend's
              // CustomizedProductEntry), else the standard-side default; fall back to the
              // technical alias only if neither resolves (non-standard side, nothing saved yet).
              // sideNameNormalized (the technical alias) is still used above for internal map keys
              // (backgroundUrls/variantSideProjects/pendingExports) — only the outgoing SideName
              // value changes here.
              const inkSoftLocationName = side.InkSoftLocationName || getDefaultDecorationLocationName(sideName) || null;

              return {
                SideName: inkSoftLocationName || sideNameNormalized,
                ProjectId: sideProjectId,
                DownloadUrl: sideDownloadUrl,
                ThumbnailUrl: thumbnailUrl,
                ThumbnailRelativeUrl: thumbnailRelativeUrl,
                PageId: '',
                JobId: sideJobId,
                PrintPlacement: printPlacement,
                ElementPlacement: rawElement ? (typeof rawElement === 'string' ? rawElement : JSON.stringify(rawElement)) : null,
                ZonePlacement: rawZone ? (typeof rawZone === 'string' ? rawZone : JSON.stringify(rawZone)) : null,
                Customizations: sideCustomizations
              };
            })
          : [];

        // DesignId/IsDefault based on variant source — drives backend routing in SaveHuddleProductStyles:
        //   DesignId=true,  IsDefault=true  → Case 1: CustomizeProducts (locked default side)
        //   DesignId=false, IsDefault=false → Case 2: ProductStyleAlternateDesign (non-locked variant with own DH project)
        //   DesignId=true,  IsDefault=false → Case 3: ProductStyleMapping + CustomProductImage only (image-only, no DH design)
        //
        // NOTE: DesignId is a boolean FLAG here — "false" means the variant has its OWN design (alternate),
        // "true" means it shares the locked variant's design ID (image-only replica or locked variant itself).

        // Determine whether this is the locked variant
        const isLockedVariant = originalProduct
          ? Number(variant.styleId) === Number(originalProduct.lockedStyleId)
          : false;

        // Project-ID divergence check — ground truth that this variant's editor was opened and
        // a clone/fresh project was created for it.  We compare per active-side project IDs
        // against the locked variant's project IDs for the SAME sides.
        // Only applied to non-locked variants; the locked variant always stays on Case 1.
        let projectIdChanged = false;
        if (!isLockedVariant && originalProduct) {
          const lockedVariant = product.selectedColors.find(
            (c: any) => Number(c.styleId) === Number(originalProduct.lockedStyleId)
          );
          for (const activeSide of activeSideNormalized) {
            // Current project ID for this variant+side (same lookup chain as prepareFinalPayloadAndSave side loop)
            const currentSideProjectKey = `${product.ProductId}_${variant.styleId}_${activeSide}`;
            const currentProjectId = this.variantSideProjects.get(currentSideProjectKey)
              || this.getSideProjectIdFromVariantSides(variant, activeSide)
              || '';

            // Locked variant's project ID for the same side
            const lockedSideKey = `${product.ProductId}_${originalProduct.lockedStyleId}_${activeSide}`;
            const lockedProjectId = this.variantSideProjects.get(lockedSideKey)
              || (lockedVariant ? this.getSideProjectIdFromVariantSides(lockedVariant, activeSide) : null)
              || '';

            if (currentProjectId && lockedProjectId && currentProjectId !== lockedProjectId) {
              projectIdChanged = true;
              break;
            }
          }
        }

        let source = variant.variantSource || '';

        // Override: if a non-locked variant's project_id has diverged from the locked variant's
        // project_id on any active side AND its artwork differs, it has its own design — force
        // Case 2 routing regardless of what variantSource says. A diverged project id alone is not
        // enough: every colour gets a seeded preview project, and routing those as alternates
        // saved same-art colours as their own designs carrying the default's print file.
        if (shouldForceAlternateRouting(isLockedVariant, projectIdChanged, anySideArtDiffers) && source !== 'customizedProduct') {
          source = 'alternateDesign';
        }

        let isDefault: boolean;
        let variantDesignId: boolean;

        // Collect per-active-side project IDs for the routing log (computed above, re-derive for logging)
        const lockedVariantForLog = !isLockedVariant && originalProduct
          ? product.selectedColors.find((c: any) => Number(c.styleId) === Number(originalProduct.lockedStyleId))
          : null;
        const perSideLog = activeSideNormalized.map((activeSide: string) => {
          const curKey = `${product.ProductId}_${variant.styleId}_${activeSide}`;
          const curPid = this.variantSideProjects.get(curKey)
            || this.getSideProjectIdFromVariantSides(variant, activeSide) || '';
          const lockedKey2 = originalProduct
            ? `${product.ProductId}_${originalProduct.lockedStyleId}_${activeSide}` : '';
          const lockedPid = lockedKey2
            ? (this.variantSideProjects.get(lockedKey2)
               || (lockedVariantForLog ? this.getSideProjectIdFromVariantSides(lockedVariantForLog, activeSide) : '')
               || '')
            : '';
          return `${activeSide}[cur=${curPid || 'none'} locked=${lockedPid || 'none'}]`;
        }).join(', ');

        if (source === 'customizedProduct') {
          // Locked variant — goes to CustomizeProducts (Case 1)
          isDefault = true;
          variantDesignId = true;
        } else if (source === 'alternateDesign') {
          // Non-locked variant with its own DH project (edited) — goes to ProductStyleAlternateDesign (Case 2)
          isDefault = false;
          variantDesignId = false;  // FIX: was incorrectly true (Case 3); must be false to trigger Case 2
        } else if (source === 'customProductImage') {
          // Image-only replica (no editor opened, no own DH project) — goes to CustomProductImage only (Case 3)
          isDefault = false;
          variantDesignId = true;  // FIX: was incorrectly false (Case 2); must be true to trigger Case 3
        } else {
          // Fallback for add flow (non-edit) or newly added colors
          isDefault = isLockedVariant;
          variantDesignId = isDefault ? true : this.getVariantDesignId(product.ProductId, variant.styleId);
        }

        // Routing decision log — visible in DevTools console for each variant during save
        const routingCase = isDefault
          ? 'Case 1: CustomizeProducts'
          : variantDesignId
            ? 'Case 3: CustomProductImage only'
            : 'Case 2: ProductStyleAlternateDesign';

        // ── InkSoft variant migration cases ──
        // Case 1: No change → keep as InkSoft (IsInksoft=true), but include for pricing/tags update
        // Case 2: Single design ID edited → migrated to Huddle (IsInksoft=false)
        // Case 3: Single design with multiple common variants — parent edited, replicas also migrated (IsInksoft=false)
        // Case 4: Multi-design, unedited variant → keep as InkSoft (IsInksoft=true), include for pricing/tags update
        let isInksoftVariant = false;
        if (this.isInksoftProduct) {
          if (!variant.isMigrated) {
            // Cases 1 & 4: Unmigrated variant — include with IsInksoft=true so pricing/tags still update in DB
            isInksoftVariant = true;
          } else {
            // Cases 2 & 3: Migrated variant — converted to Huddle
          }
        }

        // Deduplicate sides by SideName — prevents saving duplicate image records
        const uniqueSides = sidesWithCustomizations.filter((side, index, self) =>
          index === self.findIndex(s => s.SideName === side.SideName)
        );

        // WI 1819: an IsDefault=true style with zero Sides makes the backend's Case 1 loop
        // (`foreach (var side in defaultStyle.Sides ?? ...)`) never run, so it never assigns a
        // CustomizedProductId — the whole product then fails to save with an opaque error and
        // nothing publishes to Shopify. Catch it here, before the API call, with the expected
        // side name(s) so it's actionable (root cause: a custom decoration-location name like
        // "Left Logo" being mis-resolved as a sleeve — see canonicalSide() above).
        if (isDefault && uniqueSides.length === 0) {
          const expected = activeSidesForVariant.length > 0 ? activeSidesForVariant.join(', ') : 'none';
          emptyDefaultSidesErrors.push(
            `${variant.colorName || variant.styleId} (Product ${product.ProductId}) — expected side(s): ${expected}`
          );
          console.error(
            `❌ [WI 1819] Default style ${variant.colorName} (StyleId=${variant.styleId}) for Product ${product.ProductId} ` +
            `resolved to ZERO Sides. activeSidesForVariant=${JSON.stringify(activeSidesForVariant)}, ` +
            `variant.sides=${JSON.stringify((variant.sides || []).map((s: any) => s.SideName || s.Name))}`
          );
        }

        stylesArray.push({
          StyleId: variant.styleId,
          TemplateId: templateId,
          DesignId: variantDesignId,
          IsDefault: isDefault,
          IsInksoft: isInksoftVariant,
          Sides: uniqueSides
        });

      });

      const productPayload = {
        ProductId: product.ProductId,
        StoreId: String(this.storeId),
        UserId: Number(localStorage.getItem('userId')),
        ProjectName: product.selectedColors[0]?.projectName || product.productName || '',
        Styles: stylesArray,
        DecorationTypeId: (originalProduct as any)?.selectedDecorationTypeId ?? null
      };

      allProductPayloads.push(productPayload);
    });


    // Never hand on a colour that has its own artwork but no print file of its own — production
    // would print another colour's art. ensureOwnPrintExports normally prevents reaching here.
    if (printFileErrors.length > 0) {
      this.loader.hide();
      this.notification.error(
        `These colours have their own design but no print file of their own: ${printFileErrors.join(', ')}. ` +
        'Open each colour in the editor and close it to export its print file, then continue.',
        'Error'
      );
      return;
    }

    // WI 1819: never hand the backend a default/locked colour with no sides — it would silently
    // fail to create a product record and cancel the whole publish with no actionable detail.
    if (emptyDefaultSidesErrors.length > 0) {
      this.loader.hide();
      this.notification.error(
        `These default colours have no side to save, so publishing was stopped: ${emptyDefaultSidesErrors.join('; ')}. ` +
        'This can happen when a print location\'s name is mistaken for a sleeve (e.g. a custom ' +
        'location containing "left"/"right" such as "Left Logo"). Reload this page, re-add the ' +
        'side/design for the color(s) listed, and try again.',
        'Error'
      );
      return;
    }

    // Store payload in state service for price-and-tag page
    this.stateService.setColorVariantsPayload(allProductPayloads);

    // Navigate to pricing & tags page (API call moved to publish step)
    if (this.shouldNavigateAfterSave) {
      this.loader.hide();
      const prefix = getRoutePrefix(this.router.url);
      const qParams: any = {
        storeId: this.storeId,
        storeName: this.storeName
      };
      // Forward productRequestId so pricing-tags page detects product request mode
      const prData = this.stateService.getProductRequestMode();
      if (prData && prData.productRequestId) {
        qParams.productRequestId = prData.productRequestId;
      } else {
        // Fallback: check URL query param (in case state was lost)
        const prIdFromUrl = this.route.snapshot.queryParams['productRequestId'];
        if (prIdFromUrl) {
          qParams.productRequestId = prIdFromUrl;
        }
      }
      this.router.navigate([`${prefix}/product-pricing-tags`], { queryParams: qParams });
    } else {
      this.loader.hide();
    }
  }

  /**
   * Save initial/default products to database on page load (only once)
   */
  saveInitialProducts(productsPayload: any[]): void {
    // Only save once - skip if already completed
    if (this.initialSaveCompleted) {
      this.loader.hide();
      return;
    }

    // Filter only default products for initial save
    const defaultProducts = productsPayload.filter(p => p.IsDefault === true);

    if (defaultProducts.length === 0) {
      this.loader.hide();
      return;
    }


    // TODO: Re-enable when ready
    // this.http.post(`${environment.baseUrl}/api/designhuddle/SaveHuddleCustomizedProducts`, defaultProducts).subscribe({
    //   next: (response: any) => {
    //     console.log('✅ Initial products saved successfully:', response);
    //     this.initialSaveCompleted = true;
    //     this.loader.hide();
    //     this.notification.success('Products saved successfully!', 'Success');
    //   },
    //   error: (error: any) => {
    //     console.error('❌ Error saving initial products:', error);
    //     this.loader.hide();
    //     this.notification.error('Failed to save products', 'Error');
    //   }
    // });
    this.initialSaveCompleted = true;
    this.loader.hide();
  }

  /** Join base URL with path, ensuring exactly one slash between them */
  joinUrl(base: string, path: string): string {
    if (!path) return base;
    const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const trimmedPath = path.startsWith('/') ? path : '/' + path;
    return trimmedBase + trimmedPath;
  }

}
